/**
 * 한국어 사전 — 화면에 보이는 모든 문구의 유일한 출처.
 *
 * 출처는 `docs/wireframes/ctcells_console_ui_wireframe.html` 실물이다. 지어낸 문구가 아니다.
 * 어휘는 `docs/wireframes/README.md` §15 용어 사전을 따른다 — 「배치」가 아니라 「작업」,
 * `control_owner` 는 「시스템」/「단독」, 모듈 표는 「응답 확인」·「버전」.
 *
 * 🔴 내부 키와 화면 표기를 섞지 않는다. `enum.*` 키의 마지막 세그먼트는 **API 원값 그대로**다
 *    (`enum.batchStatus.running`). 코드는 계속 `batch`·`running` 을 쓰고, 한글은 여기서만 붙는다.
 *
 * 키 규칙과 결정 근거는 `docs/i18n.md` 참조.
 */
export const ko = {
  'batchHalt.title': '작업이 멈췄습니다',
  'batchHalt.estopTitle': 'E-STOP 실행 중',
  // 작업 줄 머리 주 단추 — 「멈춘 이유」를 걷었다(eric 2026-10-29 「멈춘 이유라고 하지 말고.. 조치라고 해줘」)
  'batchHalt.open': '조치',
  'batchHalt.paused': '작업이 일시정지되었습니다.',
  'batchHalt.unknown': '서버에서 멈춘 원인을 아직 받지 못했습니다.',
  'batchHalt.module': '관련 장비',
  'batchHalt.moduleOpen': '장비 상세',
  'batchHalt.details': '작업 상세',
  'batchHalt.lockManage': '제어락 관리로 이동',
  'batchHalt.lockHint':
    '상단 작업 줄의 제어락에서 관리할 수 있습니다. 원인과 정지 목록은 제어락 없이도 볼 수 있습니다.',
  'batchHalt.cause': '원인',
  'batchHalt.steps': '해소 순서',
  'batchHalt.lockOther': '그 사람이 반납해야 정지를 풀 수 있습니다',
  'batchHalt.lockMine': '내가 보유',
  'batchHalt.lockHolder': '{holder} 보유',
  'batchHalt.stops': '정지 풀기',
  'batchHalt.recover': '복구 판정',
  'batchHalt.stale':
    '최신 상태를 확인할 수 없습니다. 마지막으로 받은 내용을 표시하며 제어는 비활성입니다.',

  // ── 앱 셸 ─────────────────────────────────────────────────────────────────
  'app.userMenu': '사용자 메뉴',
  'app.navToggle': '사이드바 접기',
  'app.navCollapse': '접기',
  'app.navExpand': '펼치기',
  'app.version': '버전',
  'app.logout': '로그아웃',
  'app.home': '처음으로',
  'app.notFound': '찾을 수 없는 화면',
  /*
   * 🔴 **주소를 그대로 적지 않는다.** 사람이 고칠 수 있는 것은 주소가 아니라 「어디로 갈지」다.
   *    갈 곳을 버튼으로 준다(`app.home` · `app.back`).
   */
  'app.notFound.body': '주소가 바뀌었거나 없는 화면입니다',
  'app.back': '이전 화면',

  /*
   * 렌더 중 예외 — **화면 하나가 죽은 것**이지 앱이 죽은 것이 아니다.
   * 🔴 **예외 메시지 원문을 본문에 쓰지 않는다.** 영문 스택은 사람이 할 일을 알려 주지 않는다.
   *    원문은 접힌 `app.renderError.detail` 안에만 두고, 콘솔에도 따로 남긴다.
   */
  'app.renderError': '화면을 그리지 못했습니다',
  'app.renderError.body': '이 화면만 멈췄습니다. 다시 시도하거나 다른 화면으로 갈 수 있습니다',
  /** 셸까지 죽은 경우 — 메뉴도 E-STOP 도 없다. 갈 곳이 「다시 불러오기」뿐이다 */
  'app.renderError.fatalBody': '앱 전체를 다시 그려야 합니다',
  'app.renderError.detail': '오류 원문',
  'app.retry': '다시 시도',
  'app.reload': '다시 불러오기',

  // ── 로그인 ───────────────────────────────────────────────────────────────
  /*
   * 로그인 입력칸의 이름 — 「아이디」(eric 2026-09-28 「아이디로 수정해줘」). 로그인 화면 전용 키다.
   * ⚠ 다른 화면의 「계정」(`card.account` · `column.accountId` 등)은 그대로다 — 바꾼 것은 이 칸뿐이다.
   */
  'auth.account': '아이디',
  /*
   * 로그인 실패 줄. 🔴 `error.AUTH_FAILED` 를 그대로 고치지 않은 까닭 — 그 코드는 로그인 밖에서도
   *    난다(세션 만료·게이트 401). 입력칸 이름에 맞춘 문구는 로그인 화면에서만 쓴다(`LoginRoute`).
   */
  'auth.failed': '인증에 실패했습니다 — 아이디·비밀번호를 확인하세요',
  /* 로그인 칸 안 오른쪽 「✕」 단추의 이름(eric 2026-09-28 「한번에 지울 수 있도록」) — `{field}` 는 칸 이름 */
  'auth.clearField': '{field} 지우기',
  'auth.password': '비밀번호',
  /*
   * 비밀번호 칸 오른쪽 눈 단추(eric 2026-10-02 「눈 표시 있어서.. 누르면.. 비밀번호가 보이게」).
   * 🔴 **이름은 하나로 고정**이고 켜짐/꺼짐은 `aria-pressed` 가 말한다 — 토글 단추의 이름을 상태마다
   *    바꾸면 「비밀번호 숨기기, 눌림」처럼 두 번 뒤집혀 읽힌다(WAI-ARIA APG 토글 단추).
   *    눈에 보이는 상태는 아이콘 모양(눈 ↔ 사선 그은 눈)이 말한다.
   */
  'auth.passwordReveal': '비밀번호 표시',
  'auth.remember': '로그인 유지',
  'auth.submit': '로그인',
  'auth.pinLogin': 'PIN 로그인',
  'auth.readOnly': '조회 전용',
  /*
   * 왜 로그인 화면으로 돌아왔는가. 🔴 **차분한 알림이지 실패가 아니다** — 사람이 뭘 틀린 게 아니다.
   * ⚠ 축은 서버가 **가를 수 있는 것만** 둔다. `/auth/refresh` 는 폐기·무활동만료·절대만료를
   *   전부 401 `AUTH_FAILED` 하나로 답한다(`ctcells-core` `services/auth.py`
   *   `SessionRejection` — 「응답에서는 구분하지 않는다」). 그래서 「강제 종료」 문구가 없다.
   */
  /*
   * 모듈 모니터링 앱의 자동 로그인(`ModuleAutoLogin` · eric 2026-10-16 「로그인을 아예 없애줘」).
   * 사람이 입력하는 로그인 화면이 없으므로, 막혔을 때 **무엇을 고쳐야 하는지**를 적는다.
   */
  'auth.auto.missing.title': '자동 로그인 계정이 설정되어 있지 않습니다',
  'auth.auto.missing.text':
    '이 화면은 정해진 계정으로 스스로 로그인합니다. 배포 설정의 moduleAccount(아이디 · 비밀번호)를 채워 주세요',
  'auth.auto.rejected.title': '자동 로그인에 실패했습니다',
  'auth.auto.rejected.text':
    '서버가 설정된 계정을 받지 않았습니다. 아이디 · 비밀번호 · 계정 잠금을 확인하세요. 계정이 잠기지 않도록 스스로 다시 시도하지 않습니다',
  'auth.auto.transport': '서버에 닿지 않습니다. 잠시 뒤 다시 연결합니다',

  // ── 테마 (쵸단 담당 ThemeToggle) ─────────────────────────────────────────
  'theme.label': '테마',
  'theme.system': '시스템',
  'theme.light': '라이트',
  'theme.dark': '다크',

  // ── 강조색 (통합 콘솔만 · eric 확정 2026-09-22 「테마색은 한 3가지만」) ──────
  /* 줄 이름은 「테마색」(eric 2026-09-22) — 값은 파랑 · 검정 둘. 남색은 2026-09-22 에 걷었고 민트는 09-23 에 더했다가 09-28 에 걷었다(「초록색 부분은 빼줘」) */
  'accent.label': '테마색',
  'accent.blue': '파랑',
  'accent.ink': '검정',

  // ── 유리 효과 (통합 콘솔만 · 기본 끔) ────────────────────────────────────
  /* 리퀴드 글라스 줄은 2026-09-22 에 걷었다 — 항상 켬(eric). 키도 같이 지웠다 */

  // ── 사이드바 ─────────────────────────────────────────────────────────────
  'nav.aria': '주 메뉴',
  'nav.group.ops': '운영',
  'nav.group.res': '리소스',
  'nav.group.sys': '시스템',
  'nav.item.monitoring': '모니터링',
  /*
   * 🔴 헤더 메뉴 이름은 **「작업 관리」**다(디자인팀 시안 2026-10-01 `docs/design/CTcells-console.html`).
   *    무리 제목(화면 h2 「작업」)은 `screen.opsGroup.title` 이 따로 쥔다 — 메뉴 이름을 바꿔도 제목은 안 따라간다.
   *    장비 관점 사이드바 · 태블릿 내비도 이 키를 같이 쓴다(항목이 나란히 서도 「작업 관리」로 읽힌다).
   */
  /** 헤더 메뉴 — 「작업 관리」→「작업」(eric 2026-10-02 정정). 화면 제목은 `screen.*Group.title` 이 따로 든다 */
  'nav.item.batches': '작업',
  'nav.item.batchHistory': '이력',
  'nav.item.alarms': '알람',
  /*
   * 리소스 그룹의 유일한 항목. 시료·레시피·랩웨어를 탭으로 품는다(2026-09-18 eric 지시).
   * 🔴 **값은 「준비」다 — 「자원」으로 되돌리지 마라**(2026-09-21 eric 지시:
   *    "자원이라고 이름 붙여 있는데, 이게 일반적인 용어가 아니라 뭔 게임이나 클라우드 쪽
   *    용어 같애. 시료 레시피 랩웨어를 모두 커버하는 단어로 바꿔줘"). 작업을 돌리기 «전에»
   *    갖춰 두는 것 셋을 덮는다. 키 이름(`resources`)·경로·파일명은 그대로 두고 값만 바꿨다.
   * ⚠ 관리 › 설정 안의 **「걸린 것」**(`screen.stops.title` · 종전 「자원 점유」)은 다른
   *   것이다. 같이 바꾸지 마라.
   * 🔴 헤더 메뉴 이름은 **「작업 준비」**다(eric 2026-10-02 · 시안의 「실험 준비」에서). 무리 제목(화면 h2)도
   *    `screen.resourcesGroup.title` 로 같은 글자다 — 키는 따로라 한쪽만 바꾸면 갈린다.
   */
  /** 헤더 메뉴 — 「실험 준비」→「작업 준비」(eric 2026-10-02) */
  'nav.item.resources': '작업 준비',
  'nav.item.samples': '시료',
  'nav.item.recipes': '레시피',
  /** 작업 준비 탭 「템플릿」(eric 2026-10-13 「응 그러면. 탭 추가해줘..」 — 종전 레시피 머리의 [템플릿 관리] 단추) */
  'nav.item.templates': '템플릿',
  'nav.item.modules': '모듈',
  'nav.item.logs': '로그',
  /** 헤더 메뉴 — 「관리」(eric 2026-10-02 · 같은 날 한때 「시스템」이었다가 되돌림 「시스템 대신에 관리라고」) */
  'nav.item.admin': '관리',
  'nav.item.account': '설정',
  'nav.brandHome': '모니터링으로',
  /*
   * 헤더 「알람」 문에 붙는 활성 알람 배지(2026-09-22 · `shell/useAlarmBadge.ts`).
   * 🔴 **「미해소」에서 「활성」으로 바꿨다.** 세는 축이 서버 어휘 `state=active` 하나라서다 —
   *    `acknowledged`(확인했으나 미해제)는 안 센다. 「미해소」라고 적으면 그 건까지 센 것처럼 읽힌다.
   * ⚠ `…AtLeast` 는 목록을 상한(500)까지만 받아 **덜 봤을 때**다. 완전한 수인 척 적지 않는다.
   */
  'nav.alarmBadge': '활성 알람 {count}건',
  'nav.alarmBadgeAtLeast': '활성 알람 최소 {count}건',
  'nav.alarmBadgeSevere': '심각 이상 포함',
  'nav.moduleBadge': '주의가 필요한 모듈 {count}개',

  // ── 화면 제목 (apps/console 라우트 표와 1:1) ──────────────────────────────
  'screen.login.title': '로그인',
  /* 로그인 왼쪽 브랜드 칸 문구 — CTCells 사이트 원문 그대로(eric 2026-10-29 · 번역하지 않는다) */
  'login.tagline.line1': 'Oncology Research Platform',
  'login.tagline.line2': 'Circulating Tumor Cells for Drug Discovery',
  'screen.monitoring.title': '모니터링',
  'screen.monitoringModule.title': '모니터링 · 모듈',
  'screen.batches.title': '작업',
  /*
   * 무리 제목 — 무리 탭(진행 중 · 이력 / 시료 · 레시피 · 랩웨어)이 켜진 화면의 h2 다.
   * 🔴 헤더 메뉴(`nav.item.batches` 「작업 관리」 · `nav.item.resources` 「실험 준비」)와 **키를 가른다**(2026-10-01).
   *    메뉴 이름은 시안을 따라 길어졌고 화면 제목은 짧은 채로 둔다 — 한 키로 묶으면 둘이 같이 움직인다.
   */
  'screen.opsGroup.title': '작업',
  /** 「작업 준비」 무리의 화면 제목 — 메뉴 이름과 같게(eric 2026-10-02 「상세 타이틀 이름도 작업 준비라고 해줘」) */
  'screen.resourcesGroup.title': '작업 준비',
  'screen.batchNew.title': '새 작업',
  /** 「추가」 창 넷의 제목 꼴(「N 추가」 · eric 2026-09-30). 페이지(장비 관점 콘솔)는 위 「새 작업」 그대로다 */
  'screen.batchAdd.title': '작업 추가',
  'screen.batchDetail.title': '작업 상세',
  'screen.batchHistory.title': '이력',
  'screen.batchHistoryDetail.title': '이력 · 작업 상세',
  'screen.alarms.title': '알람',
  'screen.samples.title': '시료',
  'screen.sampleNew.title': '시료 추가',
  'screen.sampleDetail.title': '시료 상세',
  'screen.recipes.title': '레시피',
  'screen.recipeNew.title': '레시피 추가',
  'screen.recipeDetail.title': '레시피 상세',
  'screen.modules.title': '모듈',
  'screen.moduleDetail.title': '모듈 상세',
  'screen.calibrationDetail.title': '보정 상세',
  'screen.logs.title': '로그',
  'screen.admin.title': '관리',
  'screen.userNew.title': '사용자 추가',
  'screen.userDetail.title': '사용자 상세',
  'screen.userDeactivate.title': '계정 비활성',
  'screen.userLock.title': '계정 잠금',
  'screen.userUnlock.title': '잠금 해제',
  'screen.userPassword.title': '비밀번호 재설정',
  'screen.userRole.title': '역할 변경',
  'screen.sessionRevoke.title': '세션 강제 종료',
  'screen.account.title': '설정',
  'screen.tablet.title': '태블릿',
  'screen.module.title': '모듈',
  'screen.diagnostics.title': '자가 진단',

  // ── 탭 ───────────────────────────────────────────────────────────────────
  // 「작업」 무리 탭의 첫 칸(eric 2026-09-30). 제목은 `screen.opsGroup.title`(「작업」) · 메뉴는 `nav.item.batches`(「작업 관리」)
  'tab.ops.batches': '실행 중',
  // 작업 목록
  'tab.batches.recover': '복구 대기',
  /*
   * 작업 상세 — 🔴 **탭이 없다. 카드가 세로로 선다**(eric 2026-09-30). 「작업」 카드 제목은 `column.batch`.
   *   「요약」·「이벤트」 탭은 걷었다. 「시료 배정」 카드는 「시료」 카드에 합쳤다.
   *   🔴 **`env` · `report` 를 되살리지 마라**(2026-09-20 제거) — 환경은 배치 연결 필드가 0,
   *   보고서는 배치 단위가 없다(`GET /reports/{id}` 의 `id` 는 `sample_id` · 시료 표의 「보고서」 버튼이 그 자리)
   */
  'card.batchDetail.samples': '시료',
  'card.batchDetail.cmds': '명령',
  'card.batchDetail.events': '이벤트 로그',
  'card.batchDetail.trays': '적재 · 수거',
  /*
   * 알람 4탭 — 🔴 **어휘는 계약 `Alarm.state` 다**(`GET /alarms` 의 `state` 질의 파라미터).
   *
   * 종전 3탭은 「긴급 · 조치 필요 · 자동복구 중」으로 `EventPayload.category` 를 접은 것이었는데
   * **그 필드가 실물에 오지 않아** 2026-09-20 에 축을 갈아끼웠다(근거: `contract/src/alarm.ts`).
   * ⚠ 「긴급」을 `severity: CRITICAL` 에 옮겨 붙이지 않았다 — 두 축이 다르다(카탈로그에
   *   `WARN` + `category: critical` 조합이 있다). 이름도 축을 따라간다.
   * 🔵 2026-09-26 `category` 가 실물에 들어왔다 — 그러나 **탭은 그대로 `state` 축이다.** 분류는 툴바의
   *   구획 선택기(`filter.category` · `enum.alarmCategory.*`)로 따로 가른다. 두 축을 한 줄에 섞지 않는다.
   */
  'tab.alarms.all': '전체',
  'tab.alarms.active': '활성',
  'tab.alarms.acknowledged': '확인됨',
  'tab.alarms.cleared': '해제됨',
  // 로그 3탭
  'tab.logs.event': '이벤트',
  'tab.logs.syslog': '시스템',
  'tab.logs.audit': '감사',
  // 시료 4탭
  'tab.samples.all': '전체',
  'tab.samples.in_process': '진행 중',
  'tab.samples.completed': '사용 완료',
  'tab.samples.discarded': '폐기',
  /*
   * 레시피 상세 5탭 — Opentrons App 프로토콜 상세의 탭 구성을 옮긴 것이다
   * (`ProtocolDetailsTabs.tsx`: 파라미터 | 하드웨어 | 랩웨어 | 액체 | 통계).
   * 근거 전문은 `docs/opentrons-alignment.md` §4-2.
   *
   * 🔴 **「하드웨어」·「통계」 탭을 만들지 않는다.** 로봇·피펫 구성이 `payload` 에 «없고»
   *    집계 경로도 없다. 응답에 없는 값의 자리를 만들지 않는다(eric 확정 2026-09-20).
   * ⚠ Opentrons 의 「액체(Liquids)」는 우리 어휘로 **「시약」**이다. 정본 용어를 쓴다.
   */
  'tab.recipeDetail.overview': '개요',
  /*
   * 🔴 **「파라미터」가 아니다**(eric 확정 2026-09-22). 이 탭이 보이는 것은
   *    `payload.runtime_parameters` — **작업을 만들 때 사람이 채우는 값**이다. 레시피의
   *    설정값(회전 속도 · 회전 시간 …)은 「단계」 탭에 있고, 「파라미터」라는 말은 그 둘을
   *    한 이름으로 부르게 만들어 읽는 사람이 탭을 잘못 연다. 키는 그대로 두고 «말»만 고쳤다.
   */
  'tab.recipeDetail.parameters': '작업 생성 입력',
  'tab.recipeDetail.labware': '랩웨어',
  'tab.recipeDetail.reagents': '시약',
  'tab.recipeDetail.steps': '단계',
  // 보정 상세 화면의 돌아가기 글자(장비 관점 콘솔 · 모듈 상세의 탭은 2026-10-22 에 걷었다)
  'tab.moduleDetail.cal': '보정',
  // 관리 2탭
  // 🔴 「일반」→「설정」(eric 지시 2026-09-21). ⚠ 사이드바 계정 항목(`nav.item.account`)도 「설정」이다 — 다른 화면이다
  'tab.admin.general': '설정',
  /** 관리 «웹훅» 탭(eric 2026-10-28 「설정 탭을 따로 하나 더 만들고, 지금 거는 웹훅이라고 탭 이름 수정해 줘」) — 등록된 웹훅 + 알람별 전달 */
  'tab.admin.webhooks': '웹훅',
  /** 관리 «설정» 탭 — 지금은 빈 판(내용은 eric 이 곧 요청) */
  'admin.general.empty': '아직 설정할 항목이 없습니다',
  'tab.admin.members': '사용자',
  /*
   * 모니터링 2탭 — **시료 관점 콘솔 전용**(eric 지시 2026-09-21).
   * 🔴 그림 탭(`figTab.*`)을 **대신하는** 줄이다. 두 줄이 같이 서지 않는다 —
   *    장비 관점은 `figTab.*` 줄만, 시료 관점은 이 줄만 쓴다.
   * 🔴 2026-09-22 — [시료][장비] 2분할 탭을 걷고 **알약 한 줄**([시료][원심분리기][어셈블러][염색기])로
   *    바꿨다(eric 「시료 장비 탭 없애고 원심분리기 왼쪽에 시료라고 칩」). `tab.monitoring.equipment`·
   *    `tab.monitoring.aria` 는 그때 지웠다 — 장비 알약은 `enum.module.*` 이름을 그대로 쓴다.
   */
  /*
   * 🔴 2026-10-01 — 디자인팀 시안(`docs/design/DESIGN-CHANGES.md` §3 「보기 전환」): 알약 [시료] [장비] 를
   *    **구획 선택기 「시료별 보기 | 장비별 보기」** 로 바꿨다. 두 선택지 중 하나가 켜진 상태가 분명하다.
   * 🔴 2026-10-02 — 글자를 「시료 | 장비」 두 글자로 줄였다(eric 「시료별 보기 장비별 보기를.. 시료, 장비 이렇게.. 수정해줘」).
   *    오른쪽 「소모품 | 비전」과 결이 같다. 「보기 방식」은 tablist 의 접근 이름(`monitoring.deviceSelect.aria`)이 말한다.
   */
  'tab.monitoring.samples': '시료',
  /*
   * 구획 선택기(시료별 · 장비별)의 접근 이름(`role="tablist"` 의 `aria-label`). 보이는 「모드」 라벨은 시안대로 걷었다
   * (eric 2026-09-28 의 「모드」 라벨 — 시안 2026-10-01 에서 구획 선택기 자체가 「보기 방식」을 말한다).
   */
  /* 「장비」 구획 — 고르면 아래 · 옆에 원심분리기·어셈블러·염색기 구획 묶음(`seg--sub`)이 선다 */
  'monitoring.deviceSelect.equipment': '장비',
  /* 장비 구획 묶음(원심분리기 · 어셈블러 · 염색기)의 접근 이름 */
  'monitoring.deviceSelect.modules': '장비 선택',
  /** 장비 탭 오른쪽 위 초록 점의 접근 이름 끝말 — 지금 작업을 돌리는 장비(eric 2026-10-15) */
  'monitoring.deviceSelect.running': '실행 중',
  /*
   * 장비 칩의 짧은 이름 — 「장비」 탭 칸이 좁아 긴 이름(원심분리기 · 어셈블러 · 염색기)이 안 들어갈 때만 보인다
   * (`monitoring.css` 의 컨테이너 질의). 🔴 접근 이름 · `title` 은 언제나 긴 이름이다 — 짧은 글자는 그 앞머리라
   * 「보이는 글자가 이름 안에 든다」(WCAG 2.5.3)가 지켜진다. 모니터링 장비 칩 전용이다.
   */
  'monitoring.deviceSelect.short.cent': '원심',
  'monitoring.deviceSelect.short.asm': '어셈',
  'monitoring.deviceSelect.short.stain': '염색',
  /*
   * 6축 로봇 칩(eric 2026-10-06 「6축 로봇... 장비에다가.」) — 🔴 모니터링 판의 이름 「운송 로봇」(eric 2026-10-13 「6축 로봇 대신에.. 운송 로봇이라고..
   * 탭 부분 말이야」 → 같은 날 「운송 로봇 모니터링 페이지에. 6축 로봇이라고 되어 있는데.. 이거 운송 로봇이라고 수정해줘」). 칩 · 판 제목 · 단계 띠 ·
   * 그림 접근 이름이 이 키를 읽는다(`monitoringDeviceName`). `module.robot`(「6축 로봇」)은 판 밖(작업 줄 · 알람 · 기록 · 모듈 목록)이 그대로 쓴다.
   * 짧은 이름은 긴 이름의 앞머리
   */
  'monitoring.deviceSelect.robot': '운송 로봇',
  'monitoring.deviceSelect.short.robot': '운송',
  /*
   * 오른쪽 열의 제목 — 소모품 판 하나다(탭이 아니다). ⚠ 2026-10-02 오후 한때 「소모품 | 비전」 두 탭이었다가 같은 날
   * eric 「오른쪽에. .비전.. 은 없애..」로 비전이 장비 상세 카드 안으로 갔다(`VisionPane`). 탭 줄을 되살리지 마라.
   */
  'monitoring.side.title': '소모품',
  /**
   * 모니터링 보기 줄(eric 2026-10-07 「보기 [✓ 시료] [✓ 장비] [✓ 소모품 ▲1] … 응 이렇게 ㄱㄱㄱ 내가 원한 거야」) — 체크한 열만 선다.
   * 셋 다 끄면 열 자리에 이 사유 한 줄
   */
  'monitoring.view.label': '보기',
  /* eric 2026-10-12 「보기에서 ~~를 체크해주세요 이렇게 수정해줘. 잡다한 텍스트 다 빼고」 */
  /* eric 2026-11-02 「적당한 말로」 — 「왼쪽 띠를 눌러 주세요」에서 바꿈 → 「더 간단히」 */
  'monitoring.view.none': '왼쪽에서 화면을 펼쳐 주세요',
  /*
   * 시료 판을 볼 수 없는 사유(2026-09-26) — `operations` 에 `listSamples` 가 없을 때. 요청은 안 나간다.
   * 🔴 「권한이 없습니다」 한 마디로 접지 않는다 — 무엇을 왜 못 보는지(PII)를 말한다
   */
  'monitoring.samplesLocked': 'viewer 는 시료(PII) 접촉면을 볼 수 없습니다',
  // 제어 화면 이벤트 필터

  // ── 모니터링 · 패드 그림 탭 (FIGTABS) ────────────────────────────────────
  'figTab.all': '전체',
  'figTab.trays': '트레이',
  'figTab.cent': '원심분리기',
  'figTab.vision': '비전',
  'figTab.gantry': '3축 로봇',
  'figTab.tc': 'TC 랙',
  'figTab.asm': '어셈블러',
  'figTab.tray': '트레이' /* 원심·어셈블러의 `figTab.trays` 와 맞춘다(2026-09-18) */,
  'figTab.pip': '피펫 · 시약',
  'figTab.shaker': '셰이커',
  'figTab.maint': '정비',
  'figTab.aria': '표시 항목',
  /** 비전 카메라 진입 아이콘의 툴팁 — 와이어프레임 `vcamIcon()` 1669행 */
  'figure.visionCamera': '비전 카메라 · 검사용',
  /** 「전체」 보기에서 그림을 눌러 그 부품으로 넘어간다 */
  'figure.pick': '{name} 보기',

  // ── 장비 관점 — 스텝별 「지금 일하는 구성요소」 (eric 지시 2026-09-21) ────
  /**
   * 🔴 **그림 «위에» 얹는 말이다.** 장비 관점 콘솔에서만 쓴다 —
   *    시료 관점 콘솔·패드·태블릿은 이 문구를 한 글자도 그리지 않는다.
   */
  'activity.now': '실행 중',
  'activity.paused': '멈춤',
  /** 강조된 구성요소에 붙는 접근성 이름 */
  'activity.aria': '{name} — 지금 작업 중',
  /** 자리 이름 — `location_kind` 어휘(모듈 시퀀스 스키마)의 한글 표기 */
  'activity.at.inlet_tray': '입구 트레이',
  'activity.at.outlet_tray': '완료 트레이',
  'activity.at.rotor_slot': '로터',
  'activity.at.counterweight_station': '평형추 칸',
  'activity.at.front_zone': '전면',
  'activity.at.waste_container': '폐기통',
  'activity.at.equipment_stage': '어셈블 스테이지',
  'activity.at.tc_rack': 'TC 랙',
  'activity.at.reagent_bay': '시약 베이',
  'activity.at.tip_bay': '팁 랙',
  'activity.at.shaker': '셰이커',
  /** 「<자리> <번호>번」 — 번호가 있을 때만 */
  'activity.slot': '{name} {index}번',
  /** 「<출발> → <도착>」 */
  'activity.move': '{from} → {to}',
  /** 「<자리>로」 — 도착지만 알 때 */
  'activity.to': '{to}',
  /** 「<칸> 칸」 — 염색기의 대상 웰 */
  'activity.wells': '{wells} 칸',

  // ── 표 열 제목 ───────────────────────────────────────────────────────────
  'column.time': '시각',
  'column.severity': '심각도',
  'column.module': '모듈',
  'column.content': '내용',
  'column.target': '대상',
  'column.result': '결과',
  'column.kind': '종류',
  /**
   * 시료 타입(`Sample.sample_type`) — 시료 화면은 「타입」이다(eric 2026-09-30 「시료에서.. 종류 대신 타입으로」).
   * 서랍 · 추가 폼 칸은 `field.sampleType`. 레시피 · 로그 · 이벤트의 「종류」(`column.kind`)는 다른 뜻이라 그대로다
   */
  'column.sampleType': '타입',
  /** 알람 분류(`Alarm.category`) — 「종류」(`column.kind`)와 갈라 둔다. 그건 다른 원장의 칸이다 */
  'column.category': '분류',
  'column.status': '상태',
  'column.version': '버전',
  'column.step': '단계',
  'column.updated': '수정',
  'column.name': '이름',
  'column.role': '역할',
  /** 작업 목록·이력 · admin 전용 — `Batch.created_by` 를 `GET /users` 로 푼 이름(eric 2026-09-30) */
  'column.owner': '작업자',
  /** 작업 목록·이력 — `Batch.sample_count`(지금 이 배치에 붙은 시료 수 · 서버가 센다) */
  'column.sampleCount': '시료 수',
  /** 소요 시간(`started_at`→`completed_at`). 기간 거름(`filter.period`)과 다른 뜻이다 */
  'column.duration': '소요 시간',
  'column.sample': '시료',
  'column.startedAt': '시작',
  'column.progress': '진행',
  'column.report': '보고서',
  'column.format': '형식',
  'column.createdAt': '생성',
  'column.actor': '행위자',
  'column.action': '행위',
  'column.reason': '사유',
  'column.reasonChange': '사유 · 변경',
  'column.source': '출처',
  'column.message': '메시지',
  'column.item': '항목',
  'column.performedAt': '실시',
  'column.nextDue': '다음 권장',
  'column.accountId': '계정 ID',
  'column.email': '이메일',
  'column.lastActive': '마지막 활동',
  /* 사용자 표 — 값은 `User.last_login_at`(eric 2026-09-30 「마지막 로그인으로 수정해」). 세션의 `last_seen_at` 은 위 `lastActive` */
  'column.lastLogin': '마지막 로그인',
  'column.ipAddress': 'IP 주소',
  /* 모듈 표 — 같은 종류가 여럿이라 ID 로 구분한다(2026-09-20 `/modules` 8건) */
  'column.moduleId': '모듈 ID',
  /* 모듈 표 「통신」 열 — 값은 `stale`(서버 판정) · 칩은 「연결」/「응답 없음」(eric 2026-10-30 「통신 컬럼을 하나 더 두고」) */
  'column.link': '통신',
  /* 인터락 · E-STOP 을 한 열로 본다. 평상시엔 둘 다 정상이라 나누면 표가 「정상/닫힘」으로 찬다 */
  'column.safety': '안전',
  'column.heartbeat': '응답 확인',
  'column.lastDiagnostic': '최근 진단',
  'column.calibration': '보정',
  'column.recipe': '레시피',
  'column.batch': '작업',
  /*
   * 🔴 **값이 작업 ID 인 열의 머리**(2026-09-22 · eric 「컬럼들 봐봐」). 작업 목록·이력의 둘째 열이
   *    UUID 앞 8자인데 머리가 「작업」이라 사람이 작업 «이름»을 기대했다 — 계약 `Batch` 에 이름 칸이
   *    없어 그 자리에 올 이름이 없다(배치 이름 요청은 `docs/backend-requests.csv` BR-95 — 서버 조립 이름은 BR-82 로 섰다).
   * ⚠ `column.batch`(「작업」)를 고치지 «않고» 키를 판 이유: 그 키를 값이 ID 가 «아닌» 자리에서도
   *   쓴다 — 작업 상세의 카드 제목과 시료 등록의 「새 작업에서 배정」 줄. 거기서는 「작업」이 맞다.
   */
  'column.batchId': '작업 ID',
  'column.well': '웰',
  'column.bay': '자리',
  'column.layoutDetail': '상세 수준',
  'column.usedByRecipes': '사용 레시피',
  'column.labware': '랩웨어',
  'column.sampleId': '시료 번호',
  'column.patientId': '환자 번호',
  'column.currentLocation': '현재 모듈 · 위치',
  'column.registered': '등록',
  'column.drawDateCancer': '채혈일 · 암종',
  'column.average': '평균',
  'column.range': '범위',
  'column.deviation': '이탈',
  'column.verdict': '판정',
  'column.slot': '슬롯',
  'column.cell': '칸',
  'column.location': '위치',
  'column.channel': '채널',
  'column.reagent': '시약',
  'column.remaining': '잔량',
  'column.evidence': '근거',
  'column.attribution': '귀속',
  'column.event': '사건',
  'column.alarm': '알람',
  'column.occurredAt': '발생',
  /** 알람 목록 첫 열 머리 — eric 2026-09-30 「발생 시각」(값은 연월일 시분초 전부). 서랍 줄은 `column.occurredAt`(발생) 그대로 */
  'column.alarmOccurredAt': '발생 시각',
  'column.usedByBatches': '작업 사용',
  'column.correlationId': '상관 ID',
  'column.operation': '동작',
  'column.logLevel': '수준',
  'column.metric': '항목',

  // ── 검색 · 필터 ──────────────────────────────────────────────────────────
  'filter.all': '전체',
  'filter.active': '활성',
  'filter.period': '기간',
  'filter.severity': '심각도',
  'filter.module': '모듈',
  'filter.attribution': '귀속',
  'filter.batch': '작업',
  'filter.status': '상태',
  'filter.layoutDetail': '상세 수준',
  'filter.source': '출처',
  'filter.actor': '행위자',
  'filter.kind': '종류',
  'filter.logLevel': '수준',
  'filter.category': '분류',
  'filter.search.alarms': '코드 · 모듈 · 내용 검색',
  // 작업 · 이력 탭 검색칸: 눈에 보이는 글자는 「검색」뿐(eric 2026-09-29). 위 키는 접근성 이름으로 남는다
  'filter.search.batches': '작업 ID · 레시피 검색',
  'filter.search.batchesWithOwner': '작업 ID · 레시피 · 작업자 검색',
  'filter.search.batchesPlaceholder': '검색',
  'filter.search.recipes': '이름 검색',
  'filter.search.recipesWithOwner': '이름 · 작업자 검색',
  'filter.search.events': '내용 · 모듈 검색',
  'filter.search.syslog': '메시지 · 출처 검색',
  'filter.search.audit': '행위자 · 대상 · 사유 검색',
  // 🔴 「이메일」을 되살리지 마라 — `User` 에 그 필드가 없다(2026-09-20). 실제 검색 축은 둘이다
  'filter.search.users': '계정 ID · 이름 검색',

  // ── 버튼 ─────────────────────────────────────────────────────────────────
  'action.start': '시작',
  'action.pause': '일시정지',
  'action.resume': '재개',
  'action.stop': '중지',
  'action.recover': '복구',
  'action.estop': 'E-STOP',
  'action.clearSafety': '안전 해제',
  'action.showCause': '원인 보기',
  'action.loadDone': '적재 완료',
  'action.unloadDone': '수거 완료',
  'action.selectBatch': '작업 선택',
  /*
   * 작업 고르기 드롭다운(eric 2026-10-22 「작업 선택을 클릭하면.. 작업 메뉴로 가지 말고.. 작업 아이디 혹은 작업 이름이 뜨게 해줘..」)
   * — 작업 줄의 [작업 선택]이 그 자리에 시작 전 작업 목록을 편다(`StripBatchPicker`)
   */
  'strip.picker.empty': '대기 작업 없음',
  /* 고른 작업 풀기 — 작업 이름 옆 ×. 클라 선택만 지운다(서버 상태 그대로) */
  'strip.picker.clear': '고른 작업 풀기',
  'reason.stripBatchStarted': '시작한 작업이 있어 바꿀 수 없음 — 끝나면 고를 수 있음',
  'action.newBatch': '＋ 작업 추가',
  'action.manualMode': '수동 조작',
  'action.manualEnd': '종료',
  'action.cancel': '취소',
  'action.save': '저장',
  'action.close': '닫기',
  'action.add': '추가',
  'action.change': '변경',
  'action.export': '내보내기',
  'action.commit': '확정',
  'action.acquireLock': '획득',
  // 🔴 「획득」과 갈라 둔다 — 만료는 「아직 안 잡았다」가 아니라 「쥐고 있었는데 끊겼다」다
  'action.reacquireLock': '다시 획득',
  'action.releaseLock': '반납',
  'action.forceReleaseLock': '강제 해제',
  // 모듈 서랍 「제어」 카드 「진단」 줄 단추(IFC-RO-48 · eric 2026-10-22) — 종전 「자가 진단」은 쓰는 화면이 없었다
  'action.runDiagnostics': '진단',
  'action.back': '뒤로',
  'action.eventsExpand': '이벤트',
  'action.toMaintTab': '정비 탭 →',
  'action.openInSamples': '시료 화면에서 보기',

  /*
   * ── 식별자 복사 ────────────────────────────────────────────────────────
   * 🔴 **화면에 보이는 것은 앞 8자인데 복사되는 것은 원값 전부다.** 라벨이 「전체」라고
   *    말하는 이유가 그것이다 — 잘린 값을 복사한 줄 알고 붙여 넣으면 서버가 404 를 준다.
   * 🔴 **못 하면 못 한다고 말한다.** 클립보드가 없는 호스트(비보안 오리진·구형 WebView)에서는
   *    버튼을 숨기지 않고 비활성 + 사유다. 실패도 조용히 넘기지 않는다.
   */
  'action.copyId': '전체 ID 복사',
  'state.copied': '복사됨',
  'reason.clipboardUnavailable': '이 화면에서는 복사할 수 없음',
  'error.copyFailed': '복사하지 못했습니다',

  // ── 상시 작업 줄 ─────────────────────────────────────────────────────────
  'strip.label': '작업 줄',
  'strip.waitingBatches': '대기 작업',
  'strip.prevStep': '이전 단계',
  'strip.currentStep': '현재 단계',
  'strip.nextStep': '다음 단계',
  'strip.eventsExpand': '펼치기',
  'strip.eventsCollapse': '접기',
  /** 작업 줄 접기/펼치기(2026-10-01 회의) — 단추의 접근성 이름 · 툴팁. 접어도 E-STOP 은 그대로 선다 */
  'strip.foldCollapse': '작업 줄 접기',
  'strip.foldExpand': '작업 줄 펼치기',
  /** 펼친 이벤트 칸(`StripEvents`) — 표 접근성 이름 · 작업 없음 · 구독 끊김 */
  'strip.eventsCaption': '이 작업의 이벤트',
  'strip.eventsNoBatch': '작업이 없습니다',
  'strip.eventsStale': '연결 끊김 — 새 이벤트가 반영되지 않습니다',
  /** 아래로 내려 읽는 동안 새로 선 행 수 — 누르면 맨 위(최신)로(eric 2026-10-06 「일반적으로 쓰는 방식으로」) */
  'strip.eventsNew': '새 이벤트 {count}건',
  'strip.eventsFilter': '이벤트 갈래',
  'strip.eventsFilter.all': '전체',
  'strip.eventsFilter.step': '스텝',
  'strip.eventsFilter.state': '상태 변경',
  'strip.eventsFilter.other': '기타',
  'strip.eventsKindStep': '스텝',
  'pagination.label': '쪽 넘김',
  'pagination.prev': '이전',
  'pagination.next': '다음',
  'pagination.status': '{page} / {pages} 쪽',
  'event.content.stateChange': '{from} → {to}',
  'event.content.dispatchItems': '스텝 {count}개 전송',
  'event.content.executionObserved': '{module} 실행 확인',
  'event.content.byActor': '{actor} 조작',
  'event.content.stepCount': '{step}/{total}',
  /** 실행 시작 · 끝 줄의 원자 수(`ExecutionStream.total`) */
  'event.content.stepTotal': '스텝 {total}개',
  'strip.openBatch': '작업 상세 보기',
  'strip.openRecipe': '레시피 보기',
  'strip.manualTitle': '수동 조작',
  'strip.estopTitle': '전 모듈 정지 요청 · 확인 대화 없음 · 역할·제어락 무관',
  /* E-STOP 두 줄(디자인팀 시안 2026-09-30) — 윗줄 `action.estop`, 아랫줄 한글 뜻. 접근성 이름은 둘을 합친 한 줄 */
  'strip.estopSub': '전체 비상 정지',
  'strip.estopName': 'E-STOP · 전체 비상 정지',
  /* 1행 라벨 달린 메타(디자인팀 시안 2026-09-30) — 「작업자 <이름>」(eric 2026-10-12 「담당을 작업자라고」) 「시작 <연월일 시분초>」 · 시작 전이면 「시작 전」 */
  'strip.metaOwner': '작업자',
  'strip.metaStarted': '시작',
  'strip.notStarted': '시작 전',
  /** 1행 「레시피 <이름>」 — 시안은 ≤1700px 에서 숨기지만 여기서는 라벨을 달아 늘 보인다(레시피 보기 링크 유지) */
  'strip.metaRecipe': '레시피',
  /*
   * ── 2행 공정 칸(디자인팀 시안 2026-09-30 §2 「공정 진행」 · `shell/StripStages.tsx`) ──
   * 칸 셋(원심 → 어셈블 → 염색)이 같은 뼈대다: [번호 · 이름 · 상태 배지] / 막대 / [완료 / 전체 스텝 · %].
   * 상태 배지 글자 — 시안 `ST_LABEL` 그대로. 진행 중 칸은 라벨 대신 **서버가 준 지금 스텝 문구**가 배지에 선다.
   */
  'strip.stage.done': '완료',
  'strip.stage.run': '실행 중',
  'strip.stage.wait': '대기',
  'strip.stage.pause': '일시정지',
  'strip.stage.stop': '중단',
  /**
   * 트레이 격리 · 낙하 표지 — 서버 `TrayTrack.where` 가 `quarantined` · `dropped` 일 때 **떠나기 전 모듈**(`last_module_id`) 칸에
   * 상태 배지 옆으로 선다(`stripStages.ts` `holdPill` · 07 §4.12). 툴팁에 트레이 이름 · 격리 행방(서버 원문)이 붙는다.
   */
  'strip.stage.hold.quarantined': '트레이 격리',
  'strip.stage.hold.dropped': '트레이 낙하',
  /** 셈 줄의 단위 — 「**176** / 176 스텝」. 🔴 모듈이 컴파일한 스텝이다(시료 수가 아니다) */
  'strip.stage.stepsUnit': '스텝',
  /** 막대의 접근 이름 — 「원심분리기 진행 176/176」 · 총수를 모르면 「전체 수량 집계 중」이 뒤에 선다 */
  'strip.stage.progress': '{module} 진행 {value}',
  /*
   * 2행 준비 · 완료 영역(eric 2026-10-06 「원심분리기 전에.. 준비 영역 … 염색기 오른쪽에 완료 영역도」 · `stripFlowZones`).
   * 이름은 한때 「대기」였다가 같은 날 「준비」로 돌아왔다 — 「대기」는 칸 상태 글자와 겹치고, 이 칸은 적재 · 6축 이송 · 다음 실행 발송을
   * 다 덮는다(eric 「응 그렇게 해줘」). 장비 칸 앞의 좁은 칸이다 — 눈에는 「준비」 한 마디(다음 칸이 장비 이름을 말한다), 접근 이름 ·
   * 툴팁은 「원심분리기 준비」. 지금이면 배지가 무엇을 하는지 말한다 — 사람 적재 대기면 서버 상태의 한글(「적재 대기」),
   * 이 배치가 6축을 쥐었으면 「6축 이송 중」, 그 밖은 「준비 중」.
   */
  'strip.zone.prep': '준비',
  'strip.zone.prepName': '{module} 준비',
  /** 묶음 안 장비 칸의 머리 — 막대 위 「진행」(eric 2026-10-12 「각각의 프로그레스바에 준비와 진행」). 장비 이름은 묶음 머리가 말한다 */
  'strip.zone.run': '진행',
  'strip.zone.runName': '{module} 진행',
  'strip.zone.done': '완료',
  'strip.zone.preparing': '준비 중',
  /** 준비 칸 배지 — 칸이 좁아 짧게(「운송 로봇 이송 중」 96px 는 1920 에서도 안 선다 · 실측 2026-10-13) */
  'strip.zone.transfer': '운송 중',
  /** 2행 목록(`ol`)의 접근 이름 — 대기 · 장비 · 완료가 차례대로 읽힌다 */
  'strip.flowLabel': '공정 흐름',
  /**
   * 「00 사전 점검」 칸(eric 2026-10-29 「01 원심분리기 왼쪽에.. 00 사전 점검」) — 평소 「–」 · [시작]을 눌러 창이 열리면 「점검 중」 →
   * 서버 결과 `passed` 그대로 「통과」 · 「막힘」. 시작된 작업이면 「완료」(`strip.stage.done`)
   */
  'strip.preflight.title': '사전 점검',
  'strip.preflight.checking': '점검 중',
  'strip.preflight.passed': '통과',
  'strip.preflight.blocked': '막힘',
  /** 오른쪽 요약 카드 — 「전체 **1,127** / 4,730 스텝」 · 「경과 00:23:18」 */
  'strip.tallyTotal': '전체',
  'strip.tallyElapsed': '경과',
  /*
   * 요약 카드 「예상」(eric 2026-10-06) — 배치 단위 예상 종료 · 남은 시간이 서버에 없어(BR-09) 「–」와 까닭만 선다.
   * 경과 ÷ 진행 비율로 만들지 않는다. 서버가 보내면 그 값이 이 자리에 선다.
   */
  'strip.tallySteps': '스텝',
  'strip.tallyTime': '시간',
  'strip.estimate.notSent': '서버가 아직 보내지 않음',

  /*
   * ── 멈춤 한 줄(정본 07 §4.12 · 서버 v0.9.4 `Batch.halt_resume` · `resume_blockers` · 2026-10-11) ──────────
   * 🔴 셋 중 하나를 고르는 것은 서버 칸이다(`screens/batches/haltLine.ts`). 막는 «문장»은 서버가 짓는다 — 여기로 옮기지 않는다.
   * 🔴 문구는 정본 원문 그대로다(「자동으로 이어 갑니다」 · 「조치가 필요합니다」). 막는 것이 없을 때(정본 「오류가 풀렸습니다…」)는
   *    한 줄을 그리지 않는다 — 사람이 일시정지해도 와서 없는 오류를 지어냈다(eric 2026-10-14 「이거 필요없지 않아?」 · `haltLine.ts`).
   */
  'halt.auto': '자동으로 이어 갑니다',
  'halt.action': '조치가 필요합니다',
  /** 막는 것 목록의 이름(보조기술 · 작업 상세) */
  'halt.blockers': '재개를 막는 것',
  /*
   * ── 확인 재개 · 멈춤 시간(백엔드 0.28.15 · eric 2026-10-22 「일단 너가 말한 거 적용해줘」) ──────────
   * 🔴 갈래는 서버 칸 `Batch.resume_route`(auto · resume · confirm)다 — 문장으로 가르지 않는다. `confirm` 이면 [재개]는 서버가
   *    `cut_confirm_required` 로 거절하고, [복구] «멈춘 동작부터 다시»(`rerun_cut_atom` · 현장 확인)로만 잇는다.
   */
  'halt.confirm': '현장 확인이 필요합니다 — [복구] «멈춘 동작부터 다시»로 이어 갑니다',
  'halt.checks': '확인할 것',
  'action.rerunCutAtom': '멈춘 동작부터 다시',
  'reason.resumeNeedsConfirm': '현장 확인 뒤 [복구] «멈춘 동작부터 다시»로만 이어 갈 수 있음',
  'reason.physicalChecksRequired': '확인할 것을 모두 체크해야 함',
  /** `Batch.resume_checks` · `RecoverDecision.physical_checks` 어휘 — 사람이 현장에서 볼 것 */
  'enum.resumeCheck.door_area': '문 주변에 걸린 것이 없다',
  'enum.resumeCheck.robot_area': '로봇 주변에 걸린 것이 없다',
  'enum.resumeCheck.tray_positions': '트레이가 제자리에 있다',
  'enum.resumeCheck.rotor_balanced': '원심기 로터의 균형이 맞다',
  /** 끊김이 따로 요구하는 확인이 없을 때의 한 칸 — `physical_checked` 의 뜻(장비 · 쥔 것 · 시료 자리) 그대로 */
  'resume.checkGeneral': '멈춘 동작 주변(장비 · 쥔 것 · 시료 자리)을 현장에서 확인했다',
  /** 끊긴 시간 원자(원심 회전 · 진탕)를 다시 돌릴 방식 — `resume_timed_atom_choices` 가 둘일 때만 고르기가 선다 */
  'resume.timedAtom': '끊긴 회전 · 진탕',
  'enum.timedAtom.restart': '처음부터',
  'enum.timedAtom.remaining': '남은 시간만',
  'strip.resumeTitle': '재개',
  /** 멈춤 시간 줄(`Batch.halt_delay` — 서버 계산 · 화면은 세지 않는다) */
  'halt.facts': '멈춤 정보',
  'halt.code': '멈춤 종류',
  'halt.elapsed': '멈춘 시간',
  'halt.allowed': '허용 지연',
  'halt.since': '멈춘 시각',
  'halt.delayState': '지연 판정',
  'enum.haltDelay.ok': '허용 안',
  'enum.haltDelay.warn': '허용 80% 넘음',
  'enum.haltDelay.exceeded': '허용 넘음',
  'enum.haltDelay.inconclusive': '판정 불가',
  'halt.module': '멈춘 모듈',
  'halt.remoteAt': '원격 호출 예정',
  /** 목록 화면 축약형(시안 `brief()`) — 현재 공정 한 줄. 「2 / 3 공정」 */
  'strip.briefOf': '{index} / {count} 공정',
  'action.openMonitoring': '모니터링에서 보기',
  /** 1행 이벤트 로그 단추(시안 「이벤트 로그」) — 펼치면 작업 줄 아래를 이벤트 표가 채운다 */
  'action.eventsLog': '이벤트 로그',
  /*
   * 작업별 이벤트 로그 내려받기(eric 2026-10-02) — 작업 상세 머리 · 작업 줄 이벤트 칸(`EventLogDownload`).
   * 이벤트 원장만(`event_type=event`). CSV 머리글은 응답 칸 차례다(`eventLogCsv.ts` `EVENT_DETAIL_COLUMNS`).
   * 「대상」 · 「심각도」 · 「종류」 · 「동작」 · 「상관 ID」는 `column.*` 을 같이 쓴다.
   */
  'action.eventLogDownload': '이벤트 로그 다운로드',
  'action.eventLogDownloadShort': '다운로드',
  'eventLog.downloading': '받는 중…',
  'eventLog.empty': '이 작업의 기록이 없습니다',
  'eventLog.truncated': '기록이 너무 많아 끝까지 받지 못했습니다({fetched}건에서 멈춤)',
  'eventLog.fileTag': '이벤트로그',
  /*
   * 관리 › 로그 「내보내기」(eric 2026-10-14) — 지금 탭의 표를 CSV 한 장으로(`screens/logs/logsCsv.ts`).
   * 머리 세 줄(무엇 · 언제 · 몇 행) 뒤에 표 머리글 · 행이다. 행이 없으면 단추가 비활성 + 사유다.
   */
  'logs.export.title': '로그',
  'logs.export.asOf': '내보낸 시각',
  'logs.export.rowCount': '행 수',
  'logs.export.fileTag': '로그',
  'logs.export.empty': '내보낼 행이 없습니다',
  'logs.export.saving': '내보내는 중…',
  'eventLog.csv.batchName': '작업 이름',
  'eventLog.csv.batchId': '작업 ID',
  'eventLog.csv.status': '작업 상태',
  'eventLog.csv.asOf': '기준 시각',
  'eventLog.csv.asOfValue': '{at} 까지',
  'eventLog.csv.rowCount': '행 수',
  'eventLog.csv.time': '시각',
  'eventLog.csv.isoTime': '시각(원본)',
  'eventLog.csv.ledger': '원장',
  'eventLog.csv.errorCode': '에러 코드',
  'eventLog.csv.errorMessage': '에러 메시지',
  'eventLog.csv.executionId': '실행 ID',
  'eventLog.csv.commandId': '명령 ID',
  'eventLog.csv.context': '문맥',
  'eventLog.csv.extra': '그 밖',
  /*
   * 🔴 **「진행」이라고 쓰지 않는다.** 이 수는 배치 전체가 아니라 **모듈이 보고한 것**이다
   *    (`batch_progress` 본문 = `ExecutionStream` 동형 · `step`·`total` 뿐). 라벨이 범위를
   *    말하지 않으면 읽는 사람이 배치 전체로 읽는다 — ctcells-core 관리자 화면이 같은 어휘를 쓴다.
   */
  /* 2026-09-22 부터 화면에는 수만 보이고(2행 오른끝) 이 문구는 툴팁·보조기술용이다 */
  'strip.moduleStep': '모듈 내 스텝 {value}',
  /*
   * 🔴 **2행 오른끝은 «전체» 스텝이다**(eric 2026-09-23 「맨 오른쪽은 전체 스텝에서의 현재
   *    스텝이어야 해」). 모듈 내 수는 구간 오른끝 아래(`strip.moduleStepAt`)로 내려갔다.
   */
  'strip.totalStep': '전체 스텝 {value}',
  /*
   * 분모가 아직 늘 수 있다는 말. 아직 시작 안 한 모듈의 총수는 **어디에도 없다** —
   * 모듈이 레시피를 컴파일해야 나오고 그 수는 시작 프레임으로 처음 온다(2026-09-23 실측).
   */
  'strip.totalStepPartial': '아직 시작하지 않은 모듈의 스텝 수는 모릅니다',
  /*
   * 아직 시작 안 한 모듈의 총수를 **이 브라우저가 지난번 같은 레시피에서 들은 수**로 채웠다는 조용한 표지
   * (eric 2026-09-28 — 숫자는 깔끔하게 · `+?` 는 정말 모를 때만). 실제 프레임이 오면 실제 값으로 바뀐다.
   */
  'strip.totalStepEstimated': '지난 작업 기준 예상 포함',
  'strip.moduleStepEstimated': '지난 작업 기준',
  /** 구간 아래 수의 보조기술 문구 — 「원심분리기 23/93」 */
  'strip.moduleStepAt': '{module} {value}',
  'strip.dismissError': '실패 문구 닫기',
  /*
   * 빈 띠의 시료 처분 대기 안내(`BatchStrip` · `stripDisposition` · DP-02) — [중지]한 작업은 띠에서 빠져도 시료 처분은 사람이 닫을
   * 때까지 남는다. 서버 `review_reason=sample_disposition` 이 풀리면 사라진다. × 는 이 화면에서만 숨긴다(서버 상태 그대로).
   */
  'strip.disposition.text': '시료 처분 대기 — 작업 {id}',
  'strip.disposition.more': '외 {count}건',
  'strip.disposition.open': '이력에서 처리 →',
  'strip.disposition.title':
    '작업 {id} 의 시료가 아직 처분되지 않았습니다. 작업 상세(이력)에서 처분하면 이 안내가 사라집니다.',
  'strip.disposition.dismiss': '이 안내 숨기기 — 서버의 처분 대기는 그대로입니다',
  /*
   * 복구 › 시료 처분 창(`DispositionModal` · 정본 07 §4.4 [A-3] B-0252). 두 갈래의 뜻은 계약 `RecoverDecision.sample_ids` ·
   * `reason` 설명 그대로다(실서버 0.28.15) — 지어내지 않는다.
   */
  'disposition.title': '시료 처분',
  'disposition.submit': '처분 확정',
  'disposition.rerun': '다시 돌림',
  'disposition.discard': '폐기',
  'disposition.allRerun': '전부 다시 돌림',
  'disposition.allDiscard': '전부 폐기',
  'disposition.choice': '처분',
  'disposition.note': '비고',
  'disposition.done': '처리됨',
  'disposition.refused': '받지 않음 — 서버가 거절했습니다(다시 돌림 조건 밖이면 폐기를 고르세요)',
  'disposition.rerunBlocked': '처리가 시작된 시료 — 폐기만 받습니다',
  'disposition.empty': '이 작업에 처분할 시료(진행 중 · 판정 대기)가 없습니다',
  'disposition.failed': '{decision} 처리 실패 — {message}',
  'disposition.help.rerun':
    '다시 돌림 — 시료를 이 작업에서 풀고 단계를 등록으로 되돌립니다. 새 작업에 다시 담을 수 있고 옛 이력은 남습니다. 등록 단계이고 이 작업의 분주 기록이 없는 시료만 받습니다.',
  'disposition.help.discard': '폐기 — 시료를 폐기합니다. 작업은 중단 상태 그대로 남습니다.',
  'disposition.help.reason':
    '사유는 두 갈래 모두 필수이고 감사 기록에 남습니다(폐기는 시료의 폐기 사유로도 남습니다).',
  /*
   * 「중지」 뒤 모듈 실행 취소(eric 2026-09-28 「작업 중지하는 api 가 없어? 끝까지 돌려야 하는 거야?」 → 「프론트에서 처리」).
   * 중지는 작업 상태만 바꾸고 모듈은 받은 블록을 끝까지 돈다 — 그래서 실행 취소를 이어 보낸다(`useCancelAfterStop`).
   * 🔴 실패·남은 실행 문구는 사람이 닫을 때까지 선다(DP-02). 취소를 자동으로 다시 쏘지 않는다.
   */
  'strip.execCancel.failed': '장비가 아직 돌고 있습니다 — {items}',
  'strip.execCancel.item': '{module} {reason}',
  'strip.execCancel.itemCode': '{module} {reason}({code})',
  'strip.execCancel.lookupFailed':
    '장비가 아직 돌고 있을 수 있습니다 — 실행 취소를 보내지 못했습니다: {reason}',
  'strip.execCancel.leftover': '끝난 작업의 실행이 장비에 남아 있습니다 — {modules}',
  'strip.execCancel.waiting': '실행 취소를 보냈습니다 — 장비가 멈추기를 기다립니다: {modules}',
  /** 모듈 `terminal_reason` 에 실린다 — 남은 실행을 사람이 취소할 때 */
  'strip.execCancel.leftoverReason': '끝난 작업의 남은 실행 취소',
  'action.cancelExecution': '실행 취소',
  'action.retryCancelExecution': '다시 시도',
  /*
   * 단계 줄의 빈 상태 — 🔴 **행을 지우는 대신 적는 문구다**(2026-09-22).
   *    높이 불변(§13)이라 자리는 늘 남는데, 종전에는 그 자리가 공백이어서 작업 줄 아래가
   *    아무것도 없는 흰 띠였다. 「값이 없다」를 값처럼 꾸미지 않고 없다고 적는다.
   */
  'strip.noProgress': '진행 정보 없음',
  /* 1행 시간 칸 — started_at 로 «경과», completed_at 까지 있으면 «소요». 총 예상·ETA 는 서버에 없어 없다 */
  'strip.elapsed': '경과 {time}',
  'strip.duration': '소요 {time}',
  'strip.safetyCritical': '안전정지 · CRITICAL {count}건',
  'strip.segments': '구간 진행',
  // 확인 대화 — 되돌릴 수 없는 명령만 거친다(중지 · 복구 · 락 반납/강제 해제)
  'strip.stopTitle': '작업 중지',
  'strip.stopConfirmText': '작업을 중지하시겠습니까?',
  // 중지 사유는 선택(eric 2026-10-26) — 칸의 안내 글이자, 비우고 확인하면 계약 필수값(minLength 1) 자리에 실려 감사 원장에 남는 글이다(한 키 · 보이는 글 = 남는 글)
  'strip.stopReasonDefault': '사유 미입력',
  /*
   * 시작 게이트 대화 — 🔴 **제목이 두 갈래다.** 차단은 시작 버튼 자체가 없고, 경고는
   * 확인하면 시작할 수 있다. 한 제목으로 뭉치면 「확인하면 되는 줄」로 읽힌다.
   */
  'strip.startTitle': '작업 시작',
  /*
   * ── 시작 전 시약 용량 확인(정본 07 §4.4 ③ · `shell/StripReagentDialog` · 2026-10-11) ──────────────────────
   * 🔴 기본값은 레시피 표준 충전량(`Batch.reagent_basis`)이다 — 잰 값이 아니다. 대상 모듈은 서버 칸(`reagent_module_id`) 하나다.
   */
  'strip.reagentTitle': '시약 용량 확인',
  'strip.reagentModule': '대상 모듈',
  'strip.reagentBasis': '기준',
  'strip.reagentBasisValue': '레시피 표준 충전량 · 잰 값 아님 · 고칠 수 있음',
  /** `Batch.reagent_module_id = null` — 서버 설명 문면(「시약이 없거나 · 활성 염색 모듈이 0 또는 둘 이상이면 null」) */
  'strip.reagentTargetUnknown':
    '대상 미정 — 시약 보충 확인을 보낼 염색 모듈을 서버가 정하지 못했습니다(활성 염색 모듈이 없거나 둘 이상)',
  /** 리저버(`labwareId`)를 몰라 장부 키가 없는 시약 — 막지 않고 그 사실만 적는다 */
  'strip.reagentUnplaced': '{reagent} — 리저버를 몰라 기록하지 않습니다',
  /** 웰 칸의 보조기술 이름 */
  'strip.reagentWell': '{reagent} {well} 보충 후 양(µL)',
  /** 보충 기록 원장 표지(`note`) — append-only 라 누가 왜 적었는지를 남긴다 */
  'strip.reagentNote': '시작 전 시약 용량 확인 · {batch}',
  'action.confirmAndStart': '확인하고 시작',
  'reason.reagentTargetUnknown': '시약 보충을 보낼 염색 모듈이 정해지지 않았습니다',
  'reason.reagentVolumeInvalid': '양은 0 이상의 수로 적어야 합니다',
  /*
   * 🔴 **서버가 정정해 준 목록을 보일 때만 뜬다**(409 `warn_ack_mismatch`).
   *    시작 직전에 서버가 Pre-flight 를 다시 재서 그 사이 경고가 갈린 것이다 —
   *    「보냈는데 안 됐다」가 아니라 **「다른 것을 보고 확약할 뻔했다」**가 이 말의 뜻이다.
   */
  'strip.warnAckChanged': '경고 목록이 바뀌었습니다 — 다시 확인하세요',
  /** 확약이 실려 나가지 않았다(`warn_ack_required`·`warn_ack_snapshot_missing`) */
  'strip.warnAckMissing': '확약이 서버에 닿지 않았습니다 — 아래 경고를 다시 확인하세요',
  'strip.recoverTitle': '복구 판정',
  'strip.recoverDecision': '판정',

  // ── 제어락 ───────────────────────────────────────────────────────────────
  'lock.label': '제어락',
  'lock.none': '없음',
  'lock.unknown': '제어락 상태 확인 불가',
  'lock.holderUnknown': '다른 사용자가 보유 · 이름 모름',
  /* 다른 사람이 쥐었다 — `holder.display_name`(2026-10-23 · 히나 조사 1-2 「○○ 보유」) */
  'lock.heldBy': '{name} 보유',
  /* 정본 07 §2.7 「정지 발송 · 도달 n/m」 그대로(2026-10-23) — n/m 은 서버 `SafetyState.stop_reach` 값이다(화면이 세지 않는다) */
  // 정지 명령을 못 받은 장비가 있을 때만(다 도달 · 값 없음이면 칩이 없다 · eric 2026-10-29 「도달 4/4 — 필요해?」 → 팀장 결정)
  'strip.stopUnreachedChip': '정지 미도달 {count}대 · {modules}',
  'strip.safetyStopped': '안전정지',
  'lock.noPermission': '권한 없음',
  'lock.expiresAt': '만료',
  'lock.expired': '만료됨',
  /*
   * 🔴 **보유자 자리에 세션 ID 를 띄우지 않는다.** 409 `context.holder` 는 세션 ID 다
   *    (백엔드 결함 — `docs/backend-requests.csv` BR-03). 남의 락은 이름 없이 이 문구로만 말한다.
   *    내 락은 획득 응답의 `holder`(= `holder_display()` 를 거친 사용자 표기)가 있으면 그것을 쓴다.
   */
  'lock.heldByMe': '내가 보유',
  'lock.heldByOther': '다른 사용자가 보유',
  /** 내 락 [반납]이 묻지 않고 바로 나갈 때 싣는 고정 사유(`DELETE /control-token` 의 `reason` 필수 · eric 2026-10-29) */
  'lock.releaseReason': '사용자 반납',
  'lock.forceReleaseTitle': '제어락 강제 해제',

  // ── 안전 · 연결 ──────────────────────────────────────────────────────────
  'safety.estopped': '안전정지',
  'safety.controlDisabled': '연결 끊김 — 제어 불가',
  'safety.moduleStale': '응답 없음',
  /*
   * 🔴 **낡음 표기(2026-09-25 정본 07 · 계약 `ModuleSnapshot.stale`)** — 투명도로 흐리지 않는다.
   *    「응답 없음」 + **마지막 값 병기**: 남은 값이 «지금»이 아니라 «마지막»임을 글자로 말한다.
   *    시각은 서버가 준 `last_seen` 이다. 없으면 시각 없이 「마지막 값」만 붙인다.
   */
  'stale.lastSeen': '마지막 응답 {time}',
  'stale.lastValue': '{value} · 마지막 값',
  'panel.metricsStale': '마지막 메트릭',

  // ── 비활성 사유 (툴팁) ───────────────────────────────────────────────────
  'reason.noPermission': '{perm} 권한 없음',
  /* `GET /users` 는 admin 전용(`users.py ALLOWED_ROLES`) — 관리 › 사용자 탭 안의 사유 */
  'reason.usersAdminOnly': '사용자 관리는 admin 만 볼 수 있습니다',
  'reason.lockRequired': '제어락 필요',
  /** 비상정지 중에 막는 명령의 사유(eric 2026-10-14 「E-STOP 상태에서 활성화되면 안 되는 버튼은 비활성화」) */
  'reason.estopActive': '비상정지 중 — 안전 해제 후 가능',
  'reason.batchBusy': '작업 동작 중 — 제어 잠금',
  'reason.manualOnly': '수동 조작에서만',
  'reason.batchActive': '활성 작업 있음',
  'reason.pinRequired': 'PIN 로그인 필요',
  'reason.noRunningBatch': '진행 중인 작업 없음',
  'reason.noRecoveryPending': '복구 대기 없음',
  'reason.manualInProgress': '수동 조작 중 — 끝내고 시작',
  'reason.streamStale': '연결 끊김 — 제어 불가',
  'reason.commandNotAllowed': '지금 상태에서는 할 수 없음',
  /**
   * 멈춘(일시정지 · 비상정지 · 실패) 작업의 [중지] — 서버가 그 상태에서 중지를 받지 않는다(core `FROM_STOP` = created · queued · running ·
   * `stop_batch` 「paused 는 받지 않는다 — 그 중단은 폐기 결정이다」). 끝내는 길을 말한다.
   */
  'reason.stopWhileHalted': '멈춘 작업은 [복구 › 폐기]로 끝냅니다',
  /** 끝난(중단 · 완료) 작업의 복구 › 재개 · 폐기 · 완료 확정 — 계약 `recoverBatch` 「aborted 는 discard_samples · rerun_samples 만」 */
  'reason.recoverEnded': '끝난 작업은 재개 · 폐기 · 완료 확정을 받지 않음',
  /** 위와 같은데 이송 격리가 남았다(`review_reason=handoff_unconfirmed`) — 격리를 풀면 서버가 [시료 처분]을 연다 */
  'reason.recoverEndedHandoff':
    '끝난 작업은 재개 · 폐기 · 완료 확정을 받지 않음 — [트레이 자리 정리]로 이송 격리를 먼저 풀면 [시료 처분]이 열립니다',
  'reason.pinEmpty': 'PIN 을 입력하세요',
  'reason.confirmRequired': '확인란을 체크하세요',
  /**
   * 그 셸에 갈 곳(라우트)이 없어서 막힌 자리. 🔴 **권한 부족과 다른 사실이다** —
   * 권한은 사람이 바꿀 수 있고 이것은 화면 구성이라 못 바꾼다(태블릿에 레시피 상세가 없다).
   */
  'reason.noRouteHere': '이 화면에서는 이동할 수 없음',
  /*
   * 시작이 막힌 사유 — `shell/startBlockers.ts` 가 **프론트가 따로 받은 사실**과 대조해 고른다.
   * 🔴 서버 `allowed_commands` 에는 사유 칸이 없다. 대조로 못 찾으면 `serverOnly` 로 «모른다»고 말한다.
   */
  'startBlock.title': '시작할 수 없는 이유',
  'startBlock.role': '이 역할은 시작할 수 없습니다',
  'startBlock.lock': '제어락을 잡지 않았습니다',
  'startBlock.otherBatch': '다른 작업({name})이 {status} 상태입니다',
  'startBlock.preflight': '사전 점검 차단 — {codes}',
  'startBlock.preflightPending': '사전 점검 결과를 아직 받지 않았습니다',
  'startBlock.serverOnly': '서버가 시작을 허용하지 않습니다(사유 미제공)',

  // ── 경계 상태 ────────────────────────────────────────────────────────────
  'state.loading': '불러오는 중',
  'state.preparing': '준비 중',
  'state.denied.banner': '권한 없음 — {perm} 필요 · 현재 역할 {role}',
  'state.denied.screen': '화면은 잠긴 채 보이고 쓰기는 전부 비활성',
  'state.empty.generic': '없음',
  /* 표 마무리 줄 — **지금 그린 행 수**다. 서버 전체 건수가 아니다(집계 경로가 없다) */
  'table.rowCount': '{count}건',
  'state.empty.recipes': '사용 중인 레시피 없음',
  'state.empty.sessions': '활성 세션 없음',
  /* 만료 시각이 지나 표에서 뺀 세션 수(eric 2026-09-30) — 「못 봤다」가 「없다」로 읽히지 않게 */
  'state.sessionsExpiredHidden': '만료 {count}건 숨김',
  'state.empty.alarms': '알람 없음',
  'state.empty.batches': '작업 없음',
  'state.empty.samples': '시료 없음',
  'state.empty.events': '이벤트 없음',
  /*
   * 🔴 **「못 봤다」는 「0건」이 아니다.** 조회가 막히거나 실패한 자리에 붙는 머리말이다 —
   *    사유 문장 «앞»에 서서, 뒤따르는 문장이 **목록의 내용이 아니라 못 본 까닭**임을 말한다.
   *    빈 상태(`state.empty.*`)와 생김새가 같으면 사람이 「없구나」로 읽는다.
   */
  'state.notShown': '내용을 보지 못했습니다',

  // ── 입력 규칙 위반 ───────────────────────────────────────────────────────
  // 🔴 평소에는 쓰지 않는다. 입력값이 규칙을 못 채울 때만 그 필드 아래에 나타난다(README §1-1).
  'validation.password.minLength': '{n}자 이상',
  'validation.password.kinds': '대문자·소문자·숫자·특수문자 중 {n}종 이상',

  // ── 확약 드롭다운 ────────────────────────────────────────────────────────
  'confirm.load.title': '적재 확인',
  'confirm.load.subject': '원심분리기 · 전면 입고 거치대',
  'confirm.load.item.chambers': '하이픽챔버 9개 (A1~C3)',
  'confirm.load.item.counterweight': 'R″ 평형추 1개 — 손잡이 왼쪽 칸',
  'confirm.load.item.seated': '트레이가 거치대에 완전히 안착',
  'confirm.load.submit': '적재 확인',
  'confirm.unload.title': '수거 확인',
  'confirm.unload.subject': '염색기 · 전면 수거 자리',
  'confirm.unload.item.allOut': 'L 트레이 전량 반출(염색 실패분 포함)',
  'confirm.unload.item.bayEmpty': '수거 자리 비움 확인',
  'confirm.unload.submit': '수거 확인',
  'confirm.start.title': '시작 전 확인',
  'confirm.start.submit': '시작',
  'confirm.checked': '위 항목을 확인했습니다',
  'confirm.warnAck': '경고를 인지하고 시작합니다',

  // ── 모듈 ─────────────────────────────────────────────────────────────────
  'module.cent': '원심분리기',
  'module.asm': '어셈블러',
  'module.stain': '염색기',
  'module.robot': '운송 로봇',
  'module.vision': '비전 카메라',
  'module.safety': '안전계',
  'module.system': '시스템',

  // ── 모듈 패널 구역 제목 ──────────────────────────────────────────────────
  'panel.metrics': '현재 메트릭',
  'panel.progress': '진행',
  'panel.chambers': '챔버',
  'panel.info': '정보',
  /*
   * 장비별 보기 오른쪽 상세 카드(디자인팀 시안 `equipView()` · eric 2026-10-02 「디자인팀이 한 html 로 바꿔줘」).
   * 🔴 「미수신」은 **응답에 칸은 있는데 값이 `null`** 일 때만 쓴다 — 칸 자체가 없으면 타일을 안 만든다.
   */
  'panel.detailTitle': '{name} 상세',
  'panel.chambersOf': '{name}의 챔버',
  'panel.unreceived': '미수신',
  'panel.alarmCount': '{count}건',
  'panel.slots': '슬롯',
  'panel.channels': '채널',
  'panel.lastInspection': '마지막 검사',
  'panel.verdictByTarget': '항목별 판정',
  'panel.device': '장치',
  'panel.controlOwner': '제어 소유',
  /** 🔴 **안 섰으면 줄째 없다** — 「보류 없음」을 적지 않는다(`ModuleSnapshot.hold`) */
  'panel.hold': '보류',
  'panel.holdSince': '보류 시작',
  'panel.lastDiagnostic': '마지막 진단',
  'panel.remainingSamples': '남은 시료',
  'panel.inProgress': '실행 중',
  'panel.done': '완료',
  'panel.aria': '선택 항목 상세',
  'panel.legend': '범례',

  // ── 패드 ─────────────────────────────────────────────────────────────────
  'pad.runDemo': '실행 데모',
  'pad.confirmHere': '확약 · 이 패드',

  // ── 패드 › PIN 잠금 해제 ─────────────────────────────────────────────────
  // 🔴 콘솔 로그인이 아니라 **단말 잠금 해제**다. 「로그인」이라는 말을 쓰지 않는다.
  //    자릿수·형식 같은 입력 규칙 문구를 두지 않는다(§1-1) — 못 누르는 이유는 툴팁뿐이다.
  'pin.title': 'PIN 잠금 해제',
  'pin.entered': '{count} 자리 입력',
  'pin.clear': '전체 지움',
  'pin.clearShort': '전체',
  'pin.backspace': '한 자 지움',
  'pin.backspaceShort': '⌫',
  'pin.submit': '해제',
  'pin.checking': '확인 중',

  // ── 패드 › 정비 ──────────────────────────────────────────────────────────
  // 제어 소유 표기는 「시스템」 / 「단독」이다(enum.controlOwner). 「오케스트레이터」라고 쓰지 않는다.
  'maint.group.diag': '진단 · 보정',
  'maint.toLocal': '단독 모드 전환',
  'maint.toLocalHint': '이 패드가 제어 · 콘솔에는 잠금으로 표시',
  'maint.toSystem': '단독 모드 해제',
  'maint.toSystemHint': '시스템에 반환 · 사전 점검 재수행',
  'maint.confirmToLocal': '콘솔 제어를 이 패드로 가져옵니다',
  'maint.confirmToSystem': '제어를 시스템으로 되돌립니다',
  'maint.validUntil': '다음 {at}',

  // ── 새 작업 스테퍼 ───────────────────────────────────────────────────────
  'stepper.newBatch.sample': '시료',
  // 🔴 「· 배정」을 빼지 마라 — 챔버 배정 자리가 «응답에 없어» 걷혔다(2026-09-20). 이름만 남기면 거짓말이다
  'stepper.newBatch.recipe': '레시피',

  // ── enum → 화면 표기 ─────────────────────────────────────────────────────
  // 🔴 마지막 세그먼트는 API 원값이다. 코드에서 원값을 한글로 바꾸지 않는다.
  // batch.status (orchestrator_api Batch.status)
  'enum.batchStatus.created': '대기',
  /*
   * 🔴 **계약 enum 7종을 다 덮는다.** `queued` 만 빠져 있어서 서버가 그 값을 주면
   *    `batchStatusLabel()` 이 타입에서 막혔다(트리거 API 가 없어 지금은 안 오지만,
   *    **받으면 그려야 한다** — `packages/contract/src/batch.ts` 주석과 같은 이유다).
   *    `created`(대기 = 만들어만 뒀다)와 갈라야 해서 「시작 대기」다 — Pre-flight 를 지나
   *    시작을 기다리는 자리다(정본 `startBatch` 「통과 시 queued→running」).
   */
  'enum.batchStatus.queued': '시작 대기',
  'enum.batchStatus.running': '실행 중',
  'enum.batchStatus.paused': '일시정지',
  'enum.batchStatus.completed': '완료',
  'enum.batchStatus.aborted': '중단',
  'enum.batchStatus.error': '오류',
  /*
   * ExecutionStream.type (events.asyncapi `IFC-MQ-17` · `batch_progress` 본문) — **실행 생애 축**.
   * 🔴 배치 상태(`enum.batchStatus`)와 **다른 축이다.** 이것은 「모듈이 돌리는 실행 한 건」의
   *    사건이고, 배치 상태는 L2 가 가진 배치의 상태다. 같은 한글이 겹치는 것은 우연이 아니라
   *    두 축이 같은 일을 다른 주어로 말하기 때문이다 — 키를 합치지 마라.
   */
  'enum.executionEvent.started': '시작',
  'enum.executionEvent.progress': '진행',
  'enum.executionEvent.telemetry': '계측',
  'enum.executionEvent.step_complete': '스텝 완료',
  'enum.executionEvent.complete': '완료',
  'enum.executionEvent.error': '오류',
  'enum.executionEvent.cancelled': '취소',
  'enum.executionEvent.aborted': '중단',
  'enum.executionEvent.paused': '일시정지',
  'enum.executionEvent.resumed': '재개',
  /*
   * `ModuleSnapshot.state` — **계약 enum 30값 전수**(`ctcells-core/app/schemas/generated/module.py`
   * `class ModuleStatus` `state: Literal[...]` · 2026-09-26 24 → 30).
   *
   * 🔴 **전수를 채운 이유** — 실물 배치에서 배지가 «비어» 나왔다(2026-09-20 실측).
   *    종전 사전은 7값뿐이었고 그중 `READY`·`PAUSED`·`STOPPED`·`MANUAL` 넷은 **서버가 내지
   *    않는 값**이었다. 실물이 `LOADING`·`WAITING_FOR_UNLOAD` 를 보내자 `moduleStateLabel()`
   *    이 `t()` 로 바로 들어가 **`undefined`** 를 냈고 배지 안이 통째로 비었다.
   *    빈 배지는 「상태 없음」으로 읽힌다 — 돌고 있는 장비를 멈춘 것처럼 보이게 한다.
   *
   * 🔴 **2026-09-26 — 트레이 단위 상태 둘 + 대기 이름 TRAY 계열**(정본 `06 §4.1` 「은퇴 상태
   *    대응」 · `§4.2` · `§4.2a`). 수거 대기가 **주체로 갈렸다** — 로봇이 후면에서 가져가는
   *    `WAITING_FOR_TRAY_UNLOAD`(「로봇 회수 대기」 · 작업 중)와 사람이 전면에서 가져가는
   *    `WAITING_FOR_MANUAL_TRAY_UNLOAD`(「사람 수거 대기」 · 개입 대기). 낱말에 주체를 싣는다
   *    (`07` 태블릿 절 「사람 수거 대기」 · 「로봇 회수 대기」).
   *    ⚠ **은퇴 이름**(`WAITING_FOR_UNLOAD` · `WAITING_FOR_MANUAL_UNLOAD` · `WAITING_FOR_MANUAL_LOAD` ·
   *    `OUTPUT`)은 계약 enum 과 원장 옛 행에 남는다 — 새 전이는 없지만 옛 기록을 그리려면 사전에
   *    있어야 한다. 모듈별 부분집합은 `06 §4.2` `SM-MOD-01~04 · 11` 이 권위다.
   */
  // 공통 (전 모듈)
  'enum.moduleState.IDLE': '대기',
  'enum.moduleState.ERROR': '오류',
  'enum.moduleState.RECOVERY': '복구 중',
  // 🔴 블록 실행 취소로 그 자리에 멈춘 두 상태 — 전 모듈 공통(`06 SM-MOD-23`). 취소를 연 쪽이 가른다.
  //    문구는 카탈로그 `MODULE_ABORTED` 「취소로 멈춰 있음」 · `MODULE_INTERRUPTED` 「이어 보내기를 기다리는 중」
  //    ABORTED = 사람이 연 취소 · 사람 정리 뒤 원점 복귀(개입 대기) / INTERRUPTED = 코어 자동 취소 · 이어 가기 대기(작업 중)
  'enum.moduleState.ABORTED': '취소로 멈춤',
  'enum.moduleState.INTERRUPTED': '이어 보내기 대기',
  // 원심분리기 — `RUNNING`·`UNLOADING` 은 원심 전용이다 (`06 §4.1`)
  'enum.moduleState.LOADING': '적재 중',
  'enum.moduleState.WAITING_FOR_MANUAL_LOAD': '적재 대기',
  'enum.moduleState.RUNNING': '실행 중',
  'enum.moduleState.UNLOADING': '회수 중',
  // 🔴 트레이 단위(2026-09-26) — 트레이가 모듈 경계를 넘는 동안(`06 §4.1` · `slide_tray rear·front` ·
  //    핸드오프 `prepare`~`confirm`). 「반입 · 반출」은 `06 §4.2b` 트리거 낱말(「트레이 반입 슬라이드」)
  'enum.moduleState.TRAY_LOADING': '트레이 반입 중',
  'enum.moduleState.TRAY_UNLOADING': '트레이 반출 중',
  // 사람 적재 대기(`07` 태블릿 절 「적재 대기」) — 은퇴 `WAITING_FOR_MANUAL_LOAD` 의 새 이름
  'enum.moduleState.WAITING_FOR_MANUAL_TRAY_LOAD': '적재 대기',
  // 🔴 주체가 갈린다(`06 §4.2a` 「수거 대기가 둘인 까닭」) — 로봇 후면 회수 ↔ 사람 전면 수거
  'enum.moduleState.WAITING_FOR_TRAY_UNLOAD': '로봇 회수 대기',
  'enum.moduleState.WAITING_FOR_MANUAL_TRAY_UNLOAD': '사람 수거 대기',
  // 은퇴 `WAITING_FOR_MANUAL_UNLOAD`(2026-09-25 하루 쓰인 이름) → `WAITING_FOR_MANUAL_TRAY_UNLOAD`
  'enum.moduleState.WAITING_FOR_MANUAL_UNLOAD': '사람 수거 대기',
  // 어셈블러 — 어휘는 `specs/vocab/equipment.yaml` 의 동작 29값에서 가져왔다
  'enum.moduleState.TC_LOAD': 'TC 장착',
  'enum.moduleState.UV': 'UV 노출',
  'enum.moduleState.TC_REMOVE': 'TC 분리',
  'enum.moduleState.ROTATE_DRAIN': '반전 배출',
  'enum.moduleState.OUTPUT': '산출물 반출',
  // 염색기
  'enum.moduleState.TIP_CHANGE': '팁 교체',
  'enum.moduleState.PIPETTING': '흡입 중',
  'enum.moduleState.DISPENSING': '분주 중',
  'enum.moduleState.INCUBATING': '인큐베이션',
  'enum.moduleState.SHAKING': '쉐이킹',
  // 🔴 「스텝 간 대기」다 — 사람을 기다리는 자리가 아니다(`SM-MOD-14` · `06 §4.2a` 표시 각주)
  'enum.moduleState.WAITING': '스텝 대기',
  'enum.moduleState.COMPLETE': '처리 완료',
  // ⚠ 은퇴 이름(→ `WAITING_FOR_TRAY_UNLOAD` · `06 §4.1`). 2026-09-25 전 옛 기록에서만 온다 — 그때는
  //   염색기의 사람 수거도 이 이름이었다. 주체를 단정할 수 없어 낱말을 「수거 대기」로 둔다
  'enum.moduleState.WAITING_FOR_UNLOAD': '수거 대기',
  // 저수준 이동 — 3모듈 + 로봇 공용 (`SM-MOD-16`)
  'enum.moduleState.MOVING': '이동 중',
  // 6축 협동로봇
  'enum.moduleState.HOMING': '원점 복귀',
  'enum.moduleState.HANDOFF': '핸드오프',
  'enum.moduleState.SAFE_STOP': '안전정지',
  /*
   * ⚠ 아래 셋은 **서버가 내지 않는 값**이다 — 화면 별칭이 이 사전을 빌려 쓴다.
   *   `MANUAL` 은 상시 작업 줄의 「단독 운전」 표시용 유사 상태다(`BatchStrip`).
   *   `PAUSED`·`STOPPED` 는 사용자 상태 배지(`AdminScreen`·`UserDetailScreen`)가 쓴다.
   *   🔴 **이 셋을 모듈 상태로 새로 쓰지 마라.** 실물에서 오지 않는다.
   *
   * 🔴 **`READY` 를 되살리지 마라**(2026-09-20 제거). 그것은 **와이어프레임의 내부 키**였고
   *    정본 원값은 `WAITING_FOR_MANUAL_LOAD` 다(§15 · 계약 30값에 `READY` 가 없다).
   *    사전에 있는 동안 목이 그 값을 모듈 상태로 써서 「데모만 통과하는 틀린 상태」가 됐다 —
   *    목을 실물 값으로 고치자 소비자가 0이 됐다. 염색기·어셈블러에는 `WAITING_FOR_MANUAL_LOAD`
   *    자체가 없고 그 자리는 `WAITING_FOR_UNLOAD` 다.
   */
  'enum.moduleState.PAUSED': '일시정지',
  'enum.moduleState.STOPPED': '정지',
  'enum.moduleState.MANUAL': '수동 조작',
  /*
   * 모듈 상태의 **굵은 표기** — 정본 `06 §4.2a` 6분류(`statusKind()` 의 `StatusKind`)에 한 벌씩.
   * 관리 › 모듈 표의 「상태」 열이 쓴다(eric 2026-09-23 「디테일한 걸로 하지 말고 대기 · 진행 이런
   * 식으로.. 수시로 바뀌게 하지 마」). 세부 동작(이동 중 · 흡입 중 …)은 스트림마다 바뀌어 배지가
   * 깜빡였다. 🔴 세부 표기(`enum.moduleState.*`)는 그대로 산다 — 상세 화면과 툴팁이 쓴다.
   * ⚠ `pause` · `done` · `manual` 은 서버 모듈 상태에서 오지 않는 분류다(위 별칭 셋과 배치 상태가
   *   쓴다) — `StatusKind` 전수를 채우느라 둔 것이고 모듈 표에 뜰 일은 없다.
   */
  'enum.moduleStateKind.idle': '대기',
  'enum.moduleStateKind.run': '진행',
  'enum.moduleStateKind.wait': '개입 대기',
  'enum.moduleStateKind.err': '오류',
  'enum.moduleStateKind.recovery': '복구 중',
  'enum.moduleStateKind.stop': '안전정지',
  'enum.moduleStateKind.pause': '일시정지',
  'enum.moduleStateKind.done': '완료',
  'enum.moduleStateKind.manual': '수동 조작',
  // recipe.status
  'enum.recipeStatus.draft': '작성 중',
  'enum.recipeStatus.committed': '확정',
  'enum.recipeStatus.archived': '보관',
  // sample.status
  'enum.sampleStatus.in_process': '진행 중',
  'enum.sampleStatus.completed': '사용 완료',
  'enum.sampleStatus.discarded': '폐기',
  'enum.sampleStatus.awaiting_decision': '판정 대기',
  // control_owner — 「오케스트레이터」라고 쓰지 않는다 (§15)
  'enum.controlOwner.orchestrator': '시스템',
  'enum.controlOwner.local': '단독',
  /*
   * interlock.state — 「닫힘」이 정상이다. 🔴 **미발행(`null`)은 「닫힘」이 아니라 모름**이라
   * 여기 키가 없고 화면이 `—` 로 떨어뜨린다. 모르는 것을 정상이라고 말하지 않는다.
   */
  'enum.interlock.closed': '닫힘',
  'enum.interlock.door': '도어 열림',
  'enum.interlock.light_curtain': '라이트커튼 차단',
  // labware layout_detail
  'enum.layoutDetail.simple': '간단',
  'enum.layoutDetail.full': '상세',
  // labware type
  'enum.labwareType.hypicc_chamber': '챔버 트레이',
  'enum.labwareType.reagent_reservoir': '시약 리저버',
  'enum.labwareType.tip_rack': '팁 랙',
  // 비전 판정
  'enum.verdict.pass': '통과',
  'enum.verdict.fail': '실패',
  'enum.verdict.inconclusive': '판정 불능',
  // 알람 severity (error_codes.yaml)
  'enum.severity.CRITICAL': '긴급',
  'enum.severity.ERROR': '오류',
  'enum.severity.WARN': '경고',
  'enum.severity.INFO': '정보',
  'enum.severity.DEBUG': '디버그',
  /*
   * 알람 분류 (`Alarm.category`) — 「누가 어떻게 조치하나」 축. 🔴 `severity`(얼마나 나쁜가)와 **다른 축**이다.
   *
   * 🔴 **되살렸다**(2026-09-26). 2026-09-20 에는 「열조차 없다」로 걷었는데 그 막힘이 풀렸다 —
   *    `ctcells-core db/migrations/0014_alarm_active_category.sql`(열 신설) ·
   *    `app/services/alarms.py:620`(`raise_alarm` 이 적재 시점에 `catalog_category(error_code)` 로 채움) ·
   *    `app/views/alarms.py:97`(`alarm_dto` 가 싣는다 · REST·구독 같은 함수). 실물 확인 2026-09-26:
   *    새로 세운 `COMM_LOST_PERMANENT` 알람이 `"category": "unrecoverable"` 로 왔다.
   * ⚠ 어휘는 `error_codes.yaml` `category_policy` 5키다(서버 `known_categories()` 가 카탈로그에서 센다).
   *   낱말은 `07_화면명세` §4.5 의 3영역(조치 필요 · 자동복구 중 · CRITICAL)과 `on_exhaust` 정책에서 왔다.
   *   ⚠ `critical` 을 「긴급」으로 쓰지 않는다 — 그건 `enum.severity.CRITICAL` 이고 축이 다르다
   *   (카탈로그에 `WARN` + `category: critical` 조합이 있다).
   * ⚠ 옛 적재분은 키가 **안 온다**(소급 불가 · `0016` 주석). 부재는 「모른다」다 — 분류로 접지 않는다.
   */
  'enum.alarmCategory.critical': '즉시 정지', // on_exhaust: safe_stop
  'enum.alarmCategory.unrecoverable': '서비스 호출', // terminate_service · 07 「HW 고장·서비스 호출」
  'enum.alarmCategory.user_recoverable': '조치 필요', // pause_await_user · 07 「조치 필요」
  'enum.alarmCategory.recoverable': '자동 복구', // retry 1회 → alarm_and_stop
  'enum.alarmCategory.transient': '일시 장애', // retry 3회(지수) → alarm_and_stop
  // 안전 상태 (SafetyState)
  'enum.safetyState.normal': '정상',
  'enum.safetyState.degraded': '주의',
  'enum.safetyState.emergency_stop': '안전정지',
  'enum.safetyState.safe_shutdown': '서비스 필요',
  'enum.safetyState.unknown': '확인 불가',
  // 복구 대기 까닭 (Batch.review_reason)
  'enum.reviewReason.recovery_decision': '복구 판정 대기',
  'enum.reviewReason.completion_unobserved': '완료 미관측',
  'enum.reviewReason.handoff_unconfirmed': '핸드오프 확인 대기',
  'enum.reviewReason.sample_disposition': '시료 처분 대기',
  /*
   * 분주 기록이 **0 건일 때** 그 빈 칸의 성질 (`SampleReport.dispenses_basis`).
   * 🔴 「없음」과 「못 봤다」를 같은 말로 적지 않는다 — 보고서에서 「없었다」는 단언이다.
   * ⚠ `observed` 는 행이 하나 이상일 때의 값이라 이 자리에 거의 오지 않지만, 0 건인데
   *   그 값이 오면 **모순을 감추지 말고 그대로 적는다**(사람이 서버를 의심해야 한다).
   */
  'enum.dispensesBasis.observed': '기록 있음 (건수 0 — 확인 필요)',
  'enum.dispensesBasis.none': '없음 (이 레시피에 분주 단계가 없습니다)',
  'enum.dispensesBasis.unobserved': '기록을 받지 못했습니다 (분주가 없었다는 뜻이 아닙니다)',
  'enum.dispensesBasis.unknown': '판정 불가 (레시피 본문을 읽지 못했습니다)',

  // ── 역할 · 권한 ──────────────────────────────────────────────────────────
  // 역할 이름은 화면에도 원값으로 나온다(와이어프레임 `ROLES[].label`). 설명만 한글이다.
  'role.admin': 'admin',
  'role.service': 'service',
  'role.operator': 'operator',
  'role.viewer': 'viewer',
  'role.admin.who': '고객 관리자 · CTCells 연구원',
  'role.service.who': '벤더 서비스 엔지니어',
  'role.operator.who': '고객 작업자',
  'role.viewer.who': '고객사 모니터링 인원 · 의료진',
  'perm.view': 'view',
  'perm.monitor': 'monitor',
  'perm.control': 'control',
  'perm.settings': 'settings',
  'perm.maintenance': 'maintenance',

  // ── 운영 그룹(모니터링 · 작업 · 이력 · 알람) + 개발 › 제어 ─────────────────
  // 화면 5종이 쓰는 문구. 출처는 와이어프레임 실물이고 대응 행 번호는 각 화면 파일 주석에 있다.

  // 버튼
  'action.ack': '확인',
  /** 대화의 확인 버튼 — 고른 값이 대화 안에 이미 보일 때(복구 판정). 알람 확인(`action.ack`)과 뜻이 다르다 */
  'action.confirm': '확인',
  'action.clearAlarm': '해제',
  /** 알람 「모두 해제」(eric 2026-10-30) — 지금 거른 목록의 해제되지 않은 알람 전부 */
  'action.clearAllAlarms': '모두 해제',
  'alarm.bulkClear.title': '알람 모두 해제',
  'alarm.bulkClear.confirm': '알람 {count}건을 해제합니다.',
  /** 조건이 유지 중인 latching 알람은 건별 [해제]처럼 일괄에서도 빠진다(`bulkClearTargets`) */
  'alarm.bulkClear.held':
    '조건이 유지 중인 알람 {count}건은 해제하지 않습니다. 조건이 해소된 뒤 해제할 수 있습니다.',
  'alarm.bulkClear.sending': '해제하는 중',
  'alarm.bulkClear.resultCleared': '{count}건 해제',
  'alarm.bulkClear.resultFailed': '{count}건 실패',
  'alarm.bulkClear.failuresLabel': '실패한 알람',
  'alarm.bulkClear.failureItem': '{reason} — {count}건',
  'alarm.bulkClear.failuresRemain': '실패한 알람은 목록에 그대로 남습니다.',
  /** 내보내기 작업이 제한 시간 안에 끝나지 않았다. 🔴 「실패」와 갈린다 — 서버는 아직 돌고 있을 수 있다 */
  'error.exportTimeout': '내보내기가 제한 시간 안에 끝나지 않았습니다',
  /** 서버가 `status=failed` 를 줬는데 `error` 까지는 안 준 자리. 있으면 그 문장이 이긴다 */
  'error.exportFailed': '내보내기를 만들지 못했습니다',
  'action.open': '열기',
  'action.download': '내려받기',
  'action.discard': '폐기',
  'action.confirmCompleted': '완료 확정',
  'action.remove': '제거',
  'action.pick': '담기',
  // 작업 추가 창 시료 구역 오른쪽 위 단추 둘(eric 2026-10-21 「+ 시료 추가 이렇게 해줘.. 왼쪽 꺼는 + 기존 시료 추가 이렇게 하고」 · 이 차례).
  // ＋ 는 전각이다 — `action.addSample` 과 같은 글자이고, 시험이 글자를 정규식에 넣어도 특수 문자가 아니다.
  // eric 2026-10-21 「＋ 기존 시료 추가 · ＋ 새 시료 추가 이렇게 두개」
  'action.pickExisting': '＋ 기존 시료 추가',
  'action.newSample': '＋ 새 시료 추가',
  'action.addAndPick': '추가하고 담기',
  'action.selectRecipe': '레시피 선택',
  'action.createBatch': '작업 생성',
  'action.openBatch': '작업 상세 열기',

  /*
   * 알람 상태 — 확인 여부와 조건 해소는 직교하는 두 축이다(SM-ALM-05).
   *
   * 🔴 **`state` 축은 «세» 값이다**(`active · acknowledged · cleared`). 2026-09-20 실측:
   *    표의 상태 칸이 `state === 'active' ? 미확인 : 확인됨` 이진 분기라 **`cleared` 3건이
   *    「확인됨」으로 떴다** — 같은 화면의 탭 뱃지(해제됨 3)와 정면으로 어긋났다.
   *    ⇒ `alarm.cleared` 를 세웠다. **세 값 중 하나라도 빠뜨리면 같은 병이 다시 난다.**
   */
  'alarm.acknowledged': '확인됨',
  'alarm.unacknowledged': '미확인',
  'alarm.cleared': '해제됨',
  /** 「조건」 칸 값(eric 2026-10-30 「상태 컬럼에 두 개가 같이 있는데 이거 분리해줘」) — 열 머리가 「조건」이라 낱말만 */
  'alarm.condition.resolved': '해소',
  'alarm.condition.held': '유지',
  /*
   * 알람 상세 서랍(eric 2026-09-28 「알람에서도 구조를 오른쪽으로.. 상세 페이지 보이게」).
   * 🔴 줄 이름은 `Alarm` 응답 필드에서 왔다. 제목은 알람 ID, 내용 문구는 「요약」 카드 첫 줄이다(eric 2026-09-28).
   *    목록에서 숨을 수 있는 열(분류·모듈·발생)은 서랍 「요약」 카드가 적는다 — 목록 열 이름을 그대로 쓴다.
   *    `GET /alarms/{id}` 는 서버에 없다(2026-09-28 openapi.json 실측) — 서랍은 목록 응답에서 고른다.
   */
  'screen.alarmDetail.title': '알람 상세',
  'card.alarmOccurrence': '발생',
  'card.alarmHandling': '처리',
  /** 알람 상세 원격 발송 칸(`Alarm.dispatch` · v0.9.4) */
  'card.alarmDispatch': '발송',
  'row.errorCode': '코드',
  'row.serverMessage': '서버 문구',
  'row.groupingKey': '묶음 키',
  'row.eventId': '이벤트 ID',
  'row.branch': '갈래',
  'row.deviceError': '장비 오류',
  'row.ackRequired': '확인 필요',
  'row.autoClear': '자동 소멸',
  'row.ackBy': '확인자',
  'row.ackAt': '확인 시각',
  'row.clearedAt': '해제 시각',
  'state.empty.alarmNotFound': '목록에 없는 알람입니다',
  /*
   * ⚠ `alarm.noAckNeeded`('확인 불필요')를 걷었다(2026-09-20). 그 문구는 [확인] 버튼 자리를
   *   **갈아치우는** 데 쓰였는데, 이제 버튼을 그대로 두고 **비활성 + 사유**로 말한다
   *   (사유는 `reason.ackNotRequired`). 사유가 버튼의 접근 이름에 실려야 스크린리더가 읽는다.
   */

  // 새 작업
  // 🔴 분모를 쓰지 않는다 — 서버가 상한을 «재지 않는다»(`sample_ids` 는 minItems 1 뿐).
  //    「/ 9」를 쓰면 화면이 없는 규칙을 집행하는 것처럼 보인다. 9 칸은 트레이의 물리 사실이라
  //    `batchNew.capacityHint` 가 **안내로만** 말한다. 차단은 서버 몫이다.
  'batchNew.quota': '{picked} 건',
  /* 🔴 숫자를 박지 않는다 — 시료 자리 수는 고른 레시피의 웰 수가 정한다(eric 2026-10-23 「시료를 9개를 선택하도록 해」 · 웰마다 하나) */
  'batchNew.capacityHint': '레시피를 고르면 시료 자리 수가 정해집니다 — 웰마다 시료 하나',
  // 작업 추가 창 시료 구역의 트레이 그림 — 시료 자리는 고른 레시피 문서가 정한다(`POST /recipes/summary` · 2026-10-21)
  'batchNew.tray.pickRecipe': '레시피를 고르면 시료 자리가 보입니다',
  // 레시피 먼저 → 시료(2026-10-21 eric 「레시피를 먼저 고르게 하고.. 시료는 레시피 선택 후에 선택 가능하게」)
  'reason.pickRecipeFirst': '레시피를 먼저 고르세요',
  'batchNew.quotaOf': '{picked} / {slots}',
  'batchNew.newSample.title': '새 시료',
  // 작업 추가 창 — 트레이 웰마다 시료를 고른다(eric 2026-10-27 「해당 칸 또는 웰 별로 하는 건 어때?」 · 「응 한번 해봐」).
  // 웰 단추의 접근 이름 · 웰 곁 선택 창 · 다른 웰에 든 시료의 비활성 사유(옮기지 않는다 — 실수 방지 · 팀장 결정)
  // 🔴 트레이 칸 이름(P1 · REF)은 웰이 아니라 «묶음»(`sample_ref`)이다 — 한 묶음에 웰이 둘이라 P1 이 두 번 나온다. 제목은 웰 하나, 묶음은 보조
  'batchNew.well.vacant': '{well} 웰 · 묶음 {ref} · 비어 있음',
  'batchNew.well.filled': '{well} 웰 · 묶음 {ref} · {sample}',
  // 선택 창 제목 — eric 표기 그대로 한 줄(「A2 (P2)」 · 2026-10-29 「A2 (P2) 이렇게 해줘」 — 10-27 의 「A1웰(P1)」에서 「웰」을 뺐다)
  'batchNew.well.title': '{well} ({ref})',
  'batchNew.well.picker': '{well} ({ref}) 시료 선택',
  'batchNew.well.current': '이 웰',
  'batchNew.well.free': '고를 수 있는 시료',
  'batchNew.well.elsewhere': '다른 웰에 배정됨',
  'batchNew.well.assigned': '기존 작업에 배정되어 선택할 수 없는 시료',
  'batchNew.well.assignedReason':
    '기존 작업에 배정되어 있습니다. 중단된 작업이면 기존 작업의 시료 처분에서 다시 돌림 가능 여부를 확인하세요',
  'batchNew.well.processedReason':
    '이미 처리가 진행되어 새 작업에 재사용할 수 없습니다. 기존 작업에서 시료 처분을 확인하세요',
  'batchNew.well.inspectAssigned': '기존 작업 확인',
  'batchNew.well.taken': '{well} 웰에 배정됨',
  'batchNew.well.clear': '빼기',
  // 트레이 오른쪽 배정 표(eric 2026-10-27 「오른쪽에는 9개 시료 칸 넣어 원래처럼」) — 열 머리 「묶음」
  'batchNew.slot.group': '묶음',
  // 배정 표 줄의 시료 칸 단추 — 트레이 웰 단추와 이름이 겹치지 않게 «고르기»로 부른다(값은 같은 줄의 칸이 읽힌다)
  'batchNew.slot.pick': '{well} 웰 시료 고르기',
  /*
   * 한 묶음(P1 · 웰 둘)은 한 환자다(eric 2026-10-29 「P1 두 개의 환자 번호가 같아야 하잖아」) — 레시피 저장 검증이 묶음을 «환자 하나»로 보고
   * 팁을 묶음 안에서만 같이 쓴다(core `recipe_v2/validate.py` 교차오염 검사). 배치 생성 서버는 이 대조를 안 한다 — 화면이 막는다
   */
  'batchNew.slot.patientMismatch': '환자 번호가 다릅니다',
  // 조사가 묶음 이름(P1 = 피원)에 안 붙게 괄호로 둔다(팀장 2026-10-29)
  'batchNew.well.samePatient': '한 환자 묶음({ref}) — 환자 번호 {patient} 의 시료만 보입니다',
  'batchNew.well.samePatientEmpty': '같은 환자의 시료가 없습니다',
  'reason.groupPatientMismatch': '{refs} 묶음에 환자 번호가 다른 시료가 들어 있습니다',
  // 고른 레시피가 시료 자리를 알려 주지 않는다(옛 형식 · 요약 없음) — 웰이 없어 고를 수 없다
  'batchNew.tray.noSlots': '이 레시피는 시료 자리를 알려 주지 않아 웰을 고를 수 없습니다',
  // 작업 추가 창 레시피 표 — 줄 끝 [상세] 팝업(2026-10-21 eric 「각 행에서.. 상세 버튼을 만들고.. 팝업 창이 뜨면서」 · 팝업은 상세만 · 10-22)
  'batchNew.recipe.detail': '상세',
  'batchNew.recipe.detailOf': '{name} 상세',

  // 새 작업 — 작업 이름(선택 · `docs/backend-requests.csv` BR-94 · 2026-09-26)
  // 🔴 지금 서버는 이 칸을 버리고 조립 이름을 저장한다 — `ignored` 가 그 사실과 실제 이름을 말한다
  // 칸 이름 「이름」 · 안내 줄은 걷었다(eric 2026-10-21 「비우면 레시피 · 수행인 · 시각으로 자동 이 문구 빼주고. 작업 이름 --> 이름」)
  'batchNew.name.label': '이름',
  /** BR-94 — 서버가 `CreateBatchBody.name` 을 받기 전까지 이름 칸의 비활성 사유(`newBatchName.ts` `BATCH_NAME_ACCEPTED`) */
  'reason.batchNameUnsupported': '서버가 아직 작업 이름을 받지 않습니다 — 자동 이름으로 저장',
  'batchNew.name.ignored': '서버가 아직 이름을 받지 않아 자동 이름 “{saved}” 으로 저장됐습니다',
  'reason.batchNameTooLong': '작업 이름은 {max}자까지입니다',

  // 열 제목 추가분
  'column.drawDate': '채혈 날짜',
  'column.cancerType': '암종',
  'column.cancerStage': '암 병기',

  // 알람 화면 상단 KPI
  'kpi.waste': '폐기통',
  'kpi.tip': '팁',
  'kpi.reagentHeadroom': '시약 여유',
  'kpi.reagentStorage': '시약 보관',
  'kpi.threshold': '임계 {value}',
  'kpi.capacity': '/ {value}',
  'kpi.required': '필요 {value}',
  'kpi.setpoint': '설정 {value}',

  // 사이드 패널 「현재 메트릭」 항목. 값은 ModuleSnapshot.telemetry 에서 온다
  'metric.speed': '속도',
  'metric.rcf': 'RCF',
  'metric.temperature': '온도',
  'metric.axisX': 'X',
  'metric.axisY': 'Y',
  'metric.axisZ': 'Z',
  'metric.joint1': 'J1',
  'metric.joint2': 'J2',
  'metric.joint3': 'J3',
  'metric.joint4': 'J4',
  'metric.joint5': 'J5',
  'metric.joint6': 'J6',
  'metric.uv': 'UV',
  'metric.stage': '스테이지',
  'metric.tcRack': 'TC 랙',
  'metric.tip': '팁',
  /* 영상 아래 끝장치 상태(eric 2026-10-08 「3축 로봇의 x, y, z 와 기타의 값」) — 그리퍼 파지 · 피펫 팁 장착 */
  'metric.gripper': '그리퍼',
  'metric.tipMounted': '피펫 팁',
  'metric.tipMounted.on': '장착',
  'metric.tipMounted.off': '없음',
  'metric.reagentStorage': '시약 보관',
  'metric.shaker': '셰이커',
  'metric.slider': '슬라이더',
  'metric.verdict': '판정',

  // 모니터링 — 트레이 이름은 모듈마다 다르다(§14-3). 염색기는 같은 트레이가 자리만 옮긴다(§16-2)
  /**
   * 🔴 **아래 다섯은 «위치 이름»이지 표시 라벨이 아니다.** `whereOf()` 가 서버가 준
   *    `ChamberRow.position` 문자열과 **그대로 대조**한다(계약에 구조화된 위치 축이 없다).
   *    글자를 바꾸면 챔버가 전부 「작업 전」으로 떨어진다 — 표시가 아니라 **프로토콜 값**이다.
   *    화면에 보일 이름이 필요하면 `monitoring.tray.one` 처럼 따로 둔다.
   */
  'monitoring.tray.C': 'C 트레이',
  'monitoring.tray.A': 'A 트레이',
  'monitoring.tray.L': 'L 트레이',
  'monitoring.tray.Lrear': 'L 트레이 · 후면',
  'monitoring.tray.Lfront': 'L 트레이 · 전면',
  /**
   * 「전체」 보기의 트레이 한 장 — 모듈마다 한 장뿐이라 앞 글자로 구분할 이유가 없다
   * (eric 지시 2026-09-18 「C 트레이를 그냥 트레이라고 해줘」). 세 모듈이 같이 쓴다.
   */
  'monitoring.tray.one': '트레이',
  /** 트레이 이름표 꼬리 — 서버 스텝 기록의 비전 검사(정본 `SQ-CYCLE-09` · 통과 = 끝남 · 실패 = 에러 코드 · 검사 중) */
  'monitoring.tray.visionPassed': '비전 ✓',
  'monitoring.tray.visionFailed': '비전 ✗',
  'monitoring.tray.visionRunning': '비전 검사 중',
  /**
   * 블록 이름표에 **작업 전/후를 덧붙인 꼴** — 「C 트레이 · 작업 전」.
   *
   * 🔴 **위 다섯 위치 이름을 고쳐서 만들지 마라.** 그것들은 서버 `position` 과 대조하는
   *    «프로토콜 값»이다(위 머리말). 여기서 «표시»만 합친다 — 대조하는 값은 그대로다.
   * 🔴 두 장이 나란히 설 때만 붙인다. 어느 쪽이 작업 전인지 말해 주던 「전체」 보기 범례가
   *    콘솔에서 사라져(§4-1) 그 사실을 말할 자리가 이름표밖에 없다(eric 지시 2026-09-21).
   */
  'monitoring.tray.stage': '{tray} · {stage}',
  'monitoring.legend.before': '작업 전',
  'monitoring.legend.after': '작업 후',
  /*
   * 장비별 보기 단계 띠(시안 `.steps`) — 「01 작업 전 C 트레이 | 02 처리 중 원심분리기 | 03 작업 후 A 트레이」.
   * 트레이 이름은 `monitoring.tray.*`, 장비 이름은 `moduleLabel()` 에서 온다. 염색기는 「트레이 | 처리 중 | 교반 셰이커」다.
   */
  'monitoring.eqsheet.steps': '작업 흐름',
  'monitoring.eqsheet.layout': '{name} 배치',
  'monitoring.eqsheet.processing': '처리 중',
  'monitoring.eqsheet.agitate': '교반',
  /*
   * 장비 판 어셈블러 장치 칸 — 압착(TC 장착)과 UV 노출을 정면도 한 장으로(eric 2026-10-07 「압착 부분이랑 UV 노출 부분.. 두개.. 합쳐달라고
   * 했잖아.. 위에서 바라 보는게 아니라 정면에서.. 압착(TC 장착 그림만 남기라고)」). 종전 두 칸(「압착 (TC 장착)」 · 「UV 노출」 · 2026-10-06)은 걷었다.
   */
  'monitoring.eqsheet.station': '압착 (TC 장착) · UV 노출',
  'monitoring.eqsheet.process': '{name} 공정 진행',
  /*
   * 원심분리기 영상 아래 메트릭 줄(`FigureMetrics` · eric 2026-10-06 「영상 아래에다가」). 접근 이름 · 칸이 없는 값의 사유.
   * 🔴 사유는 «무엇이» 안 오는지 적는다 — 원심분리기 telemetry 에 속도 · RCF · 온도 칸이 없다(dev2 실측 · BR-115).
   */
  'monitoring.eqsheet.metrics': '{name} 메트릭',
  /* 장비 판 메트릭 상자의 「3축 로봇」 묶음(eric 2026-10-12) — 머리. 끝장치 줄(「엔드 이펙터: …」)은 걷었다(eric 2026-10-21) */
  'monitoring.eqsheet.gantry': '3축 로봇',
  /* 원심분리기 판 위 3축 그리퍼(RGD-35-30) 표지 — 장식이라 마우스 표지에만(eric 2026-10-08) */
  'monitoring.centGripper.title': '원심분리기 3축 그리퍼',
  'monitoring.asmGripper.title': '어셈블러 3축 그리퍼',
  /* 염색기 판 위 3축 피펫(ZEUS LT) — 원심 · 어셈블러 그리퍼와 같은 겹침 도구 · 끝장치만 피펫(eric 2026-10-13) */
  'monitoring.stainPipette.title': '염색기 3축 피펫',
  /* 「처리 중」 장치 그림 아래 한 줄 — 이벤트 로그 「내용」 열의 이 모듈 최근 스텝(eric 2026-10-08). 접근 이름 */
  'monitoring.eqsheet.latestStep': '{name} 최근 스텝',
  /*
   * 6축 로봇 상세(`RobotDetailTiles` — 장비 칩 「6축 로봇」을 고른 왼쪽 상세 · eric 2026-10-06). 오른쪽 카드는 팔 그림 한 장이다.
   * 🔴 「모름」 문구는 «무엇을 모르는지»를 적는다 — 위치 · 이동 구간 · 집은 트레이는 응답에 아직 없다(BR-114).
   * ⚠ 이송 띠(`RobotTransfer`)를 걷으며 그 띠만 쓰던 키(띠 이름 · 레일 · 파킹 · 띠 끊김 문구)도 걷었다.
   */
  'monitoring.robot.gripper': '그리퍼',
  'monitoring.robot.grips.none': '그리퍼 없음',
  'monitoring.robot.grips.unknown': '파지 미발행',
  'monitoring.robot.run': '실행',
  'monitoring.robot.run.none': '실행 없음',
  'monitoring.robot.run.active': '실행 중',
  'monitoring.robot.missing': '모듈 응답 없음',
  'monitoring.robot.pose.none': '자세 미발행 — 기준 자세 아이콘',
  /** 관절값 없이 서버 동작 이름으로 그린 자세(eric 2026-10-07 「움직임을 줘」) — `{action}` 은 `enum.robotSequence.*` 또는 모듈 상태 */
  'monitoring.robot.pose.schematic': '도식 자세 — {action}',
  /** 레일 마커 자리 — 서버가 로봇 위치(`rail_stop`)를 안 주면 마커 대신 이 글자(파킹으로 접지 않는다) */
  'monitoring.robot.where.none': '위치 미발행',
  'monitoring.robot.motion': '동작',
  /* 운송 로봇 카드 이름 — 동작 칸 + 제목 오른쪽 상태 칩(eric 2026-10-12) · 🔴 「6축 로봇」 → 「운송 로봇」(eric 2026-10-13 판 안 이름 통일) */
  'monitoring.robot.card': '운송 로봇',
  'monitoring.robot.state': '상태',
  'monitoring.robot.interlock': '인터록',
  'monitoring.robot.recovery': '복구 시도',
  /*
   * 운송 로봇 그림(`RobotLine`) 정거장 발치의 «자리를 모르는 까닭» — 미발행 · 발행한 자리 0개(모듈 없음은 `monitoring.robot.missing`).
   * 「핸드오프 자리」 표(제목 · 「장비」 열 · 「있음/없음」 칸)는 걷었다 — 그림이 말한다(eric 2026-10-21)
   */
  'monitoring.robot.spots.unpublished': '자리 점유 미발행',
  'monitoring.robot.spots.none': '발행한 자리 없음',
  /**
   * 6축 «위치» 구역(v0.9.4 `ModuleSnapshot.pose_telemetry` · 2026-10-11) — 칸 이름은 응답 키의 이름(`<robot_id>.<이름>` 의 뒤)으로 고른다.
   * 상하축 · 좌우축은 `specs/vocab/positions.yaml` 부가축 어휘(`lift` · `traverse`). 모르는 이름은 원문 그대로 선다.
   * ⚠ 「받은 위치 없음」류 사유 글자는 두지 않는다 — 값이 없으면 구역째 없다(eric 2026-10-08 「위치 미발행 텍스트 이런 거 빼」)
   */
  /* 「TCP」 접두는 걷었다(eric 2026-10-19 「운송 로봇에서 TCP 이 텍스트는 제거해줘」) — 축 이름만 */
  'monitoring.robot.position.tcp_x_mm': 'X',
  'monitoring.robot.position.tcp_y_mm': 'Y',
  'monitoring.robot.position.tcp_z_mm': 'Z',
  'monitoring.robot.position.tcp_rx_rad': 'RX',
  'monitoring.robot.position.tcp_ry_rad': 'RY',
  'monitoring.robot.position.tcp_rz_rad': 'RZ',
  'monitoring.robot.position.lift_mm': '상하축',
  'monitoring.robot.position.traverse_mm': '좌우축',
  /** 관절 `<robot_id>.joint_<n>_rad` — 관절을 읽는 드라이버가 있을 때만 온다 */
  'monitoring.robot.position.joint': '관절 {n}',
  /** 6축 가로 라인(eric 2026-10-08) — 파킹 표지 · 상태 한 줄 「어셈블러 — 그리퍼 잡기 · 3/5」 · 쥔 트레이 · 영상 칸의 빈 사유 */
  'monitoring.robotLine.park': 'P',
  'monitoring.robotLine.status': '{station} — {action}',
  'monitoring.robotLine.carrying': '트레이 이송 중',
  'monitoring.robotLine.carryingTray': '{tray} 이송 중',
  /** 정거장 문 두 짝(eric 2026-10-08 「앞문과 뒷문을 만들어줘」) — 접근 이름(title) */
  'monitoring.robotLine.door.front': '앞문',
  'monitoring.robotLine.door.rear': '뒷문',
  /** 그리퍼 파지 «기억»(센서 아님 · module_service `/grips/forget`) — 계약 enum 5값 */
  'enum.gripState.moving': '움직이는 중',
  /*
   * 🔴 현장 장비 용어로 짧게 — 「파지 · 해제」(eric 2026-10-11 「빈손, 비어있음.. 표현이 촌스럽잖아」). 로봇 동작 사전
   *    「그리퍼 잡기 · 놓기」(`enum.robotSequence.grip` · `.release`)와 짝이다. 원심 · 어셈블러 · 염색기 메트릭 타일과 6축 줄이 이 한 키를 같이 쓴다.
   */
  'enum.gripState.at_target': '해제',
  'enum.gripState.holding': '파지',
  'enum.gripState.dropped': '떨어뜨림',
  'enum.gripState.unknown': '판정 불가',
  /** 6축 시퀀스 — module-core 시퀀스 스키마 `x-title-ko` 를 줄인 것. 모르는 이름은 원값 그대로(`enumLabel`) */
  'enum.robotSequence.move_to_module': '모듈로 이동',
  'enum.robotSequence.move_aux_axis': '주행축 이동',
  'enum.robotSequence.move_to_pose': '좌표로 이동',
  'enum.robotSequence.grip': '그리퍼 잡기',
  'enum.robotSequence.release': '그리퍼 놓기',
  'enum.robotSequence.gripper_set': '그리퍼 개폐',
  'enum.robotSequence.home': '파킹 복귀',
  /** L2 수동 이송 갈래(`manual_transfer.place_held` · IFC-RO-56) — 로봇이 이미 쥔 트레이를 목적 자리에 놓는다 */
  'enum.robotSequence.place_held': '쥔 트레이 내려놓기',
  /**
   * 핸드오프 자리 꼬리(모듈 접두를 뗀 것) — 정본 `vocab/positions.yaml` 의 자리 설명.
   * ⚠ 서버 원값은 `tray-1` 인데 사전 키 끝 마디가 `-` 를 못 받아 `_` 로 적는다 — 바꿔 읽는 곳은 `handoffPointLabel()` 하나다
   */
  'monitoring.robot.point.slide_rear': '슬라이드 후면',
  'monitoring.robot.point.slide_front': '슬라이드 전면',
  /** 6축 라인의 트레이 칸 이름 — 슬라이드(전면 · 후면을 한 칸으로) */
  'monitoring.robot.point.slide': '슬라이드',
  'monitoring.robot.point.tray_1': '트레이 거치대',
  /** v0.9.4 자리 이름(`vocab/positions.yaml` — 6축이 거치대 · 슬라이드 위 트레이를 집는 점) */
  'monitoring.robot.point.grip_tray_on_holder': '거치대 위',
  'monitoring.robot.point.grip_tray_on_slide': '슬라이드 위',
  'monitoring.legend.checked': '위치 · 비전 확인 완료',
  /** 영상 재생 중 표지 — 「LIVE」→「연결」(eric 2026-10-02) */
  'monitoring.live': '연결',
  'monitoring.reference': '대조',
  'monitoring.attributionInconclusive': '판정 불가',
  'monitoring.stop.rear': '후면',
  'monitoring.stop.pipetting': '피펫팅',
  'monitoring.stop.front': '전면',
  'monitoring.bay.ambient': '상온 5',
  'monitoring.bay.chilled': '냉장 3 · 4 ℃',
  'monitoring.tipRack1': '팁 랙 1',
  'monitoring.tipRack2': '팁 랙 2',
  /**
   * 장비 판 그림의 랙 이름표(eric 2026-10-19 「장비 쪽에.. TC rack · 염색기 쪽에 Tip rack1, Tip rack2, Tube rack 이렇게」) — 소모품 판 카드 이름(`tip rack 1` …)과
   * 같은 영문 말 · 장비 그림만. 위 한글 키(`monitoring.tipRack1` …)는 장비 관점 앱 · 패드 그림이 그대로 쓴다
   */
  /* 2026-10-20 「장비쪽도 수정.. 다 수정」 — Rack 의 R 대문자 · 숫자 붙여 씀(Tube Rack1 · Cold Rack 과 같은 꼴) */
  'monitoring.eqRack.tc': 'TC Rack',
  'monitoring.eqRack.tip1': 'Tip Rack1',
  'monitoring.eqRack.tip2': 'Tip Rack2',
  /* 2026-10-20 「Tube Rack1, Tube Rack2 이렇게.. Cold Rack 이렇게.. 장비쪽도 마찬가지로」 — R 대문자 */
  'monitoring.eqRack.tube': 'Tube Rack',
  'monitoring.waste': '폐기통',
  'monitoring.uvOn': 'UV on',
  'monitoring.uvOff': 'UV off',
  /** 셰이커 그림 아래 한 줄 — `{state}` 는 `enum.shakerLatch.*` 6상태 라벨 그대로 */
  'monitoring.shakerLatch': '래치 {state}',
  /** 셰이커 그림 위 후문(자동 · 6축 쪽) 띠의 글자(eric 2026-10-07 「시뮬레이터를 보고 추가할 것」 중 3) — 서버 `interlock.doors[].rear.state` */
  'monitoring.shaker.door': '후문 {state}',
  'monitoring.shaker.door.open': '열림',
  'monitoring.shaker.door.closed': '닫힘',
  'monitoring.shaker.door.unknown': '미발행',
  'figTab.pip3': '피펫 3축',
  /**
   * 염색기 정면도(eric 2026-10-07 「염색기를 정면도로 수정해줘」) — 장비 판 블록 이름(단계 띠가 「염색기」를 말해 눈에서는 숨는다) · 슬라이드 자리 글자.
   * `{stop}` 은 `monitoring.stop.*` 그대로
   */
  'monitoring.eqsheet.stainFront': '피펫 · 셰이커',
  'monitoring.stainFront.stop': '자리 {stop}',
  /** 트레이 오른쪽 통 둘(eric 2026-10-08 「시약 통, 처리 통 두 개를 간단한 사양형으로」 → 「시약 랙 폐기통 이렇게」) — 자리 표시다 · 수량은 소모품 판 */
  /* 「튜브 랙」 — 종전 「시약 랙」(eric 2026-10-08 「시약 랙은 튜브 랙으로 이름 바꿔줘」) */
  'monitoring.stainFront.bin.reagent': '튜브 랙',
  'monitoring.stainFront.bin.waste': '폐기통',
  /**
   * 염색기 동작 이름 — module-core `profiles/staining.yaml` `supported_sequences` 16개(2026-10-07 확인) 전수.
   * 어휘는 정본(13 추적성 `EQ-LH-DSP-07` 「흡입·토출」 · 12 레시피 `RCP-LC-01`)을 따른다 — aspirate 흡입 · expel 토출.
   * 🔴 분주만 「시약 넣는 중」 — 모듈 스텝은 시약 «자리»(`reagent_well`)만 알고 이름을 모른다(계약 「시약 정체성은 channel 이 아니라 reagent_well 이 가리킨다」).
   *    eric 2026-10-08 「분주할 때 정확한 명을 모르면 시약 넣는다고 알려줘」 — 이름을 지어 붙이지 않는다
   */
  'enum.stainSequence.dispense_reagent': '시약 넣는 중',
  'enum.stainSequence.aspirate': '흡입',
  'enum.stainSequence.draw': '시약 흡입',
  'enum.stainSequence.expel': '토출',
  'enum.stainSequence.mix': '혼합',
  'enum.stainSequence.shake': '교반',
  'enum.stainSequence.wait_with_temp_monitoring': '온도 유지 대기',
  'enum.stainSequence.move_to': '이동',
  'enum.stainSequence.move_z': 'Z 이동',
  'enum.stainSequence.mount_tip': '팁 장착',
  'enum.stainSequence.discard_tip': '팁 폐기',
  'enum.stainSequence.open_door': '후문 열기',
  'enum.stainSequence.close_door': '후문 닫기',
  'enum.stainSequence.slide_tray': '트레이 슬라이드',
  'enum.stainSequence.set_reagent_storage_temp': '시약 보관 온도 설정',
  'enum.stainSequence.inspect': '비전 검사',
  /*
   * 염색기 v0.9.4 에 늘어난 7개 — module-core `profiles/staining.yaml` `supported_sequences`(2026-10-11 · 23개) 전수와 맞춘다.
   * 이름은 시퀀스 스키마 `x-title-ko` 를 위 줄들 말투(짧게)로 줄였다.
   */
  'enum.stainSequence.home_axes': '3축 원점 복귀',
  'enum.stainSequence.move_xy_mm': 'XY 이동',
  'enum.stainSequence.detect_liquid': '액면 감지',
  'enum.stainSequence.shaker_latch': '셰이커 래치 닫기',
  'enum.stainSequence.shaker_unlatch': '셰이커 래치 열기',
  'enum.stainSequence.shaker_stop': '셰이커 정지',
  'enum.stainSequence.shaker_heater_off': '셰이커 가열 끄기',
  'enum.stainSequence.reagent_storage_off': '시약 냉장 끄기',
  /**
   * 모듈의 말(`{sequence} 시작|완료|: 까닭`)에 붙이는 **명사형** — 사전 이름이 진행형(「… 중」)인 항목만 둔다(`sequenceText` 가 있으면 이쪽을 쓴다).
   * 진행형은 정면도 「지금 하는 일」 줄용이라 뒤에 「완료」가 붙으면 「시약 넣는 중 완료」가 된다(2026-10-11 캡처).
   * 어휘는 eric 2026-10-08 「시약 넣는다고 알려줘」 그대로 — 시약 이름을 지어 붙이지 않는다
   */
  'enum.stainSequenceStep.dispense_reagent': '시약 넣기',
  /**
   * 원심분리기 동작 이름 — module-core `profiles/centrifuge.yaml` `supported_sequences` 14개(2026-10-11) 전수.
   * 이름은 시퀀스 스키마 `x-title-ko` 를 염색기 줄 말투로 줄였다. 장비 그림 아래 한 줄(`latestStepText`)이 모듈의 말 머리를 이것으로 바꾼다
   */
  'enum.centSequence.run_centrifugation': '원심 회전',
  'enum.centSequence.open_door': '도어 열기',
  'enum.centSequence.close_door': '도어 닫기',
  'enum.centSequence.grip': '그리퍼 잡기',
  'enum.centSequence.release': '그리퍼 놓기',
  'enum.centSequence.move_to': '이동',
  'enum.centSequence.move_z': 'Z 이동',
  'enum.centSequence.gripper_set': '그리퍼 개폐',
  'enum.centSequence.rotate': '회전',
  'enum.centSequence.wait_for_manual_load': '적재 대기',
  'enum.centSequence.inspect': '비전 검사',
  'enum.centSequence.slide_tray': '트레이 슬라이드',
  'enum.centSequence.home_axes': '3축 원점 복귀',
  'enum.centSequence.move_xy_mm': 'XY 이동',
  /** 어셈블러 동작 이름 — module-core `profiles/assembler.yaml` `supported_sequences` 13개(2026-10-11) 전수. 말투는 위와 같다 */
  'enum.asmSequence.expose_uv': 'UV 노출',
  'enum.asmSequence.grip': '그리퍼 잡기',
  'enum.asmSequence.release': '그리퍼 놓기',
  'enum.asmSequence.move_to': '이동',
  'enum.asmSequence.move_z': 'Z 이동',
  'enum.asmSequence.gripper_set': '그리퍼 개폐',
  'enum.asmSequence.rotate': '회전',
  'enum.asmSequence.open_door': '도어 열기',
  'enum.asmSequence.close_door': '도어 닫기',
  'enum.asmSequence.inspect': '비전 검사',
  'enum.asmSequence.slide_tray': '트레이 슬라이드',
  'enum.asmSequence.home_axes': '3축 원점 복귀',
  'enum.asmSequence.move_xy_mm': 'XY 이동',

  /*
   * 🔴 **「모른다」를 말하는 두 줄**(2026-09-20 · `docs/backend-requests.csv` BR-31).
   *    서버에 시료↔칸 대응이 «없어서» 트레이 격자와 로터 자리를 비울 때 **반드시 함께 뜬다.**
   *
   * ⚠ **「비어 있음」으로 바꾸지 마라.** 그것은 거짓이다 — 트레이에는 시료가 들어 있고
   *   우리가 «어느 칸인지»를 모르는 것이다. 종전에는 이 자리에 fixture 시료(`S-1045` 등)가
   *   그려져 모듈이 전부 `IDLE` 인 화면의 로터가 돌고 있는 것처럼 보였다.
   * ⚠ 문면을 「서버가 안 준다」처럼 **구현 사정으로 적지 않는다.** 사용자는 그 사실이 아니라
   *   「이 화면이 무엇을 말할 수 있고 없는가」를 알아야 한다.
   */
  'monitoring.noSlotAssignment': '칸 배정 정보 없음',
  'monitoring.noRotorLoad': '적재 정보 없음',
  /*
   * 🔴 「비전」 탭 «안»의 빈 상태 — 위 두 줄과 같은 결이다(eric 지시 2026-09-21 로 **탭은 항상**
   *    서고, 자료가 없을 때 비는 것은 탭 안이다).
   * ⚠ **「영상 없음」으로 줄이지 마라.** 그러면 「카메라가 아무것도 못 봤다」로 읽힌다 —
   *   사실은 **영상이 화면까지 오는 길이 아직 없다**(스트림 경로가 계약에 0건 · §10-2).
   *   못 본 것과 볼 길이 없는 것은 다른 사실이다.
   */
  'monitoring.noVisionFeed': '카메라 영상 연결 없음',
  /*
   * 🔴 **실영상 플레이어의 경계 상태 셋.** 위 「카메라 영상 연결 없음」은 **스트림이 설정되지
   *    않았을 때**의 문구이고(= 붙을 곳 자체가 없다), 아래 셋은 **붙으려는 중·붙었다·못 붙었다**를
   *    가른다. 넷을 한 문구로 합치지 마라 — 「설정이 없다」와 「연결이 안 된다」는 다른 사실이고
   *    고칠 사람도 다르다(앞은 설정, 뒤는 중계기·망).
   * ⚠ 실패를 **빈 검은 화면으로 두지 않는다.** 사유를 적고 스스로 다시 시도한다.
   */
  /** WebRTC 도 MSE 도 없는 웹뷰. 다시 시도해도 같아서 재시도하지 않는다 */
  'vision.unsupported': '이 화면에서는 영상을 재생할 수 없습니다 — WebRTC · MSE 모두 지원 없음',
  /*
   * 영상 칸 머리의 **연결 상태 배지**(디자인팀 시안 2026-10-01 §3 「이름은 왼쪽 위 제목 + 연결 상태 배지」).
   * 위 문장들(영상 칸 안)의 짧은 꼴이다 — 뜻은 같다. 재생 중은 `monitoring.live`(LIVE) 를 그대로 쓴다.
   */
  'vision.badge.connecting': '연결 중',
  'vision.badge.failed': '연결 실패',
  'vision.badge.unsupported': '재생 불가',
  'vision.badge.none': '연결 없음',
  'vision.fullscreen.enter': '{device} 영상 전체 화면',
  'vision.fullscreen.exit': '전체 화면 끝내기',
  'vision.camera.pick': '{device} 카메라 고르기',
  /** 모듈 카드의 시료 수. 판정은 서버가 준 `Sample.current_module` 하나다 */
  'monitoring.sampleCount': '시료 {count}건',

  /*
   * ── 모듈 보류 — **모듈이 스스로 섰다**(`ModuleSnapshot.hold`) ─────────────────
   *
   * 🔴 **계약에 선언돼 있다**(2026-09-22 확인 · `ModuleSnapshotHold.kind` enum
   *    `none`·`user_pause`·`safety_stop`·`reagent_exhausted`·`tip_exhausted`). 실물도 준다.
   * 🔴 **상태(`state`)와 «다른 축»이다.** 실물에서 염색기가 `state: DISPENSING` 인 채로
   *    `hold.kind: reagent_exhausted` 로 섰고(2026-09-22 실측), 화면은 「분주 중」이라고만
   *    말했다. 2분 동안 0 스텝인데 멈췄다는 자국이 한 자도 없었다.
   *    ⇒ 둘을 **나란히** 적는다. 상태를 보류로 덮지 않는다 — 둘 다 서버가 준 사실이다.
   * 🔴 **이것은 «주의»지 «실패»가 아니다.** 위험색을 쓰지 않는다(활성 ERROR 알람이 같이
   *    있으면 그쪽이 위험색이다). 배지는 일시정지 얼굴(`❙❙`)을 빌려 쓴다.
   * ⚠ **모르는 `kind` 는 원값을 보인다** — `enumLabel` 이 사전에 없는 값을 그대로 낸다.
   *   지어내지 마라.
   */
  'monitoring.hold': '보류 · {kind}',
  'enum.holdKind.user_pause': '사용자 일시정지',
  /** v0.9.5 모듈 계약: 비상정지 축과 안전 상태 축은 따로 걸리고 따로 풀린다. */
  'enum.holdKind.emergency_stop': '비상정지',
  'enum.holdKind.safety_stop': '안전정지',
  /** 낙하 표지가 남아 새 작업을 거절하는 보류 — 안전정지와 다르며 낙하 회수 신고로 푼다. */
  'enum.holdKind.grip_drop': '그리퍼 낙하',
  /** v0.9.3 정본 신설 — 협동로봇 접촉 감지 보호정지(로봇만 멈춤 · `SM-SAFE-07` ② · 해제는 보호정지 해제 + 준비 게이트 뒤 재개) */
  'enum.holdKind.protective_stop': '보호정지',
  /** ⚠ 해제는 **안전 해제가 아니라 보충 후 재개**다(`REAGENT_EXHAUSTED_IN_RUN`) */
  'enum.holdKind.reagent_exhausted': '시약 소진',
  /**
   * 팁 랙 소진으로 모듈이 스스로 섰다(2026-09-29 계약 추가). 문구는 짝 코드 `TIP_DEPLETED` 의
   * 카탈로그 `message` 그대로다. ⚠ `reagent_exhausted` 로 접지 않는다 — 사람이 할 일이 다르다
   * (랙 교체 ↔ 시약 보충). 해제는 **랙 교체 후 재개**다
   */
  'enum.holdKind.tip_exhausted': '팁 랙 소진',
  /**
   * 코어가 레시피 스텝 경계에서 건 자동 멈춤(모듈 계약 0.15 · `/pause` 의 `origin=orchestrator` · 2026-10-22 재생성). 코어 재개가 자기 보류만 걷는다.
   * ⚠ 위험이 아니다 — `DANGER_HOLDS`(안전정지 · 보호정지)에 넣지 않는다
   */
  'enum.holdKind.orchestrator_pause': '자동 일시정지',
  /**
   * 모듈 카드의 **활성 알람 수**(`ModuleSnapshot.active_alarm_count`).
   * 🔴 `0`·`null` 이면 **적지 않는다** — 「알람 0건」은 자리만 먹는다.
   * ⚠ 이 값은 REST(`GET /modules`)가 주는 것이고 구독 미러는 늘 `null` 이다
   *   (`stream/bridge.ts` 머리말). 구독 프레임으로 이 수를 덮지 마라.
   */
  'monitoring.activeAlarms': '활성 알람 {count}건',

  /*
   * 시료 흐름 — 시료 관점 모니터링의 주인공(eric 지시 2026-09-21).
   *
   * ⚠ 종전에는 **시료 × 단계 격자**가 이 낱말들을 썼다. 격자는 2026-09-21 오후에 걷었고
   *   (「시료 흐름 부분을 없애고 환자별 챔버 그림으로 합쳐줘」) 같은 낱말을 **카드 안의
   *   단계 줄**이 쓴다. 뜻은 한 글자도 안 바뀌었다.
   * 🔴 **단계 이름은 새로 짓지 않는다.** 단계 줄과 단계 카드는 `enum.sampleStage.*`
   *    (등록 · 원심 · 어셈블 · 염색 · 완료)를 그대로 쓴다 — 계약 `SampleStage` 어휘다.
   *    eric 이 말로는 「압착」이라 했지만 `vocab/equipment.yaml` 에 그 낱말이 없다
   *    (그쪽은 「어셈블러 / 어셈블 스테이지」). 화면 문구는 사전을 따른다.
   * 🔴 **「성공」과 「지남」을 가른다.** 서버가 판정을 말하지 않으면 화면은 「지남」까지만
   *    말한다 — 단계 표지가 찍힌 것과 그 단계가 성공한 것은 다른 사실이다.
   */
  /** 도는 작업이 없거나 그 작업에 시료가 0건일 때 */
  /* `monitoring.flow.empty`(「진행 중인 작업 없음」)는 2026-09-22 에 지웠다 — 도는 작업이 없으면 점선 자리 카드가 선다(eric) */
  /** 🔴 끊겼다 — 「지남」은 남기고 「지금」만 모른다고 말한다 */
  'monitoring.flow.stale': '연결 끊김 — 지금 단계를 알 수 없음',
  /** 서버가 표지를 찍었다. **성공이라고 말하지 않는다** */
  'monitoring.flow.cell.marked': '지남',
  'monitoring.flow.cell.active': '실행 중',
  'monitoring.flow.cell.pending': '대기',
  /** 판정이 실제로 왔을 때만 */
  'monitoring.flow.cell.pass': '성공',
  'monitoring.flow.cell.fail': '실패',
  /** 폐기돼 여기서 멈췄다 — 뒤 단계는 오지 않는다 */
  'monitoring.flow.cell.halted': '중단',
  /*
   * 환자별 챔버 카드 — **시료 탭의 유일한 그림**(eric 지시 2026-09-21: 「환자1 — 챔버 1,2 …
   * **환자 단위로 잘라서**」 · 오후에 「그림 위주로 … 합쳐줘」).
   *
   * 🔴 **「챔버 1」 같은 번호를 화면이 짓지 않는다.** 칩에 찍히는 이름은 레시피가 적은
   *    웰 이름(`A1`·`C3`) 그대로다 — eric 이 「챔버 1,2」라고 말했지만 그건 «두 개»라는 뜻이고,
   *    이름은 레시피가 말하는 것을 쓴다. `monitoring.flow.chamber` 는 스크린리더용 접두사다.
   * 🔴 **「배정 정보 없음」은 「없다」가 아니라 「모른다」**이다 — 시료↔묶음을 잇는 축이 서버에
   *    없다(BR-31). 문구를 「없음」으로 줄이지 마라.
   */
  /*
   * 🔴 화면 낱말은 **「시료」**다(eric 2026-09-22 「환자라고 나오는데 환자 용어보다는 시료라고」). 키 이름의 `patient`
   *    는 레시피 묶음의 역할(`role: 'patient'` ↔ `reference`)이라 그대로 두고 **값만** 바꿨다. 카드는 시료 한 건을 말한다.
   */
  'monitoring.flow.groups.title': '시료별 챔버',
  /*
   * 🔴 **카드 머리의 순번이다**(eric 2026-09-26 「모니터링 페이지에 시료 ID 빼라고. 순번으로 하라고」 ·
   *    「너무 복잡한데」). 대조를 뺀 카드 차례(배정 웰 순 → 없으면 시료 차례)로 1부터 센다.
   *    시료 ID 는 `title` 과 보조기술에만 남는다(`SampleBundles` 의 `SampleName`).
   * ⚠ 2026-09-23 에는 이 글자를 화면에서 걷었다(eric 「시료1, P4 이런 게 많은데 이거 빼고 … 시료
   *   아이디 헤더에」) — **eric 이 2026-09-26 에 뒤집었다.** 되돌림 방지 시험도 그날 방향을 바꿨다.
   */
  'monitoring.flow.groups.patient': '시료 {index}',
  /** 시료 카드 오른쪽 위 `›` 표지의 글자 — 마우스를 올리면 보인다(디자인팀 시안 2026-10-01 §3 「카드 상세 이동」) */
  'monitoring.flow.groups.openDetail': '상세 보기',
  /**
   * 시료 카드 머리의 「작업 중」 배지(2026-10-01 CTCells 회의 「9개 중 현재 작업 중인 칩이 구분되어야 함」).
   * 🔴 서는 조건은 그 챔버의 한 마디가 「진행」일 때뿐이다(`BundleChamber` 의 `chamberWorking`) — 화면이 짐작하지 않는다.
   */
  'monitoring.flow.groups.working': '실행 중',
  /**
   * 🔴 레시피 묶음에 못 든 시료 — **몇 번 환자인지 아는 사람이 없다.** 번호를 지어내지
   *    않는다. 「환자가 아니다」가 아니라 「어느 환자인지 모른다」이다.
   */
  'monitoring.flow.groups.patientUnknown': '시료 미상',
  'monitoring.flow.groups.unassigned': '배정 정보 없음',
  /*
   * 🔴 **묶음 카드에 시료가 없는 까닭 — 챔버 아래 한 마디**(eric 2026-09-26 「‘배정 정보 없음’ 이 문구도
   *    빼줘 그냥. 그 밑에 표현하면 됨」). 카드 머리에서 내려와 모듈 칩 자리에 낱말 하나로 선다.
   *    ⓐ `vacant` — 배정 규칙이 돌았는데 이 묶음에 든 시료가 없다(묶음이 시료보다 많다)
   *    ⓑ `unknown` — 배정 규칙 자체가 없다. 🔴 「모른다」를 「없다」로 접지 마라
   */
  'monitoring.flow.groups.vacant': '미배정',
  'monitoring.flow.groups.assignUnknown': '배정 모름',
  /** 묶음에 못 든 시료의 챔버 자리. 🔴 조용히 버리지 않는다 */
  'monitoring.flow.groups.noChamber': '챔버 배정 없음',
  /* 작업이 진행 중이 아닐 때 자리 카드의 챔버 자리(eric 2026-10-08) */
  'monitoring.flow.groups.noAssignment': '챔버 배정 정보 없음',
  'monitoring.flow.chamber': '챔버 {well}',
  /*
   * ── 시료 탭 **비전** (eric 2026-09-23 「맨 위의 왼쪽 위에는 비전 영상(장비의 비전 내용)」) ──
   * ⚠ 2026-09-26 격자 칸 → 떠 있는 창 → 2026-09-28 **오른쪽 고정 열 세 칸** → 2026-10-02 오른쪽 열 「비전」 탭
   *   → 같은 날 **장비별 보기 상세 카드의 그 장비 칸 하나**(`VisionPane`)다.
   *
   * 🔴 **영상은 배치를 타지 않는다**(eric 2026-09-23 「여기 비전이 비어 있는데, 이걸 장비 쪽
   *    비전 영상을 붙여 달라고」). 그래서 카드 «안»이 비는 사유는 **스트림 설정 없음**
   *    (`monitoring.noVisionFeed`) 하나이고, 연결 실패는 짧은 표지 `vision.badge.*` 가 따로 말한다.
   * ⚠ 종전의 `monitoring.flow.vision.idle`(「실행 중인 장비가 없습니다」)은 **지웠다** —
   *   도는 작업이 없어도 영상이 서므로 그 빈 상태가 없다. 되살리지 마라.
   * 🔴 **「영상 없음」으로 줄이지 마라.** 카메라가 못 봤다는 뜻이 되어 버린다.
   */
  /**
   * 칸 영상의 이름(보조기술) — 어느 장비의 카메라인가(`VisionPane`).
   * ⚠ 떠 있는 창의 문구 · 세 칸 열 전체의 이름(`monitoring.flow.vision.column`)은 그 자리와 함께 걷었다. 되살리지 마라.
   */
  'monitoring.flow.vision.head': '비전 · {device}',
  /*
   * ── 챔버 아래 한 마디 ────────────────────────────────────────────────────
   * eric 지시(2026-09-23, 네 번 깎였다):
   * ① 「각각의 챔버 아래에 **모든 이벤트가 나오지 말고, 어디까지 끝냈는지만** 간단하게 표현해줘.」
   * ② 「원심분리기(이거 **칩 형태**) **준비 중, 진행, 완료** 이렇게 해줘.」
   * ③ 「원심분리기, 어셈블러, 염색기 이렇게 **명칭을 다 써줘**.」
   * ④ 🔴 「아니 다 적지 말고, **최종만** 적으라고. 여기서는 염색기 진행이 되어야지.」
   *
   * 같은 날 구역 머리의 단계 줄(`.stagerail` — 「등록 지남 · 원심 지남 …」 다섯 칸)을 **통째로
   * 걷었다**(「우측 상단에 이건 빼줘」). 그 열 낱말이 곧 ① 이 말한 「모든 이벤트」다.
   *
   * 🔴 **낱말 셋이 전부다.** 시각 · 이벤트 이름 · 스텝 수를 여기 더하지 마라 — 더하는 순간
   *    걷어 낸 그 줄이 챔버마다 되살아난다.
   * 🔴 **「진행 «중»」이 아니라 「진행」**이고, 「지남」이 아니라 **「완료」**다(④ · 「어디까지 «끝냈는지»」).
   * ⚠ `enum.executionEvent.progress`·`panel.done` 같은 «다른 축»의 키를 끌어다 쓰지 마라 —
   *   글자가 같다고 뜻이 같은 것이 아니고, 그쪽이 바뀌면 이 줄이 같이 흔들린다.
   * 🔴 서버 마디(`monitoring.phaseMark.*`)에 장비 이름을 넣지 마라 — eric 2026-10-29 「앞에 장비 칩이 있으니 장비
   *    이름 빼고 그냥 대기」. 「원심 대기」→「대기」 · 「원심 중」→「회전 중」 · 「어셈 완료」→「완료」. 「원심」「어셈」 같은 약어도 금지.
   *    `monitoring.chamber.done` 과 글자가 같아도 키를 합치지 마라(위 ⚠).
   */
  /** 모듈의 말 꼬리(`{sequence} 시작|완료` · `latestStepModel.sequenceText`) */
  'monitoring.step.started': '시작',
  'monitoring.step.done': '완료',
  'monitoring.chamber.ready': '준비 중',
  'monitoring.chamber.recipeStep': '단계 {no}/{total}',
  'monitoring.phaseMark.centrifuge_waiting': '대기',
  'monitoring.phaseMark.centrifuge_spinning': '회전 중',
  'monitoring.phaseMark.centrifuge_done': '완료',
  'monitoring.phaseMark.assembler_waiting': '대기',
  'monitoring.phaseMark.assembler_cycle_running': '사이클 중',
  'monitoring.phaseMark.assembler_cycle_done': '사이클 완료',
  'monitoring.phaseMark.assembler_draining': '폐액 배출 중',
  'monitoring.phaseMark.assembler_done': '완료',
  'monitoring.chamber.doing': '실행 중',
  'monitoring.chamber.done': '완료',
  /** 6축 이송 중 — 챔버 아래 배지(공정명 자리는 「6축 로봇」). 🔴 「준비 중」으로 접지 마라(팀장 (가)안 2026-10-06) */
  'monitoring.chamber.transfer': '이송 중',
  /*
   * 웰 근거(분주 기록) 챔버의 칩 — 모듈 이름 대신 **단계 이름**을 적는다(「분주 완료」 · eric 2026-09-28).
   * 분주는 시약마다 한 바퀴다 — 툴팁에 이번 바퀴의 스텝(`step_ref` 원문)을 적어 「왜 다시 준비 중인지」가 읽히게 한다.
   */
  'monitoring.chamber.dispense': '분주',
  /** 피펫이 지금 그 웰에 있다(서버 사실 · `StainPipetteWellContext`) — 배지 하나로 「분주 중」(eric 2026-10-14 「피펫으로 할 때.. 분주 중이라고 해야지」) */
  'monitoring.chamber.dispensing': '분주 중',
  'monitoring.chamber.dispenseStep': '이번 분주 스텝: {step}',
  /*
   * ── 어셈블러 · 염색기 데크 랙 이름(`deckRacks.ts`) ──────────────────────────
   * 🔴 랙 그림의 `aria-label` · 소모품 칩 · 서랍 제목에 쓴다. 이름은 **비전 화면의 이름**(tuberack1 · tuberack2)을 따른다.
   * ⚠ 시료 탭 셋째 열 데크 판과 그 이름(`monitoring.supply.deck`)은 걷었다(eric 2026-09-28 「오른쪽은 제거해버려」).
   * ⚠ 종전 `RA{index} · 상온` · `RC · 냉장` · `현재 / 보관 {temp}` 키는 **걷었다**(2026-09-26 eric 「상온
   *   RA1, RA2, RC 이 부분은 빼고」). 되살리지 마라.
   */
  /**
   * TC 랙 이름(eric 2026-10-02 「TC 적재소를 TC Rack 으로 수정하고」 → 「TC Rack 은 tc rack 으로」 · 종전 「TC 적재소」). 소모품 판 카드 머리 · 접근 이름과
   * 장비 탭 랙 그림의 접근 이름이 같이 읽는다(보이는 랙 이름 `tc rack` 은 `deckRacks.ts`). 키 `tcStock` 은 서버 자리 이름이라 그대로다.
   */
  /* ⚠ 2026-10-19 첫 글자만 대문자로(eric 「tc rack을 TC rack, tip rack1을 Tip rack1 … cold rack를 Cold rack이라고」) — 장비 그림 이름표(`monitoring.eqRack.*`)와 같은 꼴 */
  /* 2026-10-20 — R 대문자(장비 · 레시피 · 소모품 같은 이름) */
  'monitoring.supply.tcStock': 'TC Rack',
  /**
   * 냉장 랙 이름 — tc rack · tip rack 과 같이 영어 소문자(eric 2026-10-02 「듀브랙 냉장 튜브랙 다 영어로 써줘」 · 종전 「냉장 튜브 랙」).
   * 데크 label `coldrack`(`deckRacks.ts`)과 맞췄다. 소모품 판 시약 카드의 랙 선택지 · 장비 탭 랙 그림의 접근 이름이 같이 읽는다
   */
  /* 2026-10-20 「Cold Rack 이렇게」 — R 대문자(레시피 · 장비 · 소모품 같은 이름) */
  'monitoring.supply.chilledRack': 'Cold Rack',
  /** 팁 랙 이름 — tc rack 과 같이 영어 소문자(eric 2026-10-02 「팁 랙 영어로 바꿔주고」 → 「Tip Rack tip rack 으로 수정해줘」 · 종전 「팁 랙 {index}」) */
  /* 2026-10-20 「다 수정」 — Tip Rack1 · Tip Rack2(R 대문자 · 숫자 붙여 씀) */
  'monitoring.supply.tipRack': 'Tip Rack{index}',
  /** 튜브 랙 이름 — 영어 소문자(eric 2026-10-02 「다 영어로 써줘」 · 종전 「튜브 랙 {index}」). 데크 label `tuberack1` · `tuberack2` */
  /* 2026-10-20 「Tube Rack1, Tube Rack2 이렇게」 — R 대문자 · 숫자를 붙여 쓴다 */
  'monitoring.supply.tubeRack': 'Tube Rack{index}',
  /*
   * ── 소모품 요약 줄 · 서랍(eric 2026-09-27 「팁랙의 현재 상황, 전체 개수 중에 몇 개 차 있나 … 클릭하면 해당
   *    상세 내용이 나오도록」) ── 🔴 기본값(가득)의 출처는 칩 툴팁 · 보조기술 글자가 말한다(칩 줄 표지는 걷었다 · 2026-09-28 ·
   *    상세 팝오버의 「값의 출처」 줄은 2026-10-02 에 팝오버와 같이 걷었다)
   */
  /* ⚠ 선택기 줄 요약 카드의 접근 이름(`summary`)은 카드와 같이 걷었다(2026-10-02 eric 「장비별 보기에서.. 이거 없애줘」) */
  /**
   * 오른쪽 열 「소모품」 탭 판의 제목(2026-10-01 CTCells 회의 · 2026-10-02 「소모품 | 비전」 두 탭 · `SupplyPanel`).
   * ⚠ 항목 머리의 「상세」(`panelOpen`) · 상세 팝오버의 접근 이름(`popLabel`) · 제목(`detail`)은 걷었다(eric 2026-10-02
   *   「시약이나 소모품 클릭하면 팝업이 나오는데.. 안 나오게 해줘」 — 팝오버를 걷었다). 되살리지 마라.
   */
  'monitoring.supply.panel': '소모품 현황',
  /**
   * 소모품 판 시약 카드의 이름 — 한글 「시약」(eric 2026-10-02 「시약이 영어로 뭐야? … 다 영어로 써줘」로 `reagent` 였다가 같은 날
   * 「reagent 시약으로 바꾸고」로 되돌림). 랙 이름(tube rack · cold rack)은 영어 그대로다.
   * 🔴 이 키는 소모품 판 카드 이름(`supplyName()`)만 읽는다 — 다른 화면의 「시약」 문구는 제 키를 쓴다
   */
  'monitoring.supply.reagents': '시약',
  /**
   * 시약 칩의 보조기술 글자 — 눈에는 `1/8` 만 보인다(eric 2026-09-29 · 「개」는 2026-10-03 에 뺐다). 시약 단위 · **있음만 센다**(eric 2026-10-02 「1/8개 · 미수신 7
   * … 이게 맞아 보이는데?」). 미수신은 옆의 「미수신 N」 글자가 따로 읽힌다.
   * ⚠ 종전 웰 단위 `reagentFilled`(「14칸 중 11칸 채움」) · 없음을 세던 `reagentAbsent`(「8개 중 1개 없음」)는 걷었다.
   */
  'monitoring.supply.reagentPresent': '{total}개 중 {present}개 있음',
  /** 팁 · TC 머리 수치의 보조기술 글자 — 눈에는 `0/9` 만 보인다(단위는 여기에만 · eric 2026-10-03 「개 텍스트는 빼줘」) */
  'monitoring.supply.remainingOf': '{total}개 중 {remaining}개 남음',
  /**
   * 카드 머리 수치의 「/총 개수」 — 🔴 **눈에 보이는 단위 「개」가 없다**(eric 2026-10-03 「0/9 를 어셈블러 왼쪽으로 옮겨줘.. 개 텍스트는
   * 빼줘」 — 종전 「6/9개」 · 2026-10-02 「개도 넣어줘」를 뒤집었다). 단위는 보조기술 글자(`remainingOf` · `reagentPresent`)에만 든다.
   */
  'monitoring.supply.ofTotal': '/{total}',
  /**
   * 시약 카드의 시약대 소제목(eric 2026-10-02 「시약대 별로... 리스트를 적어줘」). 레시피 `labware[].display_name` 이 있으면
   * 그것이 먼저고, 없으면 `labware_id`(`staining.reagent_bay.ambient.N` · `…chilled`)에서 짓는다(`reagentBayName()`).
   */
  'monitoring.supply.bay.ambient': '상온 시약대 {index}',
  'monitoring.supply.bay.chilled': '냉장 시약대',
  'monitoring.supply.bay.none': '시약대 미지정',
  /**
   * 시약 랙 칸(eric 2026-10-06 6칸 — 랙마다 칸 하나)에 도는 작업 시약이 없을 때의 한 줄 · 머리 「—」의 보조기술 글자.
   * 칸 이름은 데크 랙 이름(`tubeRack` · `chilledRack`) 그대로다. ⚠ 종전 랙 고르기의 접근 이름(`rackPick`)은 선택기와 같이 걷었다.
   */
  'monitoring.supply.rackEmpty': '이 랙에 놓인 시약 없음',
  /*
   * tc rack · tip rack 칸 오른쪽 «구성» 목록(eric 2026-10-06 「왼쪽에는 그림.. 오른쪽에는... 성분 말이야..」) — 줄 이름표.
   * 남은 · 쓴 · 전체 세 줄뿐이다(eric 2026-10-06 「남은 팁 쓴 팁 전체 이거만 남겨줘」). 값은 장비 소모품 보고 — 모르면 앞 둘은 뺀다.
   */
  'monitoring.supply.spec.list': '{name} 구성',
  'monitoring.supply.spec.tipsLeft': '남은 팁',
  'monitoring.supply.spec.tipsUsed': '쓴 팁',
  'monitoring.supply.spec.tcLeft': '남은 TC',
  'monitoring.supply.spec.tcUsed': '쓴 TC',
  'monitoring.supply.spec.total': '전체',
  /**
   * 펴는 칸 머리 단추의 접근 이름 끝말(eric 2026-10-07 「누른다는 느낌이 있는 아이콘 같은 거 넣어줘」) — 눈에는 꺾쇠(›)가 말하고
   * 보조기술은 이 글자를 읽는다. 펼침 · 접힘 상태는 `aria-expanded` 가 말한다
   */
  'monitoring.supply.foldHint': '상세 보기',
  /** 도는 작업이 없을 때 시약 카드 용액 목록 자리의 한 줄(eric 2026-10-03 「작업이 안 도니 어떤 항체 용액인지는 안 나오겠지만」) — 랙 그림은 그대로 선다 */
  'monitoring.supply.noRecipeRack': '시약 배정 정보 없음',
  /**
   * 시약 카드의 줄 목록(시약 하나당 한 줄 · eric 2026-10-02 「한 줄에 하나씩.. 시약은 있냐 없냐로만」) — 줄 끝 표시 셋.
   * 「미수신」은 `monitoring.supply.unreceived` 를 같이 쓴다. 보관 온도는 레시피 `storage_celsius` 그대로.
   */
  'monitoring.supply.reagentList': '시약별 유무',
  'monitoring.supply.presence.present': '있음',
  'monitoring.supply.presence.absent': '없음',
  'monitoring.supply.celsius': '{celsius}℃',
  'monitoring.supply.storage': '보관 {celsius}℃',
  'monitoring.supply.noRecipe': '도는 작업 없음',
  'monitoring.supply.rackSplitUnknown': '랙별 미수신',
  /** 칩 한 장의 합계 — 칸별 기록과 합계가 어긋나 랙별로 못 나눌 때(두 팁 랙 칩이 같은 합계를 적는다) · 머리 수치라 「개」 없음(eric 2026-10-03) */
  'monitoring.supply.tipsSum': '합계 {remaining}/{total}',
  'monitoring.supply.tipsMismatch': '칸별 기록과 합계 불일치',
  'monitoring.supply.tipsMismatchNote':
    '칸별 사용 기록과 장비 보고 합계가 맞지 않습니다 — 그림은 가득으로 표시',
  /*
   * 소모품 카드(디자인팀 시안 2026-10-01 · `DESIGN-CHANGES.md` §3 「소모품」) — 막대 없이 수치만. 팁 20% 이하 「▲ 부족」,
   * 시약은 장비 값이 안 온 웰 수 「미수신 N」.
   * ⚠ 상세 팝오버의 수치 타일(`tile.tipsLeft` · `tile.total`)은 팝오버와 같이 걷었다(2026-10-02).
   * ⚠ 판 아래 범례(`legend.tipLeft` 남음 · `tile.emptySlots` 빈 자리 · `tile.loaded` 적재 · `tile.emptyCells` 빈 칸)는
   *   범례 줄과 같이 걷었다(eric 2026-10-02 「남음 · 빈 자리 · 적재 · 빈 칸.. 이건 삭제해줘」). 되살리지 마라.
   */
  'monitoring.supply.low': '부족',
  /**
   * 팁 · TC 가 0 — 머리 수치 오른쪽 빨간 ▲ 의 보조기술 · 툴팁 글자(eric 2026-10-03 「없거나.. 그러면.. 오른쪽에.. 노란색이나 빨간색.. 세모」).
   * 눈에는 ▲ 만 보인다(`low` 「부족」과 같은 꼴 · 색만으로 말하지 않게 이 낱말이 같이 읽힌다)
   */
  'monitoring.supply.empty': '없음',
  'monitoring.supply.unreceived': '미수신',
  /*
   * 🔴 시약은 «채움 여부»만(eric 2026-09-28 「용액 값은 없애줘 … 채웠냐. 안채웠냐만 알면 돼」) — 부피 문구를 두지 않는다.
   * ⚠ 종전 `fill.*`(가득 · 사용됨 · 모름) · `column.left`(남음) · `column.filled`(채움 부피) 키는 걷었다.
   */
  'monitoring.supply.state.filled': '채움',
  /* 「비어 있음」 → 「없음」(eric 2026-10-11 · 범례 「있음 · 없음」과 같은 말) */
  'monitoring.supply.state.empty': '없음',
  'monitoring.supply.wells': '칸별',
  'monitoring.supply.slotsUsed': '사용된 칸',
  /** 장비가 보고한 남은 수가 0 — 칸을 고를 필요 없이 전부 비었다(eric 2026-09-28) */
  'monitoring.supply.slotsUsedAll': '모든 칸 사용됨',
  'monitoring.supply.column.target': '대상 웰',
  'monitoring.supply.column.volume': '분주량',
  'monitoring.supply.column.rack': '랙',
  /* ⚠ 상세 팝오버의 「최근 분주 기록」 구역 키(`dispense*` · `column.source`)는 팝오버와 같이 걷었다(2026-10-02) */
  'monitoring.supply.lotExpiry': '유효 {date}',
  'monitoring.supply.result.ok': '정상',
  'monitoring.supply.result.clog': '막힘',
  'monitoring.supply.result.foam': '거품',
  'monitoring.supply.result.absent': '액 없음',
  'monitoring.supply.result.aborted': '중단',
  'monitoring.supply.op.dispense': '토출',
  'monitoring.supply.op.aspirate': '흡입',
  /*
   * 소모품 서랍 — 이름(왼쪽) — 값(오른쪽) 줄(eric 2026-09-28 「왼쪽에.. 해당 관련된 내용 적고.. 오른쪽에.. 값을 적어..
   * 현재 xx / 전체 xx 이렇게」). ⚠ 종전 큰 숫자 머리의 `filledWells` · `tipsReported` · `slotsUsedNone` 은 걷었다.
   */
  'monitoring.supply.amount': '잔량',
  'monitoring.supply.currentOfTotal': '현재 {current} / 전체 {total}',
  'monitoring.supply.row.remaining': '남은 수',
  /* ⚠ 상세 팝오버의 사실 줄 키(`row.remainingSum` · `row.filledWells` · `row.mismatch` · `row.origin` · `row.stale`)는 걷었다(2026-10-02) */
  'monitoring.supply.origin.live': '장비 보고',
  'monitoring.supply.origin.default': '장비 값 미수신 — 가득으로 표시',
  'monitoring.supply.origin.partial': '일부 미수신 — 미수신 칸은 가득으로 표시',
  'monitoring.supply.slotsNone': '없음',
  /*
   * 보충 기록(eric 2026-09-28 「보충 버튼 … 이거 두고」) — `POST /modules/{id}/consumables/replenishment`.
   * 🔴 「사람이 실제로 채웠다」는 기록이다 — 확인 창 문구가 그것을 먼저 말한다.
   */
  'monitoring.supply.replenish.action': '보충',
  /** 🔴 확인 한 번(2026-10-01 CTCells 회의 「"넣었습니까?" 확인 팝업 → 예 선택 시 보충 인식」). 「예」가 곧 기록이다 */
  'monitoring.supply.replenish.ask.tip': '팁을 넣었습니까?',
  'monitoring.supply.replenish.ask.tc': 'TC 를 넣었습니까?',
  'monitoring.supply.replenish.lead':
    '실제로 채운 뒤 「예」를 누르세요. 이 기록은 지워지지 않고 누가 언제 보충했는지 남습니다.',
  'monitoring.supply.replenish.yes': '예',
  'monitoring.supply.replenish.no': '아니요',
  'monitoring.supply.replenish.fullCount': '{count} (가득)',
  'monitoring.supply.replenish.manual': '개수 직접 입력 · 메모',
  'monitoring.supply.replenish.kindLabel': '종류',
  'monitoring.supply.replenish.kind.tip': '팁',
  'monitoring.supply.replenish.kind.tc': 'TC',
  'monitoring.supply.replenish.current': '지금 잔량',
  'monitoring.supply.replenish.count': '보충 후 잔량',
  /** 🔴 팁은 모듈 합계 하나다(랙별 보충 경로 없음) — 라벨이 그걸 말한다. 해설 문단(`tipsTotal`)은 2026-09-29 걷었다 */
  'monitoring.supply.replenish.countTips': '보충 후 잔량 (팁 랙 두 개 합계)',
  'monitoring.supply.replenish.countInvalid': '0 이상 {max} 이하의 정수를 넣으세요',
  'monitoring.supply.replenish.note': '메모(선택)',
  'monitoring.supply.replenish.demo': '목 데이터 모드에서는 보충 기록을 보내지 않습니다',
  'monitoring.supply.replenish.noModule': '이 소모품을 가진 모듈을 찾지 못했습니다',
  /** 단계 카드의 셈. 🔴 비율·진행률이 아니라 **센 수**다 */
  'monitoring.stage.marked': '{marked}/{total} 지남',
  'monitoring.stage.failures': '실패 {count}건',

  /* ── 모니터링 겉모습 개편(방안 A · 2026-09-22) ────────────────────────────
   * 🔴 **카드의 세 자리가 저마다 «다른 축»을 말한다.** 종전에는 챔버 그림 · 단계 줄 ·
   *    상태 줄이 같은 값에서 나와 한 카드가 같은 사실을 세 번 말했다.
   *      챔버  → 「담김 / 빈 / 실패」        (이 챔버에 시료가 들었나)
   *      단계 줄 → 「어디까지 + 언제」        (5칸 + 시각)
   *      아래 줄 → 「지금 무엇 + 얼마나 됐나」
   */
  /** 단계 카드의 큰 숫자 뒤에 붙는 단위. 🔴 숫자와 «다른 글자 크기»라 문장으로 합치지 않는다 */
  'monitoring.stage.markedUnit': '시료 지남',
  /*
   * ── 디자인팀 시안 2026-10-01 §2 「공정 칸」 — 전체 수를 모를 때의 막대 옆 한 마디 ──
   * 🔴 시료를 못 봤을 때(`tally` 없음)만 선다. 0 건(도는 작업 없음)은 종전대로 `mask.none` 이다.
   */
  'monitoring.stage.totalPending': '전체 수량 집계 중',
  /**
   * 챔버 그림이 말하는 것. 🔴 **단계를 말하지 않는다** — 단계는 단계 줄이 말한다.
   *    「대기」와 「진행 중」이 둘 다 이 값이다(시료가 그 챔버에 있다는 사실은 같다).
   */
  'monitoring.chamber.loaded': '담김',
  /** 단계 줄 아래 시각 칸의 이름(스크린리더용) */
  'monitoring.flow.times': '단계 시각',
  /**
   * 🔴 **「못 봤다」이지 「없다」가 아니다.** `GET /reports/{sample_id}` 는 operator/admin
   *    전용이라(`app/views/reports.py` `REPORT_ROLES`) 그 아래 역할에는 403 이다.
   *    그때 시각 칸만 접고 카드는 그대로 선다.
   */
  'monitoring.flow.timesUnavailable': '단계 시각을 볼 수 없습니다.',
  /**
   * 「{시각} 이후 {경과}」. 🔴 **화면이 해도 되는 계산은 시각의 뺄셈뿐이다** —
   *    「늦었다 · 지연」 판정은 기준값이 서버에도 계약에도 없으므로 만들지 않는다.
   * 🔴 기준은 **그 시료가 마지막으로 받은 표지의 시각**이다. 「이 단계가 시작된 때」가
   *    아니다 — 서버가 단계 «시작» 사건을 남기지 않는다(표지는 끝난 자리에서 찍힌다).
   */
  'monitoring.flow.since': '{time} 이후 {elapsed}',
  'monitoring.flow.elapsedMin': '{min}분',
  'monitoring.flow.elapsedHour': '{hour}시간 {min}분',
  /**
   * 가려진 값이 **무엇인지** 적는다 — `••••` 만 있으면 무엇을 가린 것인지 알 수 없다.
   * ⚠ 이 자리에 가려지는 것은 **시료 ID** 다(`Sample.sample_id`). 「환자 번호」가 아니다 —
   *   환자 번호(`patient_id`)는 이 카드에 애초에 실리지 않는다.
   */
  'monitoring.flow.maskedSample': '시료 번호',

  /*
   * 모니터링 시료 서랍(카드 「시료 1」을 누르면 · 2026-09-28). 구역 제목만 여기 있다 — 칸 이름은 `column.*` 를 쓴다.
   */
  'monitoring.sampleDrawer.chambers': '챔버',
  /** 서랍 트레이 그림의 접근 이름 — 그 시료의 칸(eric 2026-10-06 「챔버 말고.. 트레이를」) */
  'monitoring.sampleDrawer.tray': '트레이 · 이 시료의 칸 {wells}',
  'monitoring.sampleDrawer.progress': '진행 기록',
  'monitoring.sampleDrawer.dispenses': '분주 기록',

  // 패널 구역 제목 추가분
  'panel.suggestedActions': '조치',
  'panel.trayLoad': '트레이 적재',
  'panel.trayUnload': '트레이 수거',
  'panel.handoffPoint': '자리',
  'panel.openedAt': '열림',
  'panel.confirmedAt': '확약',
  'panel.loadedChambers': '적재 챔버',

  // 작업 상세 — 요약 카드 · 확약
  'column.completedAt': '완료',
  /* 🔴 이력 표 머리글 — `Batch.completed_at` 은 `aborted` 에도 채워져 「완료」가 아니라 「종료」다(`batchTimeColumns.tsx`) */
  'column.endedAt': '종료',
  /*
   * 작업 상세 「작업」 카드의 시각 둘(eric 2026-10-22 「시작을.. 작업 시작, 종료를 작업 종료 이렇게 하고」) — 목록 머리글
   * (`column.startedAt` 「시작」 · `column.endedAt` 「종료」)은 그대로라 이 카드 전용 키를 둔다. 값 축은 같다(`started_at` · `completed_at`)
   */
  'row.batchStartedAt': '작업 시작',
  'row.batchEndedAt': '작업 종료',
  'row.pendingConfirm': '확약 대기',
  /*
   * 작업 상세 「레시피」 카드 [상세] — 작업이 고정한 판과 레시피의 지금 판이 다를 때(2026-10-22). 서버가 판을 고르는 조회 경로를 두지 않아
   * (`GET /recipes/{id}` 는 지금 판뿐) 그 판의 본문을 받을 길이 없다 — 지금 판을 대신 보이지 않는다
   */
  'batchDetail.recipe.versionGone':
    '이 작업이 쓴 판(v{fixed})의 본문을 받을 경로가 없습니다 — 레시피의 지금 판은 v{current}입니다',
  /* 확약 **창구**(surface) 축 — 「누가」가 아니라 「어디서」 */
  'enum.confirmSource.module_local': '모듈 로컬',
  'enum.confirmSource.tablet': '태블릿',
  'enum.confirmSource.client_app': '데스크톱',
  'enum.confirmSource.robot_pick': '로봇 수거',
  'enum.confirmSource.dev_auto': '개발 자동 확인',
  'value.awaitingConfirm': '확약 대기',
  'action.confirmLoad': '적재 확인',
  'action.confirmUnload': '수거 확인',
  'reason.alreadyConfirmed': '이미 확약했습니다',
  /*
   * 적재 확약 창의 신고 칸(`LoadConfirmEnvelope.loaded_chambers` · 선택).
   * 🔴 **비우면 신고하지 않는다** — 빈 칸의 자리 글자가 「신고 안 함」이다.
   * ⚠ 평형추 적재 칸(`counterweight_loaded`)은 2026-09-29 계약에서 빠져 걷었다.
   */
  'field.loadedChambersReport': '적재 챔버 수',
  'value.notReported': '신고 안 함',
  /* 서버가 기대 챔버 수(`expected_chambers`)를 줄 때 빈 칸의 자리 글자 — 비우면 여전히 신고 안 함(eric 2026-10-29) */
  'value.expectedChambers': '예상 {count}',
  'reason.loadedChambersInvalid': '적재 챔버 수는 0 이상의 정수로 적습니다',
  /*
   * 🔴 **표마다 따로 말한다**(2026-09-20 실물에서 갈랐다). 종전에는 두 표가 같은
   *    「적재 · 수거 기록 없음」을 썼는데, 적재 1건 · 수거 0건인 회차에서 **아래 표가
   *    「둘 다 없다」고 말했다.** 없는 것은 수거뿐이다.
   *    ⚠ 되돌려 하나로 합치지 마라.
   */
  'state.empty.trayLoad': '적재 기록 없음',
  'state.empty.trayUnload': '수거 기록 없음',
  'screen.sampleReport.title': '시료 보고서',
  'row.reagentLots': '사용 시약 lot',
  'row.dispenseCount': '분주 기록',
  /* 되돌릴 수 없는 명령의 확인 대화 — 계약이 `reason` 을 필수로 세운 자리다 */
  'screen.batchDiscard.title': '작업 폐기',
  'screen.batchConfirmCompleted.title': '완료 확정',
  /*
   * 대기 작업 취소(eric 2026-10-29) — `POST /batches/{id}/stop`(`created`·`queued` → `aborted`).
   * 🔴 시료 해제 문장은 서버 근거가 있다: 시작 전 중단은 `_release_samples` 로 시료의 `batch_id`·`assigned_well` 을 푼다
   *    (`ctcells-core` `services/batches.py` `stop` · `NOT_STARTED`). 사유는 계약 필수(`IrreversibleCommandEnvelope.reason`).
   */
  'batch.cancel.action': '작업 취소',
  'batch.cancel.title': '작업 취소',
  'batch.cancel.body':
    '대기 중인 작업을 취소합니다. 담긴 시료는 다시 쓸 수 있게 풀립니다. 되돌릴 수 없습니다.',

  /*
   * 원장의 `detail.operation` — 🔴 **`enumLabel()` 로 읽지 않는다.** 그쪽은 모르는 값을
   *    `—` 로 «떨어뜨려» 무슨 명령이었는지를 통째로 지운다. 여기 오는 값은 서버가 자유롭게
   *    늘리는 연산 이름이라 **사전에 없으면 원문이 정보**다(`batchDetailModel.operationText`).
   * ⚠ 아래 여섯은 `app/views/batches.py` `_write_audit` 의 실측 목록이다.
   */
  'enum.operation.createBatch': '작업 생성',
  'enum.operation.startBatch': '시작',
  'enum.operation.pauseBatch': '일시정지',
  'enum.operation.resumeBatch': '재개',
  'enum.operation.stopBatch': '중단',
  'enum.operation.recoverBatch': '복구 판정',

  /*
   * 🔴 **`env.*` 4종을 되살리지 마라**(2026-09-20 제거 — 작업 상세 「환경」 탭과 함께).
   *    배치별 환경 요약을 주는 경로가 없다(`docs/backend-requests.csv` BR-18).
   */

  // 개발 › 제어 — 방향은 실제 움직임에 맞춘다(2026-09-16 지시)

  // 사전 점검 — 🔴 **9항목 목록(`preflight.<항목키>`)은 2026-09-20 에 걷었다.**
  //   응답이 주는 것은 `passed` + `blockers` 뿐이라 항목 이름을 화면이 쥘 근거가 없었다.
  //   되살리지 마라 — 서버가 말한 적 없는 점검을 했다고 보이게 된다.
  'preflight.passed': '통과',
  'preflight.blocked': '차단',
  'preflight.blockers': '차단 사유',
  'preflight.matrix.title': '시작 전 점검 항목',
  'preflight.matrix.state': '상태',
  'preflight.matrix.fail': '차단',
  'preflight.matrix.inconclusive': '판정 불가',
  'preflight.matrix.warning': '확인 필요',
  'preflight.matrix.count': '{count}건',
  'preflight.matrix.empty': '해당 항목 없음',
  'preflight.matrix.missing.fail': '차단 항목 정보 미제공',
  'preflight.matrix.missing.inconclusive': '판정 불가 항목 정보 미제공',
  'preflight.matrix.missing.warning': '경고 항목 정보 미제공',
  'preflight.group.common': '공통',
  'preflight.group.reagents': '시약',
  'preflight.group.details': '상세',
  'preflight.group.settingPass': '설정으로 통과',
  'preflight.group.code': '코드',
  'preflight.group.setting': '통과 처리 설정',
  'preflight.group.module': '모듈',
  'preflight.group.item': '항목',
  'preflight.group.verdict': '판정',
  'preflight.group.detail': '상세 사유',
  'preflight.group.required': '필요 조치',
  'preflight.group.confidence': '신뢰도',
  'preflight.group.autoConfirm': '자동 적재 확인 예정',
  'preflight.issue.HANDOFF_POINT_OCCUPIED_AT_START': '이송 자리 점유',
  'preflight.issue.PREFLIGHT_TRAY_NOT_LOADED': '트레이 미적재',
  'preflight.issue.BATCH_SAMPLE_SET_UNSUPPORTED': '시료 구성·원심 짝 확인',
  'preflight.issue.VISION_INSPECT_INCONCLUSIVE_AT_START': '비전 판정 불가',
  'preflight.issue.VISION_DEVICE_FAULT_AT_START': '비전 장치 고장',
  'preflight.issue.PREFLIGHT_MODULE_NOT_IDLE': '모듈 대기 상태 아님',
  'preflight.issue.PREFLIGHT_CONSUMABLE_UNVERIFIED': '소모품 잔량 확인 불가',
  'preflight.issue.PREFLIGHT_SAFETY_STOP': '안전정지',
  'preflight.issue.PREFLIGHT_GRIP_DROP_UNRECOVERED': '그리퍼 낙하물 미회수',
  'preflight.issue.PREFLIGHT_WASTE_NOT_EMPTIED': '폐기통 비움 필요',
  'preflight.issue.PREFLIGHT_DOOR_OPEN': '도어 열림',
  'preflight.issue.PREFLIGHT_BAY_OCCUPIED': '자리 내 물체 있음',
  'preflight.issue.PREFLIGHT_ITEM_MISSING': '필요 물품 없음',
  'preflight.issue.PREFLIGHT_COUNT_MISMATCH': '물품 개수 불일치',
  'preflight.issue.PREFLIGHT_POSE_INVALID': '물품 정위치 이탈',
  'preflight.issue.PREFLIGHT_PLAN_COORD_MISMATCH': '계획 좌표 불일치',
  'preflight.issue.PREFLIGHT_PROCESS_UNRESOLVED': '레시피 공정 연결 불가',
  'preflight.issue.PREFLIGHT_CHAMBER_DAMAGED': '챔버 손상',
  'preflight.issue.PREFLIGHT_CHAMBER_LEAKING': '챔버 누액',
  'preflight.issue.RECIPE_VALIDATION_FAIL': '레시피 검증 필요',
  'preflight.issue.TIP_SHORTAGE_AT_START': '팁 부족',
  'preflight.issue.TC_SHORTAGE_AT_START': 'TC 부족',
  'preflight.issue.REAGENT_SHORTAGE_AT_START': '시약 잔량 부족',
  'preflight.issue.REAGENT_VOLUME_LOW': '시약 여유 부족',
  'preflight.issue.REAGENT_EXPIRED_AT_START': '시약 사용 기한 경과',
  'preflight.issue.REAGENT_TEMP_NOT_REACHED': '시약 보관 온도 미도달',
  'preflight.issue.POSITION_UNTAUGHT': '좌표 티칭 필요',
  'preflight.issue.ALARM_NO_REMOTE_RECIPIENT': '원격 알림 수신자 없음',
  /*
   * 🔴 **`passed: true` 인데 `blockers` 가 차 있는 응답이 실재한다**(2026-09-20 실측 —
   *    `VISION_INSPECT_INCONCLUSIVE_AT_START` · `REAGENT_TEMP_NOT_REACHED` 둘 다
   *    `verdict: 'inconclusive'`). 그때 목록을 「차단 사유」라 부르면 **통과한 점검이 막힌
   *    것처럼 읽힌다.** 「막혔다」와 「못 쟀다」는 사람이 할 일이 다르다 — 이름도 달라야 한다.
   */
  'preflight.unresolved': '미판정 항목',
  /*
   * 🔴 **경고는 차단이 아니다**(2026-09-22 신설 — 실서버가 `PreflightResult.warnings` 를 낸다).
   *    막지 않는 대신 **시작할 때 확약을 요구**한다. 말이 「차단 사유」와 겹치면 사람이
   *    시작 버튼이 꺼진 줄로 읽는다 — 이름과 사유를 갈라 적는다.
   * `warnAck`는 상시 작업 줄의 시작 확약 대화(`shell/StripStartDialog.tsx`)에서 쓴다.
   */
  'preflight.warnings': '경고 — 시작 전 확인 필요',
  'preflight.warnAck': '위 경고를 확인했습니다',
  'preflight.title': '사전 점검',
  /*
   * 레시피 스텝 어휘 — **프론트 표 대조**(`@ctcells/core` `recipeStepVocabulary.ts` · 2026-09-27).
   * 🔴 서버 판정이 아니다 — 생성·시작을 막지 않는다. 옛 이름(`centrifuge.pick`)이 든 레시피는
   *    시작 직후 `paused` 로 멎었다(실측 2026-09-26 배치 `ae91ced0`). 사람에게 먼저 알린다.
   */
  'recipe.vocab.title': '레시피 스텝 어휘 — 화면 대조',
  'recipe.vocab.retired':
    '이 레시피는 옛 스텝 어휘를 씁니다({names}). 시작 뒤 멈출 수 있습니다 — 레시피를 복제하면 새 어휘로 바뀝니다',
  'recipe.vocab.unknown': '지금 어휘에 없는 스텝이 있습니다({names}). 판정은 서버가 합니다',
  'recipe.vocab.migrated': '옛 스텝 어휘 {count}곳을 새 어휘로 바꿔 올립니다',

  // 비활성 사유 추가분 — 툴팁 전용이다. 화면 본문에 조건을 글로 쓰지 않는다(§1-1)
  'reason.ackNotRequired': '확인이 필요 없는 알람입니다',
  'reason.alarmAcked': '이미 확인했습니다',
  'reason.alarmUnresolved': '조건이 아직 해소되지 않았습니다',
  'reason.alarmCleared': '이미 해제된 알람입니다',
  'reason.noAlarmsToClear': '해제할 알람이 없습니다',
  'reason.afterBatchCreated': '작업을 만든 뒤에 할 수 있습니다',
  'reason.needRecipeAndSample': '레시피와 시료가 있어야 합니다',
  'reason.batchAlreadyCreated': '작업을 이미 만들었습니다',
  'reason.requestInFlight': '보내는 중입니다',
  'reason.demoSource': '목 데이터에서는 보낼 수 없습니다',
  /* 셸이 표면을 선언하지 않았다 — 확약 창구(`confirm_source`)를 지어 보내지 않는다 */
  'reason.confirmSurfaceUnknown': '이 화면에서는 확약 창구가 정해지지 않아 확약할 수 없습니다',
  /*
   * 작업 줄 시작 창 — 사전 점검 줄마다 누를 조치(eric 2026-10-23 「사전 점검에서 체크해야 하는 것들을 버튼 형태로」 · `shell/preflightFixes.ts`).
   * 🔴 자리 정리는 이 작업도 중단시킨다 — 서버 정리가 비종착 작업을 전부 종착시킨다(뺄 작업을 받는 칸이 없다)
   */
  'pfFix.open': '점검 조치',
  'pfFix.confirmUnload': '수거 확인',
  'pfFix.confirmLoad': '적재 완료',
  'pfFix.loadsLoading': '적재 목록을 읽는 중입니다',
  'pfFix.loadsFailed': '적재 목록을 읽지 못했습니다 — [사전 점검 다시]로 재조회하세요',
  'pfFix.unloadsLoading': '수거 목록을 읽는 중입니다',
  'pfFix.unloadsFailed': '수거 목록을 읽지 못했습니다 — [사전 점검 다시]로 재조회하세요',
  'pfFix.loadMissing':
    '적재 대기 건이 없습니다. 수거·모듈 연결 상태를 확인한 뒤 [사전 점검 다시]를 누르세요.',
  'pfFix.prepare': '자리 정리',
  'pfFix.batchPreservingOnly':
    '작업을 중단하는 전체 준비는 시작 점검에서 사용할 수 없습니다. 항목별 조치를 사용하세요.',
  'pfFix.handoffPhysicalCheck': '해당 자리에서 트레이를 꺼내고 비어 있음을 직접 확인했습니다',
  'pfFix.handoffPointMissing': '서버가 자리 식별자를 제공하지 않아 정리할 수 없습니다',
  'batchHistory.incomplete': '당시 시료 배정 기록을 모두 확인할 수 없습니다.',
  'batchHistory.currentMissing': '일부 시료의 현재 상태를 확인할 수 없습니다.',
  'pfFix.replaceWaste': '폐기통 교체 기록',
  /* 소모품 줄 — 링크 대신 시작 창 위 보충 창(`shell/SupplyFix.tsx`) */
  'pfFix.supply.tip': '팁 보충',
  'pfFix.supply.tc': 'TC 보충',
  'pfFix.supply.reagent': '시약 보충',
  'pfFix.supply.noCapacity':
    '이 작업 레시피에서 칸 수를 읽지 못했습니다 — 채울 수를 정할 수 없습니다',
  /* `RCP-LBW-09` — 폐기통 자리 설치 정의 넣기(`shell/InstalledLabwareFix.tsx`) · 작업을 끊지 않는다(created 작업은 서버 가드 밖 · BR-137 철회) */
  'pfFix.installLabware': '설치 정의 넣기',
  'pfFix.lbw.title': '설치 정의 넣기 — {position}',
  'pfFix.lbw.module': '모듈',
  'pfFix.lbw.wantedRow': '점검이 찾는 정의',
  'pfFix.lbw.installed': '지금 설치된 것',
  'pfFix.lbw.templates': '랩웨어 템플릿',
  'pfFix.lbw.none': '없음',
  'pfFix.lbw.new': '새로 넣음',
  'pfFix.lbw.add': '고르기',
  'pfFix.lbw.remove': '걷기',
  'pfFix.lbw.keep': '되돌리기',
  'pfFix.lbw.wanted': '· 점검이 찾는 정의',
  'pfFix.lbw.alreadyListed': '이미 목록에 있습니다',
  'pfFix.lbw.noTemplates': '서버의 랩웨어 템플릿이 0건이라 고를 것이 없습니다',
  'pfFix.lbw.noPosition': '모듈 위치 목록에 이 자리가 없습니다',
  'pfFix.lbw.offsets': '분주점 오프셋(mm)',
  'pfFix.lbw.well': '웰',
  'pfFix.lbw.badNumber': '수가 아닌 칸이 있습니다 — {wells}',
  'pfFix.lbw.noChange': '바뀐 것이 없습니다',
  /* 서버 규칙(`preflight.trash_install_blockers` · `position_copy.installed_uris`) — 원점이 없으면 오프셋 키로만 그 정의를 «있다»고 본다 */
  'pfFix.lbw.offsetRule':
    '원점 미티칭 자리는 분주점 오프셋을 적어야 사전 점검이 이 정의를 인정합니다 — 칸을 비우면 오프셋 없이 넣습니다',
  'pfFix.lbw.partialOffsets': '오프셋 칸을 일부만 채웠습니다 — 전부 채우거나 전부 비우세요: {uris}',
  'pfFix.lbw.send': '설치 정의 저장',
  'pfFix.recheck': '사전 점검 다시',
  'pfFix.running': '{action} 중…',
  'pfFix.done': '{action} — 받았습니다 · 다시 점검합니다',
  'pfFix.failed': '{action} — {reason}',
  'pfFix.moduleUnknown': '모듈 상태를 아직 받지 못했습니다',
  'pfFix.passed': '사전 점검을 통과했습니다 — 닫고 [시작]을 다시 누르세요',
  /*
   * [한 번에 준비] — 걷었다(2026-10-24 · 부르는 곳 0건). 시작 창 꼭대기의 비활성 단추 이름(`pfAll.action`)만 남는다.
   * `pfAll.recreate.*` 는 같은 내용으로 다시 만들기(`shell/recreateBatch.ts` · [작업 중단 후 내려놓기])가 쓴다
   */
  'pfAll.action': '한 번에 준비',
  'pfAll.recreate.running': '같은 레시피 · 시료 · 웰로 다시 만드는 중…',
  'pfAll.recreate.notWaiting': '시작 전 작업만 다시 만들 수 있습니다(지금 {status})',
  'pfAll.recreate.noAssignments': '이 작업에 웰 배정이 없어 같은 내용으로 다시 만들 수 없습니다',
  'pfAll.recreate.unplaced':
    '시료 {count}건 가운데 웰이 정해진 것이 {placed}건뿐이라 같은 내용으로 다시 만들 수 없습니다',
  'pfAll.recreate.recipeNotCommitted':
    '레시피가 지금 확정(committed) 상태가 아니라({status}) 같은 내용으로 다시 만들 수 없습니다 — 아무것도 바꾸지 않았습니다',
  'pfAll.recreate.versionChanged':
    '레시피 버전이 바뀌었습니다(이 작업 v{was} → 지금 v{now}) — 다시 만들면 다른 작업이 되어 아무것도 바꾸지 않았습니다',
  'pfAll.recreate.createdOtherVersion':
    '새 작업 {batch}이 다른 레시피 버전으로 만들어졌습니다(v{was} → v{now}) — 시작하기 전에 확인하세요',
  /** 시작 창 · 운송 로봇이 쥔 채 멈춘 줄 — 이 작업을 끊는 유일한 조치(`HeldTrayFix.tsx`) */
  'pfHeld.action': '작업 중단 후 내려놓기',
  /** 점검 표 칸 안의 단추 — 칸이 좁아 짧게 · 한 줄(창 제목 · 확정 단추는 `pfHeld.action` 그대로) */
  'pfHeld.button': '중단 후 내려놓기',
  'pfHeld.notice':
    '이 작업을 중단하고, 운송 로봇이 쥔 트레이를 서버가 고른 빈 자리에 내려놓은 뒤, 같은 레시피 · 시료 · 웰로 작업을 다시 만듭니다. 내려놓기가 안 되어도 작업은 다시 만듭니다.',
  'pfHeld.physicalCheck': '로봇 팔 주변과 쥔 트레이를 현장에서 확인했습니다',
  'pfHeld.status.stopping': '작업 {batch} 중단 중…',
  'pfHeld.status.placing': '쥔 트레이를 {point}에 내려놓는 중…',
  'pfHeld.status.settling': '운송 로봇이 대기 자세로 돌아오기를 기다리는 중…',
  'pfHeld.placeRefused': '트레이를 내려놓을 수 없습니다 — {reason}',
  'pfHeld.noTarget': '트레이를 내려놓을 빈 자리가 없습니다',
  'pfHeld.placeTimeout': '{seconds}초 안에 내려놓기가 끝나지 않았습니다 — {said}',
  'pfHeld.placeHalted': '내려놓기가 멈췄습니다({status}) — {said}',
  'pfHeld.robotNotIdle': '내려놓은 뒤 운송 로봇이 대기로 돌아오지 않았습니다(지금 {state})',
  'pfHeld.handoffReason': '작업 중단 후 내려놓기 · 장부 기록이 없는 자리 {point} 비움 확약',
  'pfHeld.done': '같은 내용으로 다시 만들었습니다 — 새 작업 {batch}',
  'pfHeld.donePlaceFailed': '새 작업 {batch}을 만들었지만 트레이는 내려놓지 못했습니다 — {reason}',
  'pfHeld.failedStopped': '이 작업은 중단됐지만 다시 만들지 못했습니다 — {reason}',
  /** 시작 창 · 문 열림 등 풀 수 있는 정지(`StopsReleaseFix.tsx`) */
  'stopsRelease.check.robot_area': '쥔 트레이와 로봇 팔 주변을 직접 확인했습니다',
  'stopsRelease.check.tray_positions': '이송 자리별 트레이의 실제 위치를 확인했습니다',
  'stopsRelease.check.door_area': '열린 문 주변에 사람·손이 없고 닫아도 됩니다',
  'stopsRelease.check.rotor_balanced': '원심 적재 대칭을 맞췄고 챔버 자리를 바꾸지 않았습니다',
  'stopsRelease.title': '현재 시스템 정지',
  'stopsRelease.count': '걸린 정지 {count}건 · 시스템 전체 목록',
  'stopsRelease.children': '하위 {count}건({targets}) — 위 정지를 풀면 함께 풀립니다',
  /*
   * 전체 안전 정지 줄(`StopItem.kind === 'safety'`) — 서버 `said` 원문(「시스템 안전 비상정지 · 원인 화면 단추」) 대신 `GET /safety` 의
   * `state` · `cause` 로 짓는다(eric 2026-10-29 「이게 뭐야?」). 원인 갈래는 계약 `SafetyCause.kind` 넷 · 서버 `safety.cause_said` 와 같은 갈래
   */
  'stopsRelease.safety.line': '{head} — {cause}',
  'stopsRelease.safety.estop': 'E-STOP',
  'stopsRelease.safety.stop': '안전정지',
  'stopsRelease.safety.cause.operator': '화면의 E-STOP 단추로 눌렀습니다',
  'stopsRelease.safety.cause.hw': '{module} 비상정지 버튼',
  'stopsRelease.safety.cause.fault': '{module} 고장({code})',
  'stopsRelease.safety.cause.faultNoCode': '{module} 고장',
  'stopsRelease.safety.cause.unknown': '원인 모름',
  'stopsRelease.global': '시스템 전체 목록 · 다른 작업의 정지도 포함됩니다',
  'stopsRelease.empty': '현재 시스템 정지 목록이 비어 있습니다',
  'stopsRelease.action': '현장 확인 후 정지 풀기',
  'stopsRelease.confirm': '확인한 정지 모두 풀기',
  'stopsRelease.changed':
    '정지 목록이 바뀌었습니다. 목록을 다시 확인하고 현장 확인을 새로 해 주세요.',
  'stopsRelease.physical': '서버가 다음 현장 확인을 요구합니다',
  'stopsRelease.unknownCheck': '새로운 현장 확인 항목이 있어 해제할 수 없습니다',
  'stopsRelease.pending': '정지 해제 응답을 기다리고 있습니다',
  'stopsRelease.result': '정지 해제 결과',
  'stopsRelease.done': '처리됨',
  'stopsRelease.failed': '미처리',
  /** 실패한 결과 줄 아래 — 그 모듈 스냅숏(`GET /modules`)의 상태 · 실행 · 보류를 사전 말로만 옮긴다(조치 문장 없음) */
  'stopsRelease.moduleNow': '{module} 지금: {state}',
  'stopsRelease.moduleExecution': '{sequence} ({step}/{total})',
  // 재개는 팝업 안이 아니라 작업 줄 머리의 [재개]다(eric 2026-10-29 정지 팝업 ③ 단계 걷음)
  'stopsRelease.after':
    '장비 상태는 최신 정지 목록에서 확인하세요. 재개는 작업 줄의 [재개]로 합니다.',
  'stopsRelease.humanFirst': '현장 확인 필요',
  'stopsRelease.included': '상위 정지와 함께 해제',
  'stopsRelease.reconfirm': '다시 확인',
  /**
   * 정지 풀기의 문 닫기를 모듈이 `MODULE_ABORTED`(취소로 멈춤)로 거절한 줄 아래(`StopsAbortedDoorRecover.tsx`) —
   * 시퀀스 중계 `close_door` → 문이 닫힌 판독 → 초기화 중계 `initialize` → 정지 목록 다시 읽기
   */
  'stopsRelease.recover.action': '문 닫고 원점 복귀',
  'stopsRelease.recover.confirmBody':
    '{module} 이 취소로 멈춰 있어 정지 풀기의 문 닫기를 받지 않았습니다. 문 닫기를 직접 보내고, 문이 닫히면 원점 복귀(초기화)를 보냅니다. 정지는 끝난 뒤 [현장 확인 후 정지 풀기]로 다시 풉니다.',
  'stopsRelease.recover.check': '문 주변에 사람 · 손 · 물체가 없음을 확인했습니다',
  'stopsRelease.recover.step.closeDoor': '문 닫기',
  'stopsRelease.recover.step.waitDoor': '문 닫힘 확인',
  'stopsRelease.recover.step.initialize': '원점 복귀',
  'stopsRelease.recover.running': '{step} 진행 중입니다',
  'stopsRelease.recover.failed': '{step} 단계에서 멈췄습니다 — {message}',
  'stopsRelease.recover.doorTimeout': '정해진 시간 안에 문이 닫혔다는 판독이 오지 않았습니다',
  'stopsRelease.recover.done':
    '문을 닫고 원점 복귀를 보냈습니다. 정지 목록을 다시 읽었습니다 — 남은 정지는 [현장 확인 후 정지 풀기]로 풉니다.',
  'pfStops.action': '정지 풀기',
  'pfStops.loading': '걸린 것 목록을 읽는 중입니다',
  'pfStops.none': '풀 항목이 없습니다',
  'pfStops.releasable': '아래 항목을 한 번에 풉니다(열린 문은 닫힙니다)',
  'pfStops.notDone': '풀지 못한 항목이 있습니다 — {items}',
  'pfStops.stillListed': '{seconds}초가 지나도 목록에 남아 있습니다 — {items}',
  /* 🔴 확약은 **사람이 보고 누르는 것**이다 — 체크가 없으면 시작 버튼이 비활성 + 이 사유다 */
  'reason.warnAckRequired': '경고를 확인해야 시작할 수 있습니다',

  // 빈 상태 추가분
  'state.empty.reports': '보고서 없음',
  'state.empty.envRunning': '없음',
  'state.empty.pickedSamples': '담은 시료 없음',
  'state.empty.freeSamples': '담을 수 있는 시료 없음',
  'state.empty.committedRecipes': '확정된 레시피 없음',
  'state.empty.blockers': '차단 사유 없음',
  'state.empty.warnings': '경고 없음',
  'state.empty.preflightNotRun': '아직 점검하지 않음',

  // 명령 원장 축 — audit = 시료에 영향을 주는 동작 · ops = 물류·상태
  'enum.ledger.audit': '감사',
  'enum.ledger.ops': '운영',
  'enum.ledger.consumable': '소모품',

  // 제어 화면 이벤트 로그 종류

  // ── 마스킹 ───────────────────────────────────────────────────────────────
  // view 권한 없는 역할(service)에게 환자 번호·시료 ID 를 가린다 (§5-5 · §19-5)
  'mask.pii': '••••',
  'mask.none': '—',

  // ── 에러 문구 ────────────────────────────────────────────────────────────
  // 🔴 여기는 **덮어쓰기 목록**이지 카탈로그 사본이 아니다.
  //    서버 `ErrorResponse.message` 가 이미 error_codes.yaml 의 한글 메시지를 싣고 온다.
  //    아래에 키가 있는 코드만 서버 문구 대신 이것을 쓴다 — 근거는 docs/i18n.md.
  'error.unknown': '처리하지 못했습니다',
  'error.network': '서버에 연결하지 못했습니다',
  /*
   * 🔴 **계약 봉투를 벗어난 응답의 문구**(2026-09-22 실측). `GET /batches/<없는 id>` 가
   *    FastAPI 기본형 `{"detail":"Not Found"}` 를 주는데, 그 `detail` 이 화면까지 그대로
   *    흘러 **영문 「Not Found」가 작업 상세 자리에 떴다**. 화면 문구는 한글이다(규칙 7).
   * 🔴 `error_code` 가 «없을 때»만 쓴다 — 계약 봉투가 온 경우는 서버 한글 문구가 이긴다.
   *    상태 코드로 문구를 덮어쓰면 서버가 말해 준 까닭이 지워진다.
   */
  'error.http401': '로그인이 필요합니다',
  'error.http403': '권한이 없습니다',
  'error.http404': '없거나 지워진 항목입니다',
  /**
   * 🔴 서버의 일반 404 봉투는 `error_code` 를 싣는다(`{"error_code":"NOT_FOUND","message":"Not Found"}` · dev2 2026-10-11 실물).
   *    코드가 있으면 `error.http404` 로 안 떨어져 영문 「Not Found」가 그대로 화면에 올랐다 — 같은 문구로 막는다.
   */
  'error.NOT_FOUND': '없거나 지워진 항목입니다',
  'error.http500': '서버가 요청을 처리하지 못했습니다',
  'error.http502': '서버에 연결하지 못했습니다',
  'error.http503': '서버가 지금 응답할 수 없습니다',
  'error.http504': '서버 응답이 늦어 끊겼습니다',
  'error.INSUFFICIENT_ROLE': '권한이 없습니다',
  'error.CONTROL_LOCK_HELD':
    '다른 사용자가 제어 중입니다 — 반납을 기다리거나 관리자가 해제해야 합니다',
  /** `error_codes.yaml` `HANDOFF_RESOURCE_IN_USE`(v12.0.0 · 수동 이송 겹침 409) — 종전 `LOCK_STALE` 은 자원 락과 함께 사라졌다 */
  'error.HANDOFF_RESOURCE_IN_USE':
    '다른 이송이 그 로봇·자리를 쓰고 있습니다 — 격리면 물리 확인 뒤 [해제]로 풉니다',
  /** `error_codes.yaml` `STOPS_CHANGED` — 아무것도 풀지 않았다 · 목록을 다시 읽고 다시 확인한다 */
  'error.STOPS_CHANGED': '목록이 바뀌었습니다 — 다시 확인하세요',
  /** `error_codes.yaml` `MODULE_DELETE_REJECTED` message — 에러 코드 사전이라 남긴다(화면의 「모듈 지우기」는 2026-10-22 에 걷었다) */
  'error.MODULE_DELETE_REJECTED':
    '모듈 행을 지울 수 없습니다 — 서비스 종착 여부 · 열린 작업 · 실행을 확인하세요',
  'error.BATCH_COMMAND_NOT_ALLOWED': '지금 상태에서는 이 명령을 받을 수 없습니다',
  'error.SAFETY_NOT_CLEARED': '안전 해제 거부 — 물리 안전 조건이 아직 해소되지 않았습니다',
  'error.E_STOP': '비상 정지가 작동했습니다',
  'error.AUTH_FAILED': '인증에 실패했습니다 — 계정·비밀번호를 확인하세요',
  'error.ACCOUNT_LOCKED': '이 계정은 지금 로그인할 수 없습니다 — 관리자에게 문의하세요',
  'error.CURRENT_PASSWORD_MISMATCH': '현재 비밀번호가 일치하지 않습니다',
  'error.WEAK_PASSWORD': '비밀번호가 정책을 만족하지 않습니다',
  'error.REQUEST_VALIDATION_FAIL': '입력이 올바르지 않습니다',
  /* 카탈로그 17.1.3 문면 그대로(2026-10-22 재생성 · 종전 「레시피 검증에 실패했습니다」가 서버 새 문장을 가렸다) */
  'error.RECIPE_VALIDATION_FAIL': '레시피 값이 맞지 않습니다(범위·순서·시간)',
  'error.PREFLIGHT_BLOCK': '사전 점검을 통과하지 못했습니다',
  /*
   * 🔴 **아직 서버가 안 낸다 — 사전만 먼저 깐다**(2026-09-22 실측).
   *    정본 카탈로그 8.8.0 이 `SAMPLE_ALREADY_EXISTS`(409)를 세웠는데 실서버
   *    `ctcells-core@99d9524` 는 여전히 `IntegrityError → REQUEST_VALIDATION_FAIL`(422)로 낸다
   *    (`app/views/samples.py:148`). 실물 확인: 같은 `sample_id` 를 두 번 등록하면 두 번째가
   *    422 이고 화면이 「입력이 올바르지 않습니다」로 말한다 — **어느 칸이 틀렸다는 거짓**이다.
   *    ⚠ 이 줄은 **화면에 새 자리를 만들지 않는다.** 코드가 오는 날 문구만 바뀐다(BR 참조).
   * ⚠ 카탈로그 문구(`sample_id 중복 — 이미 등록된 시료다`)를 그대로 쓰지 않는다 —
   *   필드 원문을 노출하고 사람이 볼 말이 아니다.
   */
  'error.SAMPLE_ALREADY_EXISTS': '이미 등록된 시료 번호입니다 — 다른 번호로 등록하세요',
  /*
   * 카탈로그 문구가 `(409)` 를 문장 안에 달고 있다 — 상태 코드는 화면 말이 아니다.
   * 🔴 **재시도로 안 풀린다**(배포 구성 문제)는 뜻을 문구가 지고 있어야 한다.
   */
  'error.MODULE_DRIVER_NOT_WIRED':
    '이 장비에 드라이버가 연결되어 있지 않습니다 — 서비스 담당에게 알리세요',
  /*
   * 카탈로그 17.1.3 문면 그대로(2026-10-22 · 종전 「로터 불평형 (적재 비대칭 · 챔버 이탈 · 부하 편차)」가 서버 새 문장을 가렸다).
   * ⚠ 지우지 않는다 — 로그 이벤트 원장은 `message` 를 안 싣고 코드만 준다(`app/services/history.py`) — 이 사본이 그 자리 문구다
   */
  'error.CENT_IMBALANCE': '원심기 로터의 균형이 맞지 않습니다',
  'error.COMM_TIMEOUT': '통신 응답 시간 초과',
  /*
   * 단계 실패 사유. 🔴 **문구는 `error_codes.yaml` 의 `message` 를 그대로 옮긴 것**이다 —
   *    화면이 문장을 만들지 않는다(카탈로그가 문면의 권위다). 시료 흐름 격자가 칸에 쓴다.
   * ⚠ **2026-09-22 정정**: 이 코드를 싣는 쪽은 이제 **서버뿐**이다(`stageVerdicts.ts` —
   *   `GET /reports/{id}.events[].error_code`). 종전에 여기 적혀 있던 임시 목은 걷었다.
   *   서버가 그 칸을 채우기 시작하면 이 줄들이 **그대로** 쓰인다(사전은 임시가 아니다 · BR-49).
   */
  'error.ASM_TC_ALIGN_FAIL': 'TC 정렬 실패 — 수용부에 바르게 안착하지 않았습니다',
  'error.DISPENSE_CLOG': '디스펜서 막힘',
  'error.STAIN_SHAKE_FAIL': 'Shaker 목표 속도 미달 · 정지',

  // ── 리소스 · 시스템 화면 ──────────────────────────────────────────────────
  // 시료 · 레시피 · 랩웨어 · 모듈 · 로그 · 관리 · 로그인 · 내 계정이 쓰는 문구.
  // 출처는 와이어프레임 실물(samples 2410 · recipe 2077 · labware 2453 · modules 2615 ·
  // hist 2356 · settings 2647 · profile 2756 · loginView 2883)이다.

  // 시료
  'column.stage': '단계',
  'row.currentModule': '현재 모듈',
  'row.position': '위치',
  'row.discardReason': '폐기 사유',
  /*
   * 🔴 **두 자리 축을 낱말로 가른다**(2026-09-26 · `DM-REL-06`). 「현재 자리」는 장비가 옮길 때마다
   *    바뀌는 `current_position`, 「배정 웰」은 배치를 만들 때 정해져 안 바뀌는 `assigned_well` 이다.
   *    둘 다 「위치」라고 적으면 달라졌을 때 결함처럼 읽힌다 — 달라도 결함이 아니다.
   */
  'column.registeredAt': '등록 시각',
  'row.currentPosition': '현재 자리',
  'row.assignedWell': '배정 웰',
  /* 「배정 웰」 줄이 묶음의 웰 전부를 적을 때, 서버가 기록한 대표 웰(`assigned_well`) — 툴팁 */
  'row.assignedWell.server': '서버 배정 {well}',
  'row.sampleRef': '묶음',
  'card.placement': '자리',
  /** 모니터링 챔버 상세에서 「자리」 구역의 이름(eric 2026-10-06 「자리라고 하지 말고.. 위치라고」) */
  'card.location': '위치',
  /* ⚠ 「자리」 카드 아래 해설 문단(`note.placementAxes`)은 걷었다(eric 2026-09-29 「이런 텍스트 다 빼줘」). 되살리지 마라 */
  /* 시료 검색칸의 접근성 이름. 눈에 보이는 글자는 `filter.search.batchesPlaceholder`(「검색」 · eric 2026-09-30) */
  'filter.search.samples': '시료 번호 · 환자 번호 검색',
  'action.addSample': '＋ 시료 추가',
  /** 서랍 «보기»에서 정정 폼을 여는 명시 행위(eric 2026-09-27) */
  'action.correct': '정정',
  'action.saveCorrection': '정정 저장',
  'action.report': '보고서',
  'card.sample': '시료',
  'card.testInfo': '검사 정보',
  'card.correctionReason': '정정 사유',
  'card.operation': '운전',
  /** 시료 서랍 · 시료 추가 폼의 칸 — 「타입」(eric 2026-09-30). 목록 머리글은 `column.sampleType` */
  'field.sampleType': '타입',
  'field.rbc': '적혈구 농도',
  'field.drawDate': '채혈 날짜',
  'field.cancerType': '암종',
  'field.cancerStage': '암 병기',
  /** 시료 추가 표의 채혈 칸 — 날짜 + 시각(2026-10-04 eric 「채혈 날짜 (시간)」). 서랍 · 정정은 날짜만이라 `field.drawDate` */
  'field.drawAt': '채혈 일시',
  /** 날짜 · 시각 고르개(`DateTimePicker` · eric 2026-10-20 「달력 누르고 선택하게 해줘」) */
  'dateTime.placeholder': '날짜 · 시각 선택',
  'dateTime.prevMonth': '이전 달',
  'dateTime.nextMonth': '다음 달',
  'dateTime.monthTitle': '{year}년 {month}월',
  'dateTime.weekdays': '일,월,화,수,목,금,토',
  'dateTime.hour': '시',
  'dateTime.minute': '분',
  'dateTime.noTime': '시각 없음',
  /** 펼치기 전 값으로 되돌리고 닫는다 · 고른 값을 두고 닫는다(eric 2026-10-20 「지우기를 취소.. 닫기를 확인으로」) */
  'dateTime.cancel': '취소',
  'dateTime.confirm': '확인',
  'field.drawTime': '채혈 시각',
  /** 시료 추가의 사용자 추가 입력 — `metadata` 의 이름:값(eric 2026-10-04) */
  'field.sampleExtras': '추가 입력',
  'field.reason': '사유',
  /** 알람 해제 메모 — 서버 `AlarmAckEnvelope.note`(선택 · 감사 기록) */
  'field.alarmNote': '메모',
  /*
   * 시료 등록의 **번호 붙은 절** — Opentrons Labware Creator 의 폼 흐름을 옮긴 것이다
   * (`docs/opentrons-alignment.md` §6). 🔴 **보내는 칸은 `RegisterSampleBody` 의 7칸 그대로다** —
   *    절 이름에 기대어 계약에 없는 칸(챔버 · 담당자 · 메모)을 만들지 마라.
   *    2026-09-29 여러 줄로 바뀌며 절이 「번호 규칙 · 공통 값 · 시료 목록」이 됐다(종전 식별 · 검체 · 임상).
   *    2026-10-03 「번호 규칙」 절을 걷었다(eric) — 「공통 값 · 시료 목록」 둘이다.
   *    2026-10-04 입력 한 곳(eric 「입력이 여러 곳이 분산」) — 「시료 정보(입력 폼) · 담은 시료(목록)」 둘이다. 공통 값 절은 걷었다.
   * ⚠ `card.sample`·`card.testInfo` 는 **시료 «상세»가 아직 쓴다.** 지우지 마라.
   */
  'sec.sample.input': '시료 정보',
  /* 「담은 시료」 → 「추가된 시료」(eric 2026-10-13 「담은 시료 대신에 추가된 시료라고 이름 수정해줘」) */
  /* → 「시료 목록」(eric 2026-10-20 「시료 목록 이라고 타이틀 잡아주고.. 0건 이건 빼줘」) */
  'sec.sample.rows': '시료 목록',
  /** 시료 목록이 비었을 때 상자 안 한 줄(eric 2026-10-20 「0건 일 때... 시료를 추가하세요」) */
  'sampleNew.list.empty': '시료를 추가하세요',
  // 시료 목록 제목 줄 오른끝 — 시료 정보 팝업을 다시 연다(eric 2026-10-21 「시료 목록 타이틀 오른쪽에. +시료 추가 버튼」)
  'sampleNew.list.add': '＋ 시료 추가',
  /*
   * 정렬 축 — 🔴 **`GET /samples` 응답에 있는 값으로만 만든다.** 등록 시각(`created_at`)은
   *    2026-09-24 에 생겼다(실측 349/349) — 그래서 「최근 등록순」이 이제 선다.
   */
  /** 비운 선택 입력 칸(2026-10-01 CTCells 회의 「빈칸은 NA 처리」). 흐린 글자로 선다 — 경고가 아니다 */
  'value.na': 'NA',
  /** 🔴 값이 그대로면 서버가 감사행을 쓰지 않는다 — 「기록됐다」고 믿게 두지 않는다 */
  'reason.nothingChanged': '고친 칸이 없습니다',
  /** 🔴 가려진 환자 식별자는 고칠 수 없다 — 마스크 문자열이 진짜 값을 덮는다 */
  'reason.maskedValue': '가려진 값은 정정할 수 없습니다',
  'reason.sampleIdRequired': '시료 번호 필요',
  /*
   * 시료 추가 — 형식 도우미(eric 2026-09-29 「포멧을 좀더 쉽게 세팅하도록」).
   * 시료 번호는 사람이 적는다 — 번호 규칙(자동 채움)은 2026-10-03 걷었다(eric 「번호 규칙 빼라고 했잖아」).
   * 🔴 제안은 전부 **제안**이다 — 서버는 자유 문자열을 받는다(`sampleFormatHints.ts` 머리말).
   */
  'sampleNew.existing.checking': '등록된 번호 확인 중',
  /** 부가 조회(`GET /samples`) 실패 — 🔴 등록 화면은 선다. 「이미 있는 번호」 경고 · 제안만 빈다 */
  'sampleNew.existing.unread':
    '등록된 시료를 못 읽어 이미 있는 번호 · 제안을 확인하지 못했습니다 — {reason}',
  'sampleNew.drawDate.future': '오늘보다 뒤의 날짜입니다',
  /** 붙여 넣은 줄의 채혈 일시를 못 읽었다 — 목록 그 칸에 적는다 */
  'sampleNew.drawAt.format': 'YYYY-MM-DD 또는 YYYY-MM-DD HH:MM 으로 적습니다',
  'sampleNew.drawTime.placeholder': 'HH:MM',
  'sampleNew.drawTime.format': '시각은 HH:MM(24시간)으로 적습니다',
  'sampleNew.drawTime.noDate': '시각만 있고 날짜가 없습니다',
  /*
   * 추가 입력(metadata 이름:값 · eric 2026-10-04). 한 줄 = 왼쪽 이름 · 오른쪽 값, 아래 「+」로 한 줄씩(eric 2026-10-12 「예시는 빼고」)
   */
  /** 추가 입력 한 줄 더 — 무엇이 늘어나는지 말한다(eric 2026-10-20 「뭐를 추가하는지 구분이 안가」 · 종전 「+ 열 추가」) */
  'sampleNew.extras.add': '+ 추가 입력 추가',
  'sampleNew.extras.keyLabel': '{n}번째 항목 이름',
  'sampleNew.extras.keyPlaceholder': '이름',
  'sampleNew.extras.valueLabel': '{name} 값',
  'sampleNew.extras.valuePlaceholder': '값',
  'sampleNew.extras.remove': '{name} 항목 지우기',
  'sampleNew.extras.noKey': '이름 없는 값이 있습니다',
  'sampleNew.extras.noValue': '값 없는 항목이 있습니다 — 값을 적거나 지우세요',
  'sampleNew.extras.dupKey': '「{key}」 이름이 두 번 있습니다',
  'sampleNew.extras.reserved': '「{key}」는 화면이 쓰는 이름입니다',
  /*
   * 입력 폼의 값을 목록에 넣는 단추(종전 「저장」 → 「+ 추가」 → 「시료 추가 →」 · `sampleNew.form.put`)는 걷었다 — 폼은 팝업이고 그 바닥
   * [확인](`sampleNew.confirm`)이 그 일을 한다(eric 2026-10-21 「시료 정보 쪽.. 취소 확인 이렇게 버튼 만들고」)
   */
  'sampleNew.form.inList': '추가된 시료에 이미 있는 번호입니다',
  /* 담은 목록 — 읽기 전용, 줄마다 빼기([편집]은 걷었다 · eric 2026-10-20 「편집은 빼줘」) */
  'sampleNew.list.removeRow': '{name} 빼기',
  'sampleNew.list.removed': '「{id}」를 뺐습니다',
  'sampleNew.list.undo': '되돌리기',
  /** 폼에 엑셀 줄을 붙여 넣었을 때(`onFormPaste`) — 단추 · 붙여 넣을 칸은 걷었다(eric 2026-10-12) */
  'sampleNew.paste.added': '{count}줄을 추가했습니다',
  /** 보낼 줄 수. 등록된 줄은 세지 않는다 */
  'sampleNew.submit': '{count}건 추가',
  /** 시료 추가 창 바닥 주 버튼 — 추가된 시료 전체를 등록하는 최종 확인(eric 2026-10-13 「취소 그리고 확인 버튼으로」) */
  'sample.status.available': '사용 가능',
  'sampleNew.reregister': '시험용으로 다시 등록',
  'sampleNew.reregister.done': '{id} 로 다시 등록했습니다',
  'sampleNew.confirm': '확인',
  'sampleNew.col.result': '결과',
  'sampleNew.col.actions': '줄 동작',
  /** 번호 없는 줄(붙여넣기)의 이름 — 「2번 줄 편집」 */
  'sampleNew.row.label': '{n}번 줄',
  /** 목록 안에서 두 줄 이상이 같은 번호 — 칸 안이라 짧게 */
  'sampleNew.cell.dupInForm': '목록 안 중복',
  /** 고르거나 적는 칸의 펼침 단추 — 보조기술 이름 */
  'sampleNew.hints.open': '{field} 제안 목록',
  'sampleNew.hints.remove': '{value} 목록에서 빼기',
  /** 고르는 칸 맨 위 줄(eric 2026-10-12 「맨 처음에... + 적는 걸로」) — 목록에 없는 값을 적는 자리 */
  'sampleNew.create.empty': '+ 직접 입력',
  'sampleNew.create.typed': '+ 「{value}」 직접 입력',
  'sampleNew.row.ok': '등록됨',
  /** 🔴 BR-59 ② — 보낸 metadata 를 서버가 응답에 안 돌려줘 화면이 받은 값을 못 보인다 */
  'sampleNew.row.metadataUnseen': 'metadata 는 응답에 없음',
  /** 409 `SAMPLE_ALREADY_EXISTS` — 사유 전문은 번호 칸 밑에 선다 */
  'sampleNew.row.duplicate': '번호 중복',
  /**
   * 🔴 막는다(2026-10-04) — 시료는 지우지 않는 원장이라 `GET /samples` 에 있으면 정말 있다. 목록에 없는데
   *    서버에 있으면 409 가 그 줄에 선다(마지막 판정은 서버)
   */
  'sampleNew.row.exists': '이미 등록된 시료 번호입니다',
  'reason.registering': '등록 중',
  /** 담은 줄이 없다 — 보낼 것이 없다 */
  'reason.noSampleRows': '추가된 시료가 없습니다',
  'reason.sampleRegistered': '등록됨',
  'reason.sampleListErrors': '추가된 시료에 확인할 줄이 있음',
  /** 서버가 409 로 거절한 번호를 그대로 둔 줄 */
  'enum.sampleStage.registered': '등록',
  'enum.sampleStage.centrifuged': '원심',
  'enum.sampleStage.assembled': '어셈블',
  'enum.sampleStage.staining': '염색',
  /*
   * 모니터링 시료 관점의 «단계 카드» 첫 줄 이름(eric 2026-09-22 「원심을 원심분리기, 어셈블을 어셈블러,
   * 염색을 염색이라고」). 환자 카드의 단계 줄(등록·원심·어셈블·염색·완료)은 칸이 좁아 `enum.sampleStage.*`
   * 짧은 이름을 그대로 쓴다 — 두 자리가 다른 낱말을 쓰는 것은 의도다. 염색만 「염색기」가 아니다.
   */
  'stageCard.cent': '원심분리기',
  'stageCard.asm': '어셈블러',
  'stageCard.stain': '염색',
  'enum.sampleStage.completed': '완료',

  // 레시피
  'action.newRecipe': '＋ 레시피 추가',
  /*
   * 「＋ 레시피」 — 서버 표준 레시피를 불러온 채로 시작(종전 메뉴 · JSON 「표준 레시피에서」 창은 걷었다)(eric 2026-10-12 「바로 표준 레시피로 시작.. 빈 레시피 처럼 나오되.. 값들이 임포트 한 상태」)
   */
  'recipeNewStandard.changed': '고친 칸 {count}',
  'recipeNewStandard.badField': '값을 적으세요 — 숫자 칸은 0 이상의 정수',
  'recipeNewStandard.reagents': '시약',
  'recipeNewStandard.labware': '랩웨어',
  /** 시약 탭이 비었다(문서는 받았다 · 0건) — 랩웨어 탭은 `state.empty.recipeLabware` 를 같이 쓴다 */
  'recipeNewStandard.reagentsEmpty': '이 레시피는 시약을 두지 않습니다',
  /*
   * 레시피 추가 창 스텝 · 시약 표의 열 머리(eric 2026-10-13 「컬럼을 분리해줘」) — 나머지 열은 `recipeEdit.f.*` · `column.*` 를 같이 쓴다.
   * 모르는 키는 원래 키 이름이 머리다(지어내지 않는다)
   */
  'recipeStd.col.no': '#',
  'recipeStd.col.sequenceParams': '시퀀스 값',
  'recipeStd.col.shake': '셰이킹',
  'recipeStd.col.reagentNo': '번호',
  'recipeStd.col.description': '설명',
  'recipeStd.col.labwareId': '랩웨어 ID',
  'recipeStd.col.definitionUri': '정의',
  'recipeStd.col.tipTypeIndex': '팁 종류 번호',
  'recipeStd.col.pickOrder': '집는 차례',
  'recipeStd.col.metadataExt': '추가 정보',
  'recipeNewStandard.regenerateFailed': '고친 값으로 작업 명령을 다시 만들지 못했습니다 — {reason}',
  'recipeStandard.loading': '표준 레시피를 받는 중',
  /*
   * 새 형식(Opentrons protocol/8) 레시피 상세 요약 — 문서에 있는 값만(형식 · 문서 이름 · 명령 · 랩웨어 정의 · 액체).
   * 🔴 옛 형식 요약(절 · 스텝 · 원심/어셈/염색 수)을 그리면 «0»으로 «없다»처럼 읽힌다 — 새 형식에서는 그리지 않는다.
   */
  'recipeV2.format': '형식',
  'recipeV2.formatValue': 'Opentrons protocol/{version}',
  'recipeV2.protocolName': '문서 이름',
  'recipeV2.commands': '명령',
  'recipeV2.labwareDefinitions': '랩웨어 정의',
  'recipeV2.liquids': '액체',
  'recipeV2.commandsCell': '명령 {count}',
  'reason.v2NoCommands': '새 형식 문서에 명령(commands)이 없음',
  'recipeV2.note':
    '새 형식(Opentrons protocol) 레시피 — 절 · 단계 · 시약 탭은 옛 형식 전용이라 여기서는 요약만 보입니다',

  /*
   * 작업 준비 › 템플릿(2026-10-13 부터 작업 준비 탭). 🔴 2026-10-26 부터 «레시피» 템플릿(서버 표준 레시피 한 벌) 하나뿐이다
   * (eric 「응.. 다 빼줘」) — 데크 · 작업 · 랩웨어 · 시약 템플릿 화면 · 종류 칩 · 「＋ 템플릿 추가」의 문구는 걷었다.
   */
  'screen.templates.title': '템플릿',
  /*
   * 템플릿 종류 「레시피」(eric 2026-10-12 · 서버 표준 레시피(장비 전 구간) 한 벌). 「템플릿」 탭 안이라 「…템플릿」을 붙이지 않는다
   * (eric 2026-10-13 「레시피만 왜 레시피 템플릿이야? 랩웨어도 템플릿 있잖아.?」). 오른쪽 판 머리 · 판 접근 이름이 이 키다
   */
  'tab.template.recipe': '레시피',
  'action.recipeFromTemplate': '이 템플릿으로 레시피 만들기',
  'recipeTemplate.samples': '시료 자리',
  'recipeTemplate.commands': '명령 수',
  /** 레시피 템플릿의 작업 차례 표 — 줄 수(13)는 단계 수다. 템플릿 수(10)와 갈리니 「차례」를 붙였다(eric 2026-10-13 「작업이 10개인데.. 13개」) */
  'recipeTemplate.stageList': '작업 차례',
  /** 오른쪽 판 표 열(eric 2026-10-13 · 맨 글자 목록을 열 표로) — 염색 단계의 단계 번호(`s01`) · 시료 자리의 자리(`P1` · `REF`) */
  'recipeTemplate.col.stage': '단계',
  /** 작업 차례 「작업」 열 — 시약 + 모양 + 반복을 한 줄로(eric 2026-10-20 「이름이나.. 모양쪽.. 너무 이해하기 어려운데」) */
  'recipeTemplate.col.work': '작업',
  /** 서버 단계 모양 어휘(`fill` · `fill_shake` · `exchange` · `exchange_shake`)의 한글 */
  'recipeTemplate.shape.fill': '넣기',
  'recipeTemplate.shape.fill_shake': '넣기 · 흔들기',
  'recipeTemplate.shape.exchange': '교체',
  'recipeTemplate.shape.exchange_shake': '교체 · 흔들기',
  'recipeTemplate.col.sampleRef': '자리',
  'state.empty.templates': '템플릿 없음',
  'filter.search.templates': '이름 · ID 검색',
  'template.field.shape': '모양',
  'template.field.reagent_role': '시약 역할',
  /* 레시피 문서 안의 배치 표(랩웨어 · 시약) — 값은 문서 원값 글자 그대로(`recipeComposition.ts`) */
  'tplTable.location': '칸',
  /** 랩웨어 표 「칸」 — 데크 3열 냉장 자리(Opentrons 슬롯 B3 · 칸이 아니다 · eric 2026-10-20 「A3은 없어..」) */
  'tplTable.place.chilled': '냉장',
  'tplTable.labware': '랩웨어',
  'tplTable.labwareId': '자리',
  'tplTable.reagent': '시약',
  'tplTable.wellsVolume': '웰 · 양',
  'action.create': '만들기',
  'action.makeBatchFrom': '이 레시피로 작업 만들기',
  'action.readJson': '읽기',
  'action.pickFile': '파일 선택',
  'action.addChannel': '＋ 채널 추가',
  'action.removeChannel': '채널 삭제',
  'sort.label': '정렬',
  /*
   * 🔴 **`sort.recipe.recent`(「최근 수정」) · `sort.recipe.usage`(「작업 사용 많은 순」)를
   *    되살리지 마라**(2026-09-21 제거). 둘 다 **고를 수는 있는데 아무 일도 안 나는 죽은 옵션**
   *    이었다 — 목록 정렬이 그 둘에서 `return 0` 으로 떨어졌다.
   *    ⓐ 「수정」: `Recipe` 에 `updated_at` 이 «없다»(재커밋으로 version 이 오르는 모델이라
   *       그 축 자체가 없다). 이름이 틀렸을 뿐 아니라 셀 값도 없었다
   *    ⓑ 「작업 사용」: `usage_count` 가 2026-09-20 에 걷혔다(집계 경로 없음)
   *    ⇒ 「최근 등록」(`created_at`) 하나만 남긴다. 그 값은 실물이 준다.
   */
  'card.basicInfo': '기본 정보',
  'card.method': '만드는 방법',
  'card.recipePreview': '만들어질 레시피',
  'card.baseRecipe': '기반 레시피',
  'card.payloadJson': '본문 JSON',
  'card.summary': '요약',
  'card.history': '이력',
  'method.form': '값 직접 설정',
  'method.clone': '기존 레시피 복제',
  'method.json': 'JSON 가져오기',
  'field.name': '이름',
  'row.schemaVersion': '스키마',
  'row.sections': '절',
  'row.steps': '스텝',
  'row.reagents': '시약',
  'row.chambers': '챔버',
  'row.batchInputs': '작업 생성 시 입력',
  'row.method': '방법',
  'section.centrifuge': '원심',
  'section.assembler': '어셈블',
  'section.staining': '염색',
  'section.trayPath': '트레이 배치',
  'section.common': '공통',
  'field.speed': '회전 속도',
  'field.spinDuration': '회전 시간',
  'field.rounds': '회차',
  'field.uvDuration': 'UV 시간',
  'field.uvAngle': 'UV 각도',
  'field.loadTilt': '적재 기울기',
  'field.drainTilt': '배출 기울기',
  /*
   * 🔴 **`field.washCount`(「워싱 횟수」)를 걷었다**(2026-09-21). 그 칸이 `value="5"` 를
   *    박고 있었는데 실물 레시피의 `wash` 스텝은 **0건**이었다. 계약이 아는 「워싱 횟수」는
   *    `StainingStep.repetitions` 하나이고 **스텝마다** 붙는다 — 절 단위 대표값이 없다.
   *    되살리려면 스텝 표가 먼저다(`RecipeSections` 염색 카드 주석).
   */
  'field.tipChange': '팁 교체',
  'field.patientCount': '환자 수',
  'field.chambersPerPatient': '환자당 챔버',
  'field.referenceWell': 'Reference 웰',
  'field.recoveryDelay': '복구 허용 지연',
  'field.storageTemp': '보관 온도',
  'column.component': '성분',
  'column.lot': 'lot',
  'value.perBatchInput': '작업 생성 시 입력',

  /*
   * 레시피 상세 탭이 쓰는 열·카드 이름(2026-09-21 · Opentrons 정렬).
   * 🔴 **응답에 있는 값만 열이 된다.** Opentrons 파라미터 탭의 `기본값`·`범위`, 액체 탭의
   *    `색`·`설명`은 우리 실물에 «없어» 키조차 만들지 않았다. 나중에 생기면 API 가 먼저다.
   */
  'column.mandatory': '필수',
  /** 이 값이 들어갈 자리를 가리키는 점 경로(`reagents.5.lot`). 식별자라 번역하지 않는다 */
  'column.bindsTo': '연결 대상',
  'column.base': '기반',
  /** 🔴 자유 객체라 계약이 안을 보증하지 않는다 — 모르는 키를 «버리지 않고» 이 열에 편다 */
  'column.otherValues': '그 밖의 값',
  'column.totalVolume': '용량 (µL)',
  'column.sequence': '순서',
  'column.note': '비고',
  /** 알람 목록 · 서랍의 조건(latching 알람의 유발 조건 유지 · 해소) */
  'column.alarmCondition': '조건',
  /*
   * 🔴 **`column.waitSec` 를 걷었다**(2026-09-22). 염색 스텝 표의 열이었는데 실물 16건 ·
   *    스텝 1,136개 전수에 `wait_sec` 키가 0건이라 열 전체가 `—` 였다.
   *    되살리려면 그 값을 주는 레시피를 먼저 확인해라.
   *
   * 염색 스텝 표에 더한 다섯 — 🔴 **전부 실물이 주는 값이다**(2026-09-22 전수 실측).
   *   `tip_policy` 768/1136 · `liquid_class` 128 · `to_waste` 368 · `waste_stream` 368 · `shake` 368
   */
  'column.tipPolicy': '팁 정책',
  'column.liquidClass': '액체 클래스',
  'column.toWaste': '폐기',
  'column.wasteStream': '폐액 분류',
  'column.shake': '셰이킹',
  'value.yes': '예',
  'value.no': '아니오',
  'card.runtimeParameters': '작업 생성 때 받을 값',
  'card.recipeLabware': '레시피 랩웨어',
  'card.liquidClasses': '액체 클래스',
  'state.empty.runtimeParameters': '이 레시피는 작업 생성 때 받을 값을 두지 않습니다',
  'state.empty.recipeLabware': '이 레시피는 랩웨어를 두지 않습니다',
  'state.empty.liquidClasses': '이 레시피는 액체 클래스를 두지 않습니다',
  'state.empty.recipeSteps': '이 절에는 스텝이 없습니다',
  /** 🔴 「명령 불가」가 아니라 «자리가 다 찼다»다 — 종전에는 사유가 틀린 말이었다 */
  'reason.reagentChannelsFull': '시약 채널 8개를 모두 씀',
  // 🔴 **레시피 상세의 검증·드라이런 문구를 되살리지 마라**(2026-09-29 eric 「검증, 드라이런 이거 빼」 —
  //    버튼 · 결과 카드 · 판정 · 예상 소요 · 팁 수 문구를 전부 걷었다). 그 전(2026-09-20)에는 집계 문구
  //    (`label.validationRuns` · `label.dryRuns` · `label.lastRun`)가 fixture 가 지어낸 수로 서 있었다.
  //    ⚠ `label.statusChange` 도 같이 뺐다 — 상태 드롭다운이 사라졌다(전이 경로가 보관 하나뿐).
  'state.empty.recipeList': '레시피 없음',
  'screen.recipeArchive.title': '레시피 보관',
  'reason.committedOnly': '확정된 레시피만',
  'reason.draftOnly': '작성 중에서만',
  // 🔴 「보관」이 막히는 까닭 둘을 가른다 — `draft` 는 「아직 확정 전」, `archived` 는 「이미 했다」다.
  //    한 문구로 묶으면 보관된 레시피에서 「확정된 레시피만」이라는 틀린 말이 뜬다(2026-09-21)
  'reason.recipeAlreadyArchived': '이미 보관된 레시피입니다',
  // 「이 레시피로 작업 만들기」가 막히는 까닭도 상태마다 다르다
  'reason.archivedNoBatch': '보관된 레시피로는 작업을 만들 수 없습니다',
  'reason.draftNoBatch': '확정 전 레시피로는 작업을 만들 수 없습니다',
  // 🔴 값이 흐린 것이 「기본값 안내」가 아니라 「고칠 수 없는 값」임을 화면이 한 번 말한다
  'notice.recipeReadOnly': '읽기 전용 — 확정된 레시피는 고칠 수 없습니다',
  // 「값 직접 설정」이 막히는 까닭 — 칸 목록은 편집기 옆 「만들기 전에 고칠 것」이 편다
  // 「값 직접 설정」은 이름을 빌려 올 곳(기반 레시피 · JSON 문서)이 없다 — 서버 `name` 은 필수다
  'error.recipeJsonInvalid': 'JSON 형식이 아닙니다',
  'error.recipeJsonNotObject': '본문이 객체가 아닙니다',
  'error.recipeJsonMissingSections': '필수 절이 없습니다 — {sections}',
  // 플랫폼 포트(`platform.files.pickText`)가 읽기에 실패했을 때. **취소는 여기 오지 않는다**
  'error.fileReadFailed': '파일을 읽지 못했습니다',

  /*
   * 레시피 「값 직접 설정」 편집기(2026-09-29 되살림). 칸은 `recipe.schema.json` v0.9.1 이 정한다 —
   * 괄호 안 단위는 스키마 설명의 단위 그대로다. 열거 값(`dispense`·`per_sample` …)은 번역하지 않는다.
   */
  'recipeEdit.card.template': '출발 틀',
  'recipeEdit.template.label': '출발 틀 레시피',
  'recipeEdit.template.toggle': '출발 틀 레시피 목록 열기',
  'recipeEdit.action.load': '불러오기',
  'recipeEdit.action.clear': '비우기',
  'recipeEdit.action.defaults': '기본값',
  'recipeEdit.template.none': '불러올 레시피를 고르세요',
  'recipeEdit.extraKeys': '스키마에 없는 칸 {count}곳 — 이대로 만들면 서버가 거부합니다',
  'recipeEdit.optional': '선택 칸',
  'recipeEdit.groups': '레시피 절',
  'recipeEdit.filled': '{count}곳 적힘',
  'recipeEdit.empty': '비어 있음',
  'recipeEdit.jsonFilled': '적힘 · {lines}줄',
  'recipeEdit.jsonGroup': 'JSON 절',
  'recipeEdit.issueMark': '고칠 것 {count}곳',
  'recipeEdit.noStepsYet': '스텝이 없습니다',
  'recipeEdit.steps': '스텝',
  'recipeEdit.moduleSteps': '모듈 스텝',
  'recipeEdit.stepN': '스텝 {n}',
  'recipeEdit.action.addStep': '＋ 스텝 추가',
  'recipeEdit.action.removeStep': '삭제',
  'recipeEdit.action.moveUp': '위로',
  'recipeEdit.action.moveDown': '아래로',
  'recipeEdit.action.expand': '펼치기',
  'recipeEdit.action.collapse': '접기',
  'recipeEdit.reason.firstStep': '맨 위 스텝입니다',
  'recipeEdit.reason.lastStep': '맨 아래 스텝입니다',
  'recipeEdit.card.otherSections': '그 밖의 절',
  'recipeEdit.json.reagents': '시약 (JSON)',
  'recipeEdit.json.labware': '랩웨어 (JSON)',
  'recipeEdit.json.trayPath': '트레이 배치 (JSON)',
  'recipeEdit.json.liquidClasses': '액체 클래스 (JSON)',
  'recipeEdit.json.runtimeParameters': '작업 생성 때 받을 값 (JSON)',
  'recipeEdit.card.issues': '만들기 전에 고칠 것',
  'recipeEdit.issuesMore': '외 {count}곳',
  'recipeEdit.issue.required': '{where} — 값을 적어야 합니다',
  'recipeEdit.issue.number': '{where} — 숫자가 아닙니다',
  'recipeEdit.issue.integer': '{where} — 정수여야 합니다',
  'recipeEdit.issue.min': '{where} — {min} 이상이어야 합니다',
  'recipeEdit.issue.max': '{where} — {max} 이하여야 합니다',
  'recipeEdit.issue.option': '{where} — 목록에 있는 값이어야 합니다',
  'recipeEdit.issue.retired': '{where} — 은퇴한 이름입니다. {to} 를 쓰세요',
  'recipeEdit.issue.duplicate': '{where} — 같은 값이 두 번 있습니다',
  'recipeEdit.issue.json': '{where} — JSON 형식이 아닙니다',
  'recipeEdit.issue.shapeObject': '{where} — 객체({ })여야 합니다',
  'recipeEdit.issue.shapeArray': '{where} — 배열([ ])이어야 합니다',
  'recipeEdit.issue.noSteps': '{where} — 스텝이 하나 이상 있어야 합니다',
  'recipeEdit.issue.location': '{where} — 자리 종류나 지점 중 하나를 적어야 합니다',
  'recipeEdit.card.violations': '서버가 거부한 까닭 {count}건',
  'recipeEdit.violationsMore': '외 {count}건',
  'recipeEdit.bool.label': '{field} 선택',
  'recipeEdit.options.toggle': '{field} 목록 열기',
  'recipeEdit.f.speedRpm': '회전 속도 (rpm)',
  'recipeEdit.f.durationSec': '회전 시간 (s)',
  'recipeEdit.f.temperature': '온도 (℃)',
  'recipeEdit.f.uvDurationSec': 'UV 시간 (s)',
  'recipeEdit.f.uvIntensity': 'UV 세기',
  'recipeEdit.f.uvTilt': 'UV 각도 (°)',
  'recipeEdit.f.loadTilt': '적재 기울기 (°)',
  'recipeEdit.f.drainAngle': '배출 기울기 (°)',
  'recipeEdit.f.zDescend': 'Z 하강 속도 (%)',
  'recipeEdit.f.zHold': 'Z 유지 시간 (s)',
  'recipeEdit.f.defaultTip': '기본 팁 정책',
  'recipeEdit.f.defaultLiquid': '기본 액체 클래스',
  'recipeEdit.f.storageTemp': '시약 보관 온도 (℃)',
  'recipeEdit.f.maxDelay': '복구 허용 지연 (s)',
  'recipeEdit.f.stepId': '스텝 ID',
  'recipeEdit.f.sequence': '시퀀스',
  'recipeEdit.f.round': '회차',
  'recipeEdit.f.chambers': '챔버',
  'recipeEdit.f.payloadKind': '옮기는 것',
  'recipeEdit.f.source': '출발 자리',
  'recipeEdit.f.target': '도착 자리',
  'recipeEdit.f.sourceKind': '출발 자리 종류',
  'recipeEdit.f.sourceSlot': '출발 칸',
  'recipeEdit.f.sourcePoint': '출발 지점',
  'recipeEdit.f.sourceWaste': '출발 폐기통',
  'recipeEdit.f.targetKind': '도착 자리 종류',
  'recipeEdit.f.targetSlot': '도착 칸',
  'recipeEdit.f.targetPoint': '도착 지점',
  'recipeEdit.f.targetWaste': '도착 폐기통',
  'recipeEdit.f.pointId': '지점',
  'recipeEdit.f.bodyPosition': '본 공정 기준 위치',
  'recipeEdit.f.note': '비고',
  'recipeEdit.f.params': '파라미터 (JSON)',
  'recipeEdit.f.operation': '동작',
  'recipeEdit.f.targetWells': '대상 웰',
  'recipeEdit.f.reagentChannel': '시약 채널',
  'recipeEdit.f.volumeUl': '부피 (µL)',
  /** 시약 칸마다 흡입하지 않고 남기는 양(레시피 `reagents.<id>.dead_volume_ul` · eric 2026-10-20 「사용자가 입력하는 부분.. 넣어줘봐」) */
  /** 작업 차례 표 — 액체 클래스가 빈 단계는 염색기 기본 액체 클래스를 쓴다(eric 2026-10-20) */
  'recipeEdit.defaultLiquidCell': '{name} (기본)',
  'recipeEdit.f.deadVolumeUl': '남김 (µL)',
  'recipeEdit.f.waitSec': '대기 (s)',
  'recipeEdit.f.shakeRpm': '셰이킹 속도 (rpm)',
  'recipeEdit.f.shakeSec': '셰이킹 시간 (s)',
  'recipeEdit.f.mixRepetitions': '혼합 횟수',
  'recipeEdit.f.mixVolume': '혼합 부피 (µL)',
  'recipeEdit.f.repetitions': '반복 횟수',
  'recipeEdit.f.tipPolicy': '팁 정책',
  'recipeEdit.f.liquidClass': '액체 클래스',
  'recipeEdit.f.flowRate': '유속 (µL/s)',
  'recipeEdit.f.toWaste': '폐기로 보냄',
  'recipeEdit.f.wasteStream': '폐액 분류',
  'recipeEdit.f.touchTip': '팁 터치',
  'recipeEdit.f.airGap': '에어 갭 (µL)',
  'recipeEdit.f.pushOut': '푸시 아웃 (µL)',
  'recipeEdit.f.maxStepSec': '스텝 제한 시간 (s)',

  'action.archive': '보관',
  'action.restore': '복원',
  'card.usedByRecipes': '사용 레시피',
  'field.layoutDetail': '상세 수준',
  'field.grid': '격자',
  'field.defaultVolume': '기본 용량',
  'field.tipLength': '팁 길이',
  'field.dimensions': '외형 치수',
  'field.cornerOffset': '코너 오프셋',

  'label.defId': '정의 ID',

  /** 🔴 서버에 그 경로가 없다 — 핸들러 없는 버튼을 그리는 대신 사유를 붙인다 */
  'reason.noServerRoute': '서버가 아직 받지 않음',
  'reason.pickedAbove': '위에서 고릅니다',
  // 자리(location)는 positions.yaml 기준점 ID 다. 점을 밑줄로 바꿔 마지막 세그먼트 하나로 접는다
  'location.staining_tray': '염색기 · 트레이 자리',
  'location.staining_reagent_bay_ambient': '염색기 · 상온 시약 자리',
  'location.staining_reagent_bay_chilled': '염색기 · 냉장 시약 자리',
  'location.staining_tip_bay_1': '염색기 · 팁 랙 1',
  'location.staining_tip_bay_2': '염색기 · 팁 랙 2',
  'location.centrifuge_tray': '원심분리기 · 트레이 자리',
  'location.assembler_tray': '어셈블러 · 트레이 자리',
  'location.assembler_tc_rack': '어셈블러 · TC 랙',

  // 모듈
  'module.connected': '연결',
  /* `ModuleSnapshot.e_stop` — HW E-Stop 물리 상태 미러다. 시스템 안전 상태(`safety.*`)와 다른 축이다 */
  'module.eStopEngaged': 'E-STOP 눌림',
  /*
   * 모듈 초기화 중계(`IFC-RO-44` · eric 2026-09-28 「모듈 초기화 버튼 만들어줘」). 모듈 서랍의 「제어」 카드.
   * 🔴 사유는 서버 거절과 한 짝이다 — 단독 모드 = 409 `MODULE_LOCAL_MODE`. 도는 작업 사유는 걷었다(v0.9.1 · 서버가 배치 중에도 받는다).
   */
  'card.moduleControl': '제어',
  'action.initializeModule': '초기화',
  'moduleInit.confirmTitle': '모듈 초기화',
  'moduleInit.confirmBody':
    '{module}을(를) 초기화합니다. 진행 중인 실행이 있으면 끊기고 원점으로 돌아갑니다.',
  'moduleInit.advisory': '모듈이 허용 명령을 안 보냈습니다 — 서버 판정에 맡깁니다',
  'moduleInit.accepted': '초기화 요청을 모듈이 받았습니다 — 상태는 구독으로 따라옵니다',
  'moduleInit.occupied': '점유 중인 자리',
  /*
   * 「제어」 카드 줄(eric 2026-10-22 「깔끔하게 추가해줘」) — 상태 · 파지 기억(`IFC-RO-53`) · 자동 복구 잠김(`IFC-RO-31`) · 진단(`IFC-RO-48`).
   * 🔴 파지 기억 · 복구 잠김은 서버가 `reason` 을 필수로 받는다(감사) — 창이 사유를 받는다. 진단은 상태를 바꾸지 않아 창이 없다.
   */
  /* 「제어」 카드 단추의 툴팁 설명(eric 2026-10-22 「마우스 호버를 하면 해당 설명이 보이게」) — 서버 summary · 정본 용어로 */
  'moduleCtl.desc.initialize': '모듈을 원점으로 되돌립니다',
  'moduleCtl.desc.forgetGrips': '«쥐고 있다» 기록만 지웁니다 · 가동부는 그대로',
  'moduleCtl.desc.resetRecovery': '자동 복구 잠김을 사람이 풉니다',
  'moduleCtl.desc.diagnostics': '안전 · 재료 정위치를 점검합니다',
  'moduleCtl.desc.clearHandoff': '작업을 시작할 수 있는 상태로 되돌립니다',
  'moduleCtl.results': '최근 결과',
  'action.forgetGrips': '파지 기억 초기화',
  'action.resetRecoveryLock': '복구 잠김 풀기',
  'moduleCtl.forgetGrips.confirmBody':
    '{module}의 «쥐고 있다» 기록만 지웁니다. 가동부는 움직이지 않습니다 — 파지물을 비운 것을 눈으로 확인한 뒤에만 하세요.',
  'moduleCtl.resetRecovery.confirmBody':
    '{module}의 자동 복구 잠김을 풉니다. 잠김을 부른 원인을 확인한 뒤에만 하세요.',
  'moduleCtl.done': '받았습니다 — 상태는 구독으로 따라옵니다',
  'moduleCtl.diagPassed': '진단 통과',
  'moduleCtl.diagFailed': '진단 실패',
  'enum.diagnostic.skipped': '해당 없음',
  'enum.diagnostic.unknown': '판정 불가',
  /* `ModuleSnapshot.interlock.doors[]` — 문 상태(센서 우선 판정) · 구동부 상태. 문 이름은 `monitoring.robotLine.door.*` 를 쓴다 */
  'enum.doorState.open': '열림',
  'enum.doorState.closed': '닫힘',
  'enum.doorDrive.opening': '여는 중',
  'enum.doorDrive.open': '열림',
  'enum.doorDrive.closing': '닫는 중',
  'enum.doorDrive.closed': '닫힘',
  'enum.doorDrive.fault': '구동 이상',
  'reason.moduleLocalMode': '단독 모드 — 모듈 로컬 화면이 제어 중',
  'card.connection': '연결',
  /*
   * 모듈 상세 — 2026-09-26 새 응답 칸(`ModuleSnapshot.model` · `fw_version`). 미러 넷 구성 요소 카드는 2026-10-22 에 걷었다.
   * 🔴 셰이커 래치 6상태를 **접지 않는다** — `idle_unknown`·`unknown` 은 「닫힘」이 아니다(`FR-LH-19`).
   */
  'row.model': '모델',
  'row.fwVersion': '펌웨어',
  /** 모듈이 그 미러를 안 보냈을 때(`null`) — 모니터링 셰이커 칸(`shakerModel.ts`) */
  'value.notPublished': '값 없음',
  'enum.shakerLatch.opening': '여는 중',
  'enum.shakerLatch.idle_open': '열림',
  'enum.shakerLatch.closing': '닫는 중',
  'enum.shakerLatch.idle_closed': '닫힘',
  'enum.shakerLatch.idle_unknown': '멈춤 · 열림 여부 모름',
  'enum.shakerLatch.unknown': '모름',
  'card.recentDiagnostics': '최근 진단',
  'card.connectionHistory': '연결 이력',
  'row.lastSeen': '마지막 응답',
  'row.discovered': '발견',
  'row.uptime': '가동',
  'column.date': '일자',
  'column.event2': '사건',
  'label.calibrationExpiring': '만료 임박',
  'label.nextDue': '다음 {date}',
  'state.empty.calibrations': '보정 항목 없음',

  // 보정 기록(`POST /calibrations`)
  'action.recordCalibration': '＋ 보정 기록',
  'screen.calibrationRecord.title': '보정 기록 추가',
  'field.calibrationType': '보정 종류',
  'field.performedAt': '실시 시각',
  /** ⚠ 「다음 권장」(`column.nextDue`)과 **다른 칸**이다 — 이쪽은 계약의 `expires_at` 이다 */
  'field.expiresAt': '유효기한',
  /** 🔴 「실패」가 아니다. 판정을 **안 적는다**는 뜻이고 본문에 `passed` 키가 없다 */
  'value.noVerdict': '미판정',
  'reason.calibrationTypeRequired': '보정 종류 필요',
  'reason.performedAtRequired': '실시 시각 필요',
  'reason.performedAtFuture': '실시 시각이 미래입니다',
  'reason.expiresAtInvalid': '유효기한을 읽을 수 없습니다',

  'state.empty.diagnostics': '진단 기록 없음',
  'state.empty.logs': '기록 없음',
  'reason.diagnosticsNoRoute': '진단 실행 경로 없음',
  'enum.diagnostic.pending': '대기',
  'enum.diagnostic.running': '검사 중',
  'enum.diagnostic.pass': '통과',
  'enum.diagnostic.fail': '실패',
  'diag.comm_latency': '통신 · 응답 시간',
  'diag.power_interlock': '전원 · 인터락',
  'diag.door_sensor': '도어 센서',
  'diag.axis_homing': '축 원점 복귀',
  'diag.drive_torque': '구동부 토크',
  'diag.temperature_sensor': '온도 센서',
  'diag.vision_camera': '비전 카메라',
  'diag.driver_revision': '드라이버 판본',
  'field.ipAddress': 'IP 주소',

  // 로그
  'enum.historyType.audit': '감사',
  'enum.historyType.command': '명령',
  'enum.historyType.event': '이벤트',
  /* 2026-10-08(v0.9.4) 배치 기록이 `event_log` 에서 뗀 원장(`batch_record`) — 화면은 이벤트와 한 목록에 섞는다 */
  'enum.historyType.batch_record': '작업 기록',
  'enum.auditAction.safety_stop': '안전정지',
  'enum.auditAction.ack': '알람 확인',
  'enum.auditAction.start_warn_ack': '경고 인지 후 시작',
  'enum.auditAction.recipe_edit': '레시피 편집',
  'enum.auditAction.role_change': '역할 변경',
  'enum.auditAction.user_create': '계정 생성',
  'enum.auditAction.chain_verify': '원장 검증',

  // 관리
  'action.addUser': '＋ 사용자 추가',
  'action.changeRole': '역할 변경',
  'action.resetPassword': '비밀번호 재설정',
  'action.lock': '잠금',
  'action.unlock': '잠금 해제',
  'action.deactivate': '비활성',
  /*
   * 사용자 상세 서랍 「계정 설정」 카드(eric 2026-09-28 「사용자 --> 설정 컨셉대로.. 하나의 row 에다가 배치해줘」).
   * 왼쪽 줄 이름 · 오른쪽 조작. 줄 이름이 「비밀번호」라 단추는 「재설정」 두 글자다.
   */
  'card.accountSettings': '계정 설정',
  'row.accountStatus': '계정 상태',
  'action.reset': '재설정',
  /** ⚠ 재활성 경로가 계약에 0건이다 — 단추는 서되 비활성 + 사유(`reason.noReactivateRoute`) */
  'action.activate': '활성',
  'reason.noReactivateRoute': '재활성 경로 없음',
  'reason.selfDeactivate': '자기 계정은 비활성할 수 없음',
  /** 🔴 계약 필수값이다 — `IrreversibleCommandEnvelope.reason`(minLength 1) */
  /** 시료 처분 창(`DispositionModal`) — 종착 아닌 시료마다 하나를 다 골라야 [처분 확정]이 켜진다 */
  /** 자기 계정에 하면 안 되는 것들. 집행은 서버지만 눌러 보고 막히게 두지 않는다 */
  'reason.selfLock': '자기 계정은 잠글 수 없음',
  'reason.selfPassword': '내 계정은 설정에서 바꿈',
  'reason.selfSession': '이 기기 세션은 끊을 수 없음',
  'reason.actionInFlight': '요청 중',
  /**
   * 🔴 비활성은 되돌릴 수 없다 — 계약에 재활성 경로가 0건이다(정본·실서버 둘 다).
   *    잠금(되돌릴 수 있다)과 같은 화면에 있어서 확인 대화가 이 사실을 값으로 내보인다.
   */
  'row.reversible': '되돌리기',
  'value.irreversible': '불가',
  'action.endSession': '강제 종료',
  'action.endOtherSessions': '다른 세션 모두 종료',
  'action.openAuditLog': '로그 › 감사 열기 →',
  'setting.section.ops': '운영',
  /*
   * 🔴 **실행 단추만 모은 구획**(2026-09-22 · eric 「관리쪽 설정 탭 상세가 너무 지저분해」).
   *    「배치 준비 강제 정리」는 값이 아니라 연산이라 설정 줄 사이에 끼우지 않는다.
   *    (「시스템 종료」는 onClick 없는 죽은 단추라 2026-09-28 에 걷었다 — `GeneralSettings.tsx` 머리말)
   * ⚠ 「작업」이라고 부르지 마라 — 이 앱에서 「작업」은 **배치**를 가리키는 정본 어휘다.
   */
  'setting.section.actions': '시스템 조작',
  /*
   * 🔴 **`setting.noSettingsApi` · `setting.noValueFromServer` 를 되살리지 마라**(2026-09-22 삭제).
   *    eric 원문 「안 알려주는 건 그냥 — 로 표현하고 넘어가」 — 값이 없는 줄에는 사유를 적지 않고
   *    「—」만 둔다. 설정 조회·저장 경로가 없다는 «사실»은 `GeneralSettings.tsx` 머리말에 있다
   *    (확인 방법 · 날짜 · `docs/backend-requests.csv` BR-86 까지). 화면 문구가 아니라 주석이 그것을 기억한다.
   */
  /*
   * ⚠ 서버에 없는 설정 17줄(계정·비밀번호 · 세션 · 제어락 · 알림 임계 · 기록)의 키는 2026-09-28 에
   *   구획째 지웠다(eric 「정할 수 없으면 내용 다 빼버려」). 되살리지 마라 — `GeneralSettings.tsx` 머리말.
   */
  'setting.mode': '운영 모드',
  /*
   * ── 배치 준비 강제 정리(`POST /modules/prepare` · 2026-09-22) ─────────────────────
   * 🔴 **이름을 짓지 않았다.** 계약 summary 가 「배치 준비 강제 정리 (선행 AH-26)」이고
   *    실서버 `openapi.json` 도 같은 말을 쓴다. 화면 어휘를 여기서 새로 만들지 마라.
   * ⚠ 「초기화」라고 부르지 마라 — 모듈 `/initialize` 는 이 연산의 **다섯 단계 중 하나**일 뿐이고,
   *   사람이 그 말만 보면 배치가 닫힌다는 것을 모른다.
   */
  'setting.prepareModules': '배치 준비 강제 정리',
  'action.prepareModules': '정리 실행',
  /**
   * 🔴 **실측 문면이다**(2026-09-22). 「비종착 배치를 전부」라는 말만으로는 **아직 시작도 안 한
   *    작업**이 같이 닫히는 것을 사람이 모른다 — 그 한 줄이 이 대화의 존재 이유다.
   */
  'prepare.residualRisk':
    '멈춘 작업이 폐기됩니다. 진행 중인 작업과 아직 시작하지 않은 대기 작업도 모두 닫힙니다. 닫힌 작업은 다시 열 수 없고 그 시료는 다시 쓸 수 없습니다.',
  /* 서버 ③ — 모듈마다 `/handoff/prepare` 로 찔러 차 있으면 `/handoff/confirm role=pick` 으로 푼다 */
  'prepare.clearsHandoff': '모든 모듈의 트레이 자리 기록을 비웁니다.',
  /*
   * 🔴 종착 안 한 작업이 하나라도 있으면 버튼을 막는다(2026-09-28) — 서버가 그 작업을 전부 종착시킨다.
   *    못 읽었거나 읽는 중이어도 막는다 — 「못 봤다」를 「0건」으로 접지 않는다.
   */
  'reason.prepareActiveBatches':
    '진행 중이거나 대기 중인 작업이 있습니다 — 먼저 중지/폐기하세요: {names}',
  'reason.prepareBatchesLoading': '작업 목록을 읽는 중입니다',
  'reason.prepareBatchesUnknown': '작업 목록을 못 읽어 판정할 수 없습니다 — {message}',
  /* 모듈 서랍 「제어」 카드의 같은 연산(eric 2026-09-28 「넣어줘」) — 트레이 자리 잔재를 푸는 것이 이 자리의 쓸모다 */
  'action.clearHandoff': '트레이 자리 정리',
  'prepare.drawerResult': '트레이 자리 정리 결과',
  'row.prepareBatches': '지금 닫힐 작업',
  'row.prepareModules': '되돌릴 모듈',
  'row.prepareClosed': '닫은 작업',
  'row.prepareReady': '준비 상태',
  /*
   * ⚠ `ready: false` 는 **실패가 아니다** — 200 이고 무엇이 남았는지가 본문에 있다(계약 원문).
   *   그래서 「실패」가 아니라 「아직 시작할 수 없음」이다.
   */
  'value.prepareReady': '작업을 시작할 수 있음',
  'value.prepareNotReady': '아직 시작할 수 없음',
  'state.empty.activeBatches': '진행 중인 작업 없음',
  'enum.systemMode.normal': '정상',
  'enum.systemMode.maintenance': '점검',
  'enum.systemMode.calibration': '보정',
  'enum.systemMode.simulation': '시뮬레이션',
  /*
   * 🔴 **고를 수 없는 두 값이다 — 그래도 사전에 있다**(2026-09-22). 서버는 행이 없으면
   *    `startup` 을 주고(계약 원문 「행이 없으면 startup 이다 — 404 가 아니다」),
   *    `shutdown` 은 별도 조작이라 선택기에 안 세운다. 사전에서 빼면 그 상태가 원값으로 샌다.
   */
  'enum.systemMode.startup': '기동 중',
  'enum.systemMode.shutdown': '종료',
  'enum.pwComplexity.low': '하',
  'enum.pwComplexity.mid': '중',
  'enum.pwComplexity.high': '상',
  'enum.userStatus.active': '활성',
  'enum.userStatus.locked': '잠김',
  'enum.userStatus.disabled': '비활성',
  'unit.chars': '자',
  'unit.times': '회',
  'unit.count': '개',
  'unit.days': '일',
  'unit.minutes': '분',
  'unit.hours': '시간',
  'unit.seconds': '초',
  'unit.percent': '%',
  'unit.years': '년',
  'card.account': '계정',
  'card.activeSessions': '활성 세션',
  'card.recentAudit': '최근 감사',
  'row.created': '생성',
  'row.lastLogin': '마지막 로그인',
  'row.failCount': '실패 누적',
  'column.device': '기기',
  'column.loggedInAt': '로그인',
  'value.currentSession': '현재',
  'value.thisDevice': '이 기기',
  /* 「내 계정」 세션 표의 한 줄은 기기다 — 같은 기기에서 여러 번 로그인했으면 그 수를 적는다(2026-09-22) */
  'field.accountId': '계정 ID',
  'field.userName': '이름',
  'field.password': '비밀번호',
  'field.passwordConfirm': '비밀번호 확인',
  'field.email': '이메일 (선택)',
  'field.role': '역할',
  'field.currentPassword': '현재 비밀번호',
  'field.newPassword': '새 비밀번호',
  'field.newPasswordConfirm': '새 비밀번호 확인',
  /* 「내 계정」 비밀번호 줄 + 변경 창(eric 2026-09-23 「변경 누르면 비밀번호 변경하는 창 뜨게 해줘」) */
  'screen.passwordChange.title': '비밀번호 변경',
  'value.passwordChangedAt': '변경됨 {time}',
  'section.sessions': '세션',
  /* 「내 계정」의 세션 표 이름 — 종료 단추를 걷고 읽기만 하는 표가 됐다(eric 2026-09-22 「로그인 정보로 바꿔야」) */
  'section.loginInfo': '로그인 정보',
  'section.preferences': '개인 설정',
  'setting.language': '언어',
  'locale.ko': '한국어',
  /** 언어 이름은 어느 언어에서든 자기 말로 적는다 */
  'locale.en': 'English',
  'state.empty.audit': '기록 없음',
  'state.empty.users': '사용자 없음',
  'reason.currentSession': '지금 쓰는 세션',
  'reason.onlySession': '다른 세션 없음',
  'validation.password.mismatch': '비밀번호가 일치하지 않습니다',
  /*
   * 로그인 빈 칸 — 🔴 **비활성 «사유»가 아니라 제출 뒤의 «결과»다**(eric 2026-09-23
   * 「포커싱이 안 된 상태에서도 로그인 버튼이 테마색을 유지하면서 클릭 가능하도록 해줘」).
   * 종전에는 `reason.loginIncomplete` 로 버튼을 흐리게 두었는데, 브라우저 자동완성이 값을
   * 채워도 React 상태가 비어 있어 **칸을 건드리기 전까지 버튼이 죽어 있었다.**
   * ⇒ 버튼은 늘 살아 있고, 정말로 비었을 때만 그 칸 아래에 이 한 줄이 선다.
   */
  'validation.login.usernameRequired': '아이디를 입력하세요',
  'validation.login.passwordRequired': '비밀번호를 입력하세요',

  /*
   * ── 제출 검증 — 칸 아래 안내(eric 2026-10-27 「시료 번호 필요 이거 빼줘... 확인 누르면.. 빠진 부분.. 채우라고」) ──
   * 🔴 **비활성 «사유»가 아니라 제출 뒤의 «안내»다.** 확인 · 추가 · 저장은 살아 있고, 누르면 빠진 · 틀린 칸에만
   *    이 문구가 그 칸 아래 선다(`useSubmitValidation`). 그래서 「~ 필요」 명사형이 아니라 **할 일을 말하는 문장**이다.
   * ⚠ 권한 · 제어락 · stale · 서버 상태 · 전송 중 · 물리 확인 · 사전 점검 경고 확인 · 「바뀐 것 없음」은
   *    여기 오지 않는다 — 종전 `reason.*` 비활성 사유 그대로다.
   * ⚠ 옛 `reason.*` 입력 사유(sampleIdRequired 등)는 화면을 다 옮긴 뒤 지운다(2단계).
   */
  // 공통 — 라벨이 바로 위에 있어 칸 이름을 되풀이하지 않아도 되는 자리(선택 그룹 · 표 셀)
  'validation.required': '입력하세요',
  'validation.choiceRequired': '선택하세요',
  'validation.reasonRequired': '사유를 입력하세요',
  'validation.nameRequired': '이름을 입력하세요',
  // 시료 직접 입력 · 목록 · 정정
  'validation.sampleIdRequired': '시료 번호를 입력하세요',
  'validation.sampleIdInList': '추가된 시료에 이미 있는 번호입니다 — 다른 번호를 입력하세요',
  'validation.sampleIdTaken': '이미 등록된 시료 번호입니다 — 다른 번호를 입력하세요',
  'validation.sampleTypeRequired': '타입을 선택하세요',
  'validation.drawAtInvalid': '채혈 일시를 다시 입력하세요',
  'validation.extrasInvalid': '추가 입력에서 표시된 항목을 고치세요',
  'validation.sampleRowsRequired': '시료를 1건 이상 추가하세요',
  'validation.sampleListErrors': '추가된 시료에서 표시된 칸을 고치세요',
  'validation.correctionReasonRequired': '정정 사유를 입력하세요',
  // 작업 추가
  'validation.recipeRequired': '레시피를 선택하세요',
  'validation.samplesRequired': '시료를 선택하세요',
  'validation.wellsUnassigned': '빈 웰에 시료를 배정하세요',
  // 레시피 새로 작성 · JSON · 복제
  'validation.recipeNameRequired': '레시피 이름을 입력하세요',
  'validation.recipeFormIssues': '고칠 칸 {count}곳이 있습니다 — 표시된 칸을 고치세요',
  'validation.recipeJsonRequired': '레시피 JSON 을 붙여 넣으세요',
  'validation.baseRecipeRequired': '복제할 레시피를 선택하세요',
  // 계정 · 비밀번호
  'validation.accountIdRequired': '계정 ID 를 입력하세요',
  'validation.userNameRequired': '이름을 입력하세요',
  'validation.roleRequired': '역할을 선택하세요',
  'validation.password.required': '비밀번호를 입력하세요',
  'validation.password.currentRequired': '현재 비밀번호를 입력하세요',
  'validation.password.newRequired': '새 비밀번호를 입력하세요',
  'validation.password.confirmRequired': '비밀번호 확인을 입력하세요',

  /*
   * ── 걸린 것(관리 › 설정 절 · 2026-10-11) ──────────────────────────────────────────
   * 🔴 서버 v0.9.4 가 `/resource-locks`(자원 점유)를 걷고 `GET /safety/stops`(`IFC-RO-109`) ·
   *    `POST /safety/stops/release`(`IFC-RO-110`)로 모았다. 절 이름 「걸린 것」은 계약 요약
   *    「걸린 것 목록」을 따른다. 종전 `screen.resourceLocks.*` · `resourceLock.*` 키는 같이 걷었다.
   * ⚠ 항목 문장(`said`)은 서버가 짓는다 — 여기 문구로 옮기지 않는다.
   */
  'screen.stops.title': '걸린 것',
  'screen.stopsRelease.title': '한 번에 풀기',
  'action.releaseStops': '풀기',
  'action.approveReleaseStops': '풀기 승인',
  'column.stopKind': '종류',
  'column.stopTarget': '대상',
  'column.stopSaid': '내용',
  'column.stopReleasable': '풀기',
  /** 풀기 결과 표의 에러 코드 칸 — 서버 `error_code` 원값(eric 2026-10-13 목록을 열 표로) */
  'column.stopErrorCode': '에러 코드',
  /** 계약 `StopItem.kind` 어휘 — 사전에 없는 값은 원값이 보인다 */
  'stops.kind.safety': '안전 정지',
  'stops.kind.module_hold': '모듈 보류',
  'stops.kind.robot_holding': '쥔 로봇',
  'stops.kind.door_open': '열린 문',
  'stops.kind.quarantine': '이송 격리',
  'stops.releasable.yes': '풀 항목',
  'stops.releasable.no': '사람이 먼저',
  /** 하위 줄(`parent`) — 상위(안전 해제)가 함께 푼다 */
  'stops.releasable.withParent': '함께 풀림',
  'state.empty.stops': '걸린 것 없음',
  /** 열린 알람은 풀 항목이 아니다(원칙 «알람은 알림이다») — 건수만 */
  'stops.alarmsOpen': '열린 알람 {count}건(참고)',
  'stops.confirmIntro': '아래 풀 항목을 차례대로 한 번에 풉니다.',
  'stops.releasableLabel': '풀 항목',
  'stops.humanFirst': '사람이 먼저 할 것 — 이 창에서 풀지 않습니다',
  /** 🔴 풀기는 재개가 아니다(`SM-BATCH-02`) — 서버 `views/stops.py` 「배치 재개는 하지 않는다」 */
  'stops.noResume':
    '풀기는 작업을 재개하지 않습니다. 풀린 뒤 작업 화면에서 [재개]를 따로 누르세요.',
  /** 정본 `07` §4.12 ⓔ 문면 */
  'stops.physicalCheck': '쥔 트레이·로봇 팔 주변·문을 현장에서 확인했다',
  'stops.resultLabel': '풀기 결과',
  'stops.result.done': '풀림',
  'stops.result.notDone': '못 풂',
  'reason.noReleasableStops': '풀 항목 없음',
  'reason.physicalCheckRequired': '현장 확인 체크가 필요합니다',
  /** 작업 상세 → 이 절로 가는 길 */

  /**
   * 관리 › 설정 «알림 수신자»(v0.9.4 · `IFC-RO-103`~`107` · 정본 `07` §4.7 · admin 한정). 화면 `AlarmRecipientsSection` · 창 `AlarmRecipientForm`.
   * 🔴 주소는 다시 보이지 않는다(응답은 `url_set` 만) — 「설정됨 / 안 됨」 · 고칠 때 빈 칸 = 그대로.
   */
  'alarmRecipients.selection.title': '받을 알람',
  'alarmRecipients.selection.all': '모든 알람',
  'alarmRecipients.selection.off': '보내지 않음',
  'alarmRecipients.selection.none': '선택 없음 — 보내지 않음',
  'alarmRecipients.selection.count': '알람 {count}개 선택',
  'alarmRecipients.selection.clear': '선택 지우기',
  'alarmRecipients.selection.search': '알람 코드 · 내용 검색',
  'alarmRecipients.selection.groupAll': '{group} 전체 선택',
  'alarmRecipients.selection.groupCount': '{selected}/{total}개 선택',
  'alarmRecipients.selection.noResults': '검색 결과 없음',
  'alarmRecipients.title': '알림 수신자',
  'alarmRecipients.intro':
    '알람을 원격으로 받을 곳입니다. [시험 발송]은 시험 본문 한 번만 보내고 알람·감사 기록을 남기지 않습니다.',
  'alarmRecipients.empty': '등록된 수신자 없음',
  'alarmRecipients.adminOnly': 'admin 전용 — settings 권한이 있어야 볼 수 있습니다',
  'alarmRecipients.column.name': '이름',
  'alarmRecipients.column.kind': '종류',
  'alarmRecipients.column.enabled': '켜짐',
  'alarmRecipients.column.minSeverity': '최소 등급',
  'alarmRecipients.column.filter': '거름',
  'alarmRecipients.column.role': '역할',
  'alarmRecipients.column.url': '주소',
  'alarmRecipients.column.actions': '동작',
  'alarmRecipients.column.sources': '출처',
  'alarmRecipients.column.errorCodes': '알람 코드',
  'alarmRecipients.kind.webhook': '웹훅',
  'alarmRecipients.kind.slack': 'Slack',
  'alarmRecipients.role.all': '전부',
  'alarmRecipients.role.escalation': '재알림만',
  'alarmRecipients.role.backup': '예비',
  /** 계약 `AlarmRecipient.role` 설명 그대로(`02` `FR-MON-06` ⓒ) */
  'alarmRecipients.roleHelp.all': '첫 알림과 재알림을 받습니다',
  'alarmRecipients.roleHelp.escalation': '확인되지 않은 알람의 재알림만 받습니다',
  'alarmRecipients.roleHelp.backup': '다른 수신자 전부가 끝내 받지 못했을 때만 받습니다',
  'alarmRecipients.enabled.on': '켬',
  'alarmRecipients.enabled.off': '끔',
  'alarmRecipients.toggleLabel': '{name} 켜짐',
  'alarmRecipients.severityLabel': '{name} 최소 등급',
  'alarmRecipients.roleLabel': '{name} 역할',
  'alarmRecipients.filter.all': '전부',
  /** `{sources}` · `{codes}` = 「전부」 또는 개수 */
  'alarmRecipients.filter.summary': '출처 {sources} · 코드 {codes}',
  'alarmRecipients.filter.allLabel': '전부(거르지 않음)',
  'alarmRecipients.filter.noCandidates': '고를 후보가 없습니다 — 직접 적어 넣으세요',
  'alarmRecipients.filter.typePlaceholder': '직접 적기',
  'alarmRecipients.url.set': '설정됨',
  'alarmRecipients.url.unset': '안 됨',
  'alarmRecipients.url.placeholderNew': 'http:// 또는 https:// 주소',
  'alarmRecipients.url.placeholderKeep': '설정됨 — 비워 두면 그대로',
  'alarmRecipients.url.placeholderNone': '주소 없음 — 적으면 설정됩니다',
  'alarmRecipients.action.add': '＋ 수신자 추가',
  'alarmRecipients.action.edit': '고치기',
  'alarmRecipients.action.delete': '지우기',
  'alarmRecipients.action.test': '시험 발송',
  'alarmRecipients.action.save': '고치기',
  'alarmRecipients.action.addTyped': '넣기',
  'alarmRecipients.form.addTitle': '알림 수신자 추가',
  'alarmRecipients.form.editTitle': '알림 수신자 고치기',
  'alarmRecipients.reason.urlRequired': '주소를 적으세요',
  'alarmRecipients.reason.noChange': '바뀐 칸이 없습니다',
  'alarmRecipients.reason.typeFirst': '먼저 값을 적으세요',
  'alarmRecipients.rejected': '서버가 거절한 까닭',
  'alarmRecipients.test.ok': '시험 발송 — 성공 · HTTP {status}',
  /** `{reason}` = 서버 `error_kind`(예외 종류 이름 · `HTTP <code>`) 원문 */
  'alarmRecipients.test.failed': '시험 발송 — 실패 · {reason}',
  'alarmRecipients.test.error': '시험 발송을 보내지 못했습니다 — {reason}',
  'alarmRecipients.deleteTitle': '{name} 지우기',
  'alarmRecipients.deleteBody':
    '이 수신자로는 더 보내지 않습니다. 이미 남은 발송 기록에는 「지운 수신자」로 남습니다.',
  /** 알람 상세 「발송」 칸 — `Alarm.dispatch[]` 서버 값 */
  'alarmRecipients.dispatch.none': '발송 기록 없음',
  'alarmRecipients.dispatch.deleted': '지운 수신자',
  /** 알람 서랍 「발송」 표의 머리(eric 2026-10-13 「·」로 잇던 한 줄 → 열 표). 갈래 · 상태 열은 `column.kind` · `column.status` */
  'alarmRecipients.dispatch.col.channel': '수신자',
  'alarmRecipients.dispatch.col.attempts': '시도',
  'alarmRecipients.dispatch.col.sentAt': '보낸 시각',
  'alarmRecipients.dispatch.kind.initial': '첫 알림',
  'alarmRecipients.dispatch.kind.fallback': '대체 발송',
  'alarmRecipients.dispatch.kind.escalation': '재알림',
  'alarmRecipients.dispatch.state.pending': '대기',
  'alarmRecipients.dispatch.state.sent': '보냄',
  'alarmRecipients.dispatch.state.failed': '실패',
  'alarmRecipients.dispatch.attempts': '시도 {count}',
  /**
   * 관리 › 설정 «알람 설정»(eric 2026-10-28) — 채널(구글챗 · 디스코드 · 슬랙) · 알람별 전달(팝업 · 채널).
   * 화면 `AlarmSettingsContainer` · `AlarmChannelsSection` · `AlarmRoutingTable` · 셸 `AlarmPopup`.
   * 🔴 디스코드 · 구글챗은 서버 수신처 종류(`kind`)에 없다 — 카드만 서고 비활성 + 사유(웹훅 우회 금지).
   */
  'alarmSettings.adminOnly': 'admin 전용 — settings 권한이 있어야 바꿀 수 있습니다',
  /**
   * 서버 원격 알람 스위치(`GET /settings/alarm-remote`)가 «꺼져» 있을 때만 서는 한 줄 + 단추(eric 2026-10-28 「웹훅 설정이 되어 있으면
   * 보내는 걸로 해... 이렇게 명시적으로 하지 말고」 — 켬 · 끔 스위치 줄 · 받을 곳 수는 걷었다).
   */
  'alarmSettings.remote.offWarning': '원격 알람이 서버에서 꺼져 있어 보내지 않습니다',
  'alarmSettings.remote.turnOn': '켜기',
  /** 등록된 웹훅 목록(eric 2026-10-28 「응 저렇게 해 줘」) — 수신처 전부 · 줄마다 [고치기][시험 발송][연결 끊기] */
  'alarmSettings.channels.title': '등록된 웹훅',
  'alarmSettings.hooks.empty': '등록된 웹훅이 없습니다',
  'alarmSettings.hooks.urlUnset': '주소 없음',
  // 웹훅 목록 표 · 상세 판(eric 2026-10-29 「웹훅 리스트를 보여주고.. 해당 웹훅을 클릭하면 상세페이지」)
  'alarmSettings.hooks.col.alarms': '받는 알람',
  'alarmSettings.hooks.state.on': '켜짐',
  'alarmSettings.hooks.state.off': '꺼짐',
  'alarmSettings.hooks.detailTitle': '웹훅 상세',
  'alarmSettings.hooks.notFound': '그 웹훅을 찾을 수 없습니다',
  'alarmSettings.hooks.add': '＋ 웹훅 추가',
  'alarmSettings.hooks.addTitle': '웹훅 추가',
  // 「연결 끊기」(지우기) → «사용» 스위치(끄기 · 켜기 · eric 2026-10-29 「사용 하고 오른쪽에 토글로」)
  'alarmSettings.action.enable': '사용',
  'alarmSettings.action.enableLabel': '{name} 사용',
  'alarmSettings.channel.slack': 'Slack',
  'alarmSettings.channel.discord': 'Discord',
  'alarmSettings.channel.googleChat': 'Google Chat',
  /** 일반 웹훅(서버 `kind: webhook`) — 목록 · 고치기에만 선다(새로 추가 선택지에는 없다 · 팀장 결정) */
  'alarmSettings.channel.webhook': '웹훅',
  /**
   * 켜기 · 최소 등급 칸은 설정 창에서 걷었다(eric 2026-10-28 「켜기 · 최소 등급 · 경고 · 오류 · 긴급 — 이거 설정에서 빼 줘」) —
   * 저장된 수신처가 꺼져 있거나 높은 등급만 받게 돼 있을 때만 서는 경고 한 줄 + 단추(평소엔 안 선다).
   */
  'alarmSettings.channel.filtered':
    '이 채널은 꺼져 있거나 일부 등급만 받도록 저장돼 있어 알람이 다 가지 않습니다',
  'alarmSettings.action.receiveAll': '모두 받기로 맞추기',
  'alarmSettings.action.test': '시험 발송',
  'alarmSettings.form.url': 'Webhook 주소',
  'alarmSettings.form.urlRequired': '주소를 적으세요',
  'alarmSettings.form.save': '저장',
  /** 채널 고르기 + 그 채널의 설정 칸(eric 2026-10-28 「세 개를 선택하면 해당 관련된 연결 설정 창」) */
  'alarmSettings.channel.pick': '채널',
  /**
   * Discord · Google Chat 의 [저장] · [시험 발송] · [연결 끊기] 비활성 사유 — 툴팁 · 숨긴 글자로만(화면 글자 없음).
   * 🔴 「서버 미지원」류 문구는 화면에서 걷었다(eric 2026-10-28 「일단 서버 미지원 문구 다 없애줘. 곧 요청할 거라」) — 채널 탭 상태 글자도 없다.
   */
  'alarmSettings.channel.notYet': '아직 연결할 수 없는 채널입니다',
  /** 웹훅 주소 받는 법 — (?) 단추 이름 · 말풍선 이름 */
  'alarmSettings.form.urlHelp': '웹훅 주소 받는 법',
  /** 웹훅 주소 받는 법 — 서비스별 공식 안내(alarmChannelModel.ts 머리말의 링크) · Webhook 주소 칸 (?) 말풍선 글(본문에 깔지 않는다) */
  'alarmSettings.channel.help.googleChat':
    'Google Chat 스페이스 → 앱 및 통합 → 웹훅 관리 → 웹훅 추가에서 이름을 정하고 저장하면 주소가 나옵니다.',
  'alarmSettings.channel.help.discord':
    'Discord 서버 설정 → 연동 → 웹후크 → 새 웹후크에서 보낼 채널을 고르고 「웹후크 URL 복사」를 누릅니다.',
  'alarmSettings.channel.help.slack':
    'Slack 앱 설정(api.slack.com/apps) → Incoming Webhooks 켜기 → Add New Webhook 에서 채널을 고르면 주소가 나옵니다.',
  'alarmSettings.form.name': '이름',
  'alarmSettings.form.urlKeep': '설정됨 — 비워 두면 그대로',
  'alarmSettings.form.urlPlaceholder.googleChat':
    'https://chat.googleapis.com/v1/spaces/…/messages?key=…&token=…',
  'alarmSettings.form.urlPlaceholder.discord': 'https://discord.com/api/webhooks/…/…',
  'alarmSettings.form.urlPlaceholder.slack': 'https://hooks.slack.com/services/…',
  'alarmSettings.form.urlPlaceholder.webhook': 'https://…',
  'alarmSettings.form.urlShape.googleChat':
    'Google Chat 웹훅 주소 꼴이 아닙니다 — https://chat.googleapis.com/v1/spaces/…/messages?key=…&token=…',
  'alarmSettings.form.urlShape.discord':
    'Discord 웹후크 주소 꼴이 아닙니다 — https://discord.com/api/webhooks/{id}/{token}',
  'alarmSettings.form.urlShape.slack':
    'Slack Incoming Webhook 주소 꼴이 아닙니다 — https://hooks.slack.com/services/…',
  'alarmSettings.form.urlShape.webhook': '주소는 http:// 또는 https:// 로 시작해야 합니다',
  'alarmSettings.reason.noChange': '바뀐 칸이 없습니다',
  'alarmSettings.routing.title': '알람별 전달',
  // 웹훅 상세 「알람별 설정」 카드(eric 2026-10-29 「편집도 빼고.. 알람별 설정이라고 하고.. 오른쪽에 수정이라고 버튼 있고」)
  'alarmSettings.routing.cardTitle': '알람별 설정',
  'alarmSettings.routing.edit': '수정',
  'alarmSettings.routing.done': '완료',
  'alarmSettings.routing.identity': '이름 · 주소',
  'alarmSettings.routing.editOff': '꺼진 웹훅입니다 — 먼저 [사용]을 누르세요',
  'alarmSettings.routing.intro':
    '켠 경로로 그 알람이 알림으로 옵니다. 줄을 펼치면 상황과 조치가 보입니다.',
  'alarmSettings.routing.empty': '맞는 알람이 없습니다',
  /** 행 표지 — 지금까지 실제로 뜬 적이 있는 코드(`GET /alarms` 의 `error_code`) */
  'alarmSettings.routing.seen': '뜬 적 있음',
  /** 서버(수신처 거름 · 팝업 설정)에 저장돼 있는데 사전 생성물에 없는 코드 — 빼지 않고 행으로 세운다 */
  'alarmSettings.routing.unknownCode': '사전에 없는 코드 — 저장된 값이라 그대로 보입니다',
  'alarmSettings.routing.col.message': '내용',
  'alarmSettings.routing.expand.description': '상황',
  'alarmSettings.routing.expand.actions': '조치',
  'alarmSettings.routing.col.code': '알람',
  'alarmSettings.routing.col.severity': '등급',
  'alarmSettings.routing.toggle': '{code} — {channel}',
  /** 그 채널의 최소 등급보다 낮아 켜 두어도 안 간다 — 서버 거름(`min_severity`) 그대로 */
  'alarmSettings.routing.belowMin': '등급 미달',
  /** 「원격 알림」 한 열 = 켜진 수신처 전부(eric 2026-10-28 「하나만 하면 설정된 곳에 다 보내게」) */
  'alarmSettings.routing.col.remote': '원격 알림',
  'alarmSettings.routing.remote.none': '연결된 채널이 없습니다 — 위 채널 카드에서 연결하세요',
  /** 켜진 수신처 중 일부만 받는다 — 누르면 전부로 맞춘다 */
  'alarmSettings.routing.remote.partial': '일부',
  /**
   * 모듈 묶음(eric 2026-10-28 「모듈별로 나눠 줄 수 있어? 작업 시작 누르면 공통 .. 이렇게」) — 묶음 이름은 사전 점검과 같다(`moduleGroups.ts`).
   * `toggle` = 묶음 머리 스위치의 접근 이름 · `codes` = 확인 창 안의 「무엇을 끄는가」
   */
  'alarmSettings.routing.group.count': '{count}종',
  'alarmSettings.routing.group.toggle': '{group} 전체 — {channel}',
  'alarmSettings.routing.group.codes': '{group} 알람 {count}종',
  /** `{channels}` = 저장하지 못한 수신처 이름 · `{reason}` = 서버 사유 */
  'alarmSettings.routing.remote.failed': '{channels} 에 저장하지 못했습니다 — {reason}',
  'alarmSettings.routing.confirmTitle': '원격 알림 — 고른 알람만 받기로 바꿉니다',
  /** `{channels}` = 지금 「모든 알람」을 받는 수신처 이름 */
  'alarmSettings.routing.confirmBody':
    '{channels} 은(는) 지금 모든 알람을 받습니다. {code} 를 끄면 사전의 나머지 알람만 받습니다 — 사전에 새로 추가되는 코드는 받지 않습니다.',
  'alarmSettings.routing.confirm': '바꾸기',
  /** 셸 팝업(`AlarmPopup`) — 🔴 확인 · 해제는 알람 화면의 그 경로로만(DP-02). 닫기는 아무것도 바꾸지 않는다 */
  'alarmPopup.title': '새 알람 {count}건',
  'alarmPopup.open': '알람 화면으로',
  'alarmPopup.close': '닫기',
  'alarmPopup.note': '닫아도 알람은 확인되지 않습니다. 확인은 알람 화면에서 합니다.',
  /*
   * 위치 등록부(관리 › 설정 · `IFC-RO-95`·`96` · `PositionRegistrySection`) — 2026-10-12 시연. 칸 이름은 서버 admin 화면
   * (`position_registry.js` `LABEL`)과 같은 말이다. ⚠ 모듈 서랍 「위치」 탭(티칭)은 2026-10-22 에 걷었다(eric 「위치 탭은 제거해줘」).
   */
  'positionRegistry.title': '위치 등록부',
  'positionRegistry.intro':
    '지점 이름의 목록입니다. 좌표 값은 여기 없습니다. 지우지 않고 비활성으로 바꿉니다 — 바꾼 등록부는 새 작업부터 쓰입니다.',
  'positionRegistry.idPlaceholder': '<모듈 종류>.<자리> — 예: staining.deck-7',
  'positionRegistry.none': '없음',
  'positionRegistry.noChoices': '서버가 선택지를 주지 않았습니다',
  'positionRegistry.noAxes': '이 소유 모듈에는 고를 부가축이 없습니다',
  'positionRegistry.protected': '보호 — {why}',
  'positionRegistry.summary': '{shown} / {total} 지점 · 티칭됨 {taught} · 모듈이 모름 {unsynced}',
  'positionRegistry.empty': '조건에 맞는 지점이 없습니다',
  'positionRegistry.field.position_id': '이름',
  'positionRegistry.field.owner': '소유 모듈',
  'positionRegistry.field.frame': '프레임',
  'positionRegistry.field.kind': '종류',
  'positionRegistry.field.dof': '자유도',
  'positionRegistry.field.station': '정거장',
  'positionRegistry.field.approach': '접근',
  'positionRegistry.field.labware_rotation_deg': '랩웨어 돌림(°)',
  'positionRegistry.field.deck_slot': '데크 칸',
  'positionRegistry.field.axes': '부가축',
  'positionRegistry.field.slide_axis': '슬라이드 축',
  'positionRegistry.field.serves': '맡는 모듈',
  'positionRegistry.field.d1_exempt_why': '정거장 공유 까닭',
  'positionRegistry.field.note': '메모',
  'positionRegistry.field.source': '근거',
  'positionRegistry.field.status': '상태',
  'positionRegistry.status.active': '활성',
  'positionRegistry.status.disabled': '비활성',
  'positionRegistry.column.taught': '티칭',
  'positionRegistry.column.synced': '모듈',
  'positionRegistry.column.actions': '동작',
  'positionRegistry.taught.yes': '티칭됨',
  'positionRegistry.taught.no': '미티칭',
  'positionRegistry.synced.yes': '모듈이 앎',
  'positionRegistry.synced.no': '모듈이 모름 — 티칭 불가',
  'positionRegistry.action.add': '+ 새 지점',
  'positionRegistry.action.edit': '수정',
  'positionRegistry.action.disable': '비활성',
  'positionRegistry.action.enable': '재활성',
  'positionRegistry.action.save': '저장',
  'positionRegistry.filter.all': '전부',
  'positionRegistry.filter.showDisabled': '비활성도 보기',
  'positionRegistry.form.addTitle': '새 지점',
  'positionRegistry.form.editTitle': '지점 수정 — {id}',
  'positionRegistry.reason.noChange': '바꾼 칸이 없습니다',
  'positionRegistry.reject.protected':
    '보호된 지점이라 비활성 · 종류 · 소유 모듈을 바꿀 수 없습니다 — {why}',
  'positionRegistry.reject.inUse':
    '보관되지 않은 레시피 {count}건이 이 지점을 랩웨어 자리로 써서 비활성으로 바꿀 수 없습니다',
  'positionRegistry.reject.idTaken': '같은 이름의 지점이 이미 있습니다(비활성 포함)',

  // ── 계정 상세 「세션 만료」 카드 (IFC-RO-89 · `UserSessionPolicyCard`) ───
  /*
   * 세 값의 이름은 `03` `UAM-SES-06` 어휘(액세스 토큰 · 무활동 만료 · 절대 상한)다.
   * 출처 넷은 서버 enum(`EffectiveSessionValue.source`) 그대로 — 계정 → 역할 → 전체 → 기본값 순으로 먼저 있는 단.
   */
  'userSessionPolicy.title': '세션 만료',
  'userSessionPolicy.access': '액세스 토큰',
  'userSessionPolicy.idle': '무활동 만료',
  'userSessionPolicy.absolute': '절대 상한',
  'userSessionPolicy.source.user': '계정 지정',
  'userSessionPolicy.source.role': '역할 지정 ({role})',
  'userSessionPolicy.source.default': '전체 지정',
  'userSessionPolicy.source.base': '기본값',
  'userSessionPolicy.unit.hours': '{count}시간',
  'userSessionPolicy.unit.minutes': '{count}분',
  'userSessionPolicy.unit.seconds': '{count}초',
  /** 서버 판정(`below_unattended_cycle`)을 그대로 옮긴다 — 화면이 무인 사이클 길이와 비교하지 않는다 */
  'userSessionPolicy.belowCycle':
    '절대 상한이 무인 사이클보다 짧습니다 — 무인 운전 중에 세션이 끊길 수 있습니다',
  'userSessionPolicy.userTier': '{label} — 이 계정 단',

  // ── 관리 › 설정 정책 세 구획 (IFC-RO-87·88 · 90·91 · 92·93 · `SettingsPoliciesSection`) ───
  /*
   * 🔴 2026-09-28 에 걷은 `setting.section.session|password|lock` 류를 되살린 것이 아니다 — 그때는 서버 경로가 없었다.
   *    값은 전부 서버 응답에서 온다. 단(scope) 낱말은 서버 어휘(default · role:<역할> · user:<id>)를 한글로만 바꾼다.
   */
  'policy.section.session': '세션 만료',
  'policy.section.security': '비밀번호 · 로그인 잠금',
  'policy.section.controlLock': '제어락',
  'policy.adminOnly': 'admin 전용 — settings 권한이 있어야 볼 수 있습니다',
  'policy.invalidNumber': '1 이상의 정수만 넣을 수 있습니다',
  'policy.scope.base': '기본값',
  'policy.scope.default': '전체',
  'policy.scope.role': '역할 · {id}',
  'policy.scope.user': '계정 · {id}',
  'policy.column.scope': '단',
  'policy.column.minutes': '{label} (분)',
  'policy.column.updated': '고친 때',
  'policy.column.updatedBy': '고친 사람',
  'policy.placeholder.base': '기본값 {value}',
  'policy.placeholder.inherit': '윗단 따름',
  'policy.unit.minutes': '분',
  'policy.unit.seconds': '초',
  'policy.unit.chars': '자',
  'policy.unit.times': '회',
  'policy.session.defaults':
    '기본값 — 액세스 토큰 {access} · 무활동 만료 {idle} · 절대 상한 {absolute} · 무인 사이클 {cycle}. 빈 칸은 윗단(계정 → 역할 → 전체 → 기본값)을 따릅니다',
  /** 서버가 맡긴 비교(`unattended_cycle_sec` 설명) — 그 단에 «적힌» 절대 상한만 본다 */
  'policy.session.belowCycle': '절대 상한이 무인 사이클({cycle})보다 짧은 단: {scopes}',
  'policy.security.minLen': '비밀번호 최소 길이',
  'policy.security.maxLen': '비밀번호 최대 길이',
  'policy.security.lockThreshold': '로그인 실패 잠금 횟수',
  'policy.security.lockMinutes': '로그인 잠금 시간',
  'policy.lock.ttl': '제어락 수명',
  'policy.lock.heartbeat': '제어락 갱신 간격',
  'policy.value.base': '{value}{unit} · 기본값',
  'policy.value.set': '{value}{unit} · 지정 (기본 {base})',

  // ── 개발용 「테스트 작업 시작」 (eric 2026-09-22 · 나중에 지운다) ───
  /*
   * 🔴 **고객용 문구가 아니다.** `VITE_DEV_QUICK_BATCH=true` 일 때만 화면에 나온다 —
   *    꺼 두면 이 키를 읽는 요소가 DOM 에 아예 없다(`packages/core/src/devQuickBatch.ts`).
   * ⚠ 이 절은 기능과 같이 걷힌다. 다른 화면이 여기 키를 끌어다 쓰지 마라.
   */
  /* 「개발용」 표지 배지(`dev.badge`)는 2026-09-22 에 걷었다 — 단추 하나에 「테스트」가 그 표지를 대신한다(eric) */
  'dev.quickBatch.handoffReleaseReason':
    '테스트 작업 시작 — 시뮬레이터 장부에 기록이 없는 핸드오프 자리({point})를 비었다고 확약(로컬 개발)',
  'dev.quickBatch': '테스트 작업 시작',
  /** 「테스트 작업 시작」이 만든 작업의 표시 이름(eric 2026-09-28 · 임시 하드코딩 — 요청 93 이 열리면 서버 이름) */
  'dev.quickBatchName': '테스트 작업',
  /** 도는 동안의 버튼 글자 — 🔴 진행 줄 · 결과 문구는 그리지 않는다(eric 2026-09-28 「그냥 내부적으로 돌려서 시작하라고」) */
  'dev.quickBatchRunning': '시작 중…',
  /** 시뮬 준비(작업 줄 [시작] · 작업 추가의 사전 점검 앞 · `useSimStartPrepare`) */
  'dev.simPrepare.running': '시뮬 준비 중…',
  'dev.simPrepare.failed': '시뮬 준비가 멈췄습니다 — {step}: {reason}',
  'dev.simPrepare.supply': '소모품 보충',
  /* 시작할 작업의 레시피를 모른다 — `GET /recipes/undefined` 를 부르지 않는다(2026-10-22 실측 404) */
  'dev.simPrepare.noRecipe': '시작할 작업의 레시피를 몰라 보충량을 정할 수 없습니다',
  /** 🔴 보충은 시료보다 앞이다 — 여기서 멈추면 시료도 작업도 안 만들었다 */
  'dev.quickStep.supplyFailed': '소모품 보충에서 멈췄습니다 — {reason} · 시료는 만들지 않았습니다',
  /** 🔴 웰 배정은 시료보다 앞이다 — 레시피 묶음이 모자라면 시료도 작업도 안 만든다 */
  'dev.quickStep.assignFailed': '웰 배정에서 멈췄습니다 — {reason} · 시료는 만들지 않았습니다',
  /** 레시피 `tray_path.sample_groups` 에 그 역할 묶음이 시료 수보다 적다 — 웰을 지어내지 않는다 */
  'dev.quickAssign.short': '레시피 묶음이 모자랍니다 ({role} {have}/{need})',
  'dev.quickAssign.role.patient': '환자',
  'dev.quickAssign.role.reference': '대조',
  'dev.quickSupply.kind.tip': '팁',
  'dev.quickSupply.kind.tc': 'TC',
  'dev.quickSupply.kind.reagent': '시약',
  'dev.quickSupply.kind.waste': '폐기통',
  /** 레시피에 그 랩웨어의 칸 선언이 없어 채울 수를 모른다 — 지어낸 수로 덮지 않는다 */
  'dev.quickSupply.uncounted': '{module} {kind} 칸 수를 레시피에서 셀 수 없습니다',
  /** 🔴 확약 대화를 닫아도 **작업은 서버에 남는다** — 「없던 일」로 적지 않는다 */
  'dev.quickStep.canceled': '작업 {batch} 을(를) 만들었고 시작하지 않았습니다',
  /** 🔴 만든 시료는 그대로 둔다(서버가 append-only 다). 몇 건까지 갔는지 같이 적는다 */
  'dev.quickStep.failed': '{reason} — 시료 {done}건 등록 후 멈췄습니다',
  /* ── 자동 준비(eric 2026-09-28 「그냥 다 초기화하고 … 그냥 실행하게 해줘」) · `devQuickReset.ts` ── */
  'dev.quickStep.reset': '자동 준비',
  /** 🔴 차단은 확약으로 넘지 않는다 — 작업은 서버에 남는다 */
  'dev.quickStep.blocked': '사전 점검 차단: {reason} · 작업 {batch} 은(는) 시작하지 않았습니다',
  /** 🔴 준비에서 멈추면 시료도 작업도 안 만들었다 */
  'dev.quickStep.resetFailed':
    '자동 준비({step})에서 멈췄습니다 — {reason} · 시료는 만들지 않았습니다',
  /*
   * ── 서버 준비 점검 · 시작 뒤 정체(2026-09-29 재부팅 사고 — 스케줄러가 안 떠 `running` 인데 스텝이 안 나갔다) ──
   * 🔴 `{checks}` 는 `/readyz` 본문의 `이름: 원값` 그대로다(영문 원값 노출 — eric 2026-09-21 「일단 노출」).
   *    프론트는 서버를 재기동할 수 없다 — 사유를 정확히 적는 데까지다.
   */
  'dev.quickReset.step.ready': '서버 준비 점검',
  'dev.quickReset.ready.notReady':
    '서버가 준비되지 않았습니다 — {checks} · API 재기동이 필요합니다',
  /** 🔴 못 읽은 것은 통과가 아니다 — 프록시가 없으면 SPA index.html 이 200 으로 온다 */
  'dev.quickReset.ready.unreadable': '서버 준비 상태를 읽지 못했습니다(/readyz · {reason})',
  /** 🔴 `{state}` 는 `GET /scheduler` 의 `state` 원값(+ 종착 까닭 원문) — `ticking` 밖이면 멈춘다 */
  'dev.quickReset.ready.scheduler': '스케줄러가 돌지 않습니다 — {state}',
  /** 🔴 작업은 끊지도 다시 시작하지도 않는다 — 알리기만 한다 */
  'dev.quickStart.stalled':
    '시작했지만 {seconds}초 동안 스텝이 나가지 않았습니다 — 서버 준비 상태: {checks}',
  /** 시작 직후 작업이 멈춤(`paused` · `aborted` · `error`). `{status}` 는 상태 라벨, `{reason}` 은 서버 `halt_reason` 원문 */
  'dev.quickStart.halted': '시작했지만 곧 멈췄습니다({status}) — {reason}',
  /** `halt_reason` 이 비었을 때 — `{alarm}` 은 최근 활성 알람의 `출처 코드` 원문 */
  'dev.quickStart.haltedAlarm': '시작했지만 곧 멈췄습니다({status}) — 최근 알람: {alarm}',
  'dev.quickStart.haltedBare': '시작했지만 곧 멈췄습니다({status})',
  'dev.quickReset.step.lock': '제어락',
  'dev.quickReset.step.scheduler': '스케줄러 종착 해제',
  'dev.quickReset.step.batches': '종착 안 한 작업',
  'dev.quickReset.step.unloads': '반출 확인',
  'dev.quickReset.step.prepare': '트레이 자리 정리',
  'dev.quickReset.step.executions': '모듈 실행 취소',
  'dev.quickReset.step.safety': '안전 해제',
  'dev.quickReset.step.quarantine': '이송 격리 해제',
  'dev.quickReset.step.release': '쥔 트레이 내려놓기',
  'dev.quickReset.step.arm': '팔 이동 자세 복귀',
  'dev.quickReset.step.initialize': '모듈 초기화',
  'dev.quickReset.step.idle': '모듈 IDLE 대기',
  'dev.quickReset.notReached': '{step} — 앞 단계에서 멈춰 보내지 않았습니다',
  'dev.quickReset.failed': '{step} 실패 — {reason}',
  'dev.quickReset.how.stop': '중지',
  'dev.quickReset.how.discard': '폐기',
  'dev.quickReset.lock.held': '제어락 — 이미 쥐고 있습니다',
  'dev.quickReset.lock.running': '제어락 획득 중…',
  'dev.quickReset.lock.done': '제어락 획득',
  /** 🔴 남의 락을 강제로 뺏지 않는다 */
  'dev.quickReset.lock.other': '다른 세션이 제어락을 쥐고 있습니다 — {reason}',
  'dev.quickReset.scheduler.none': '스케줄러 도는 중',
  'dev.quickReset.scheduler.running': '스케줄러 종착 해제 중… ({state})',
  /** 🔴 감사 원장에 봉인되는 사유 꼬리 — 머리는 `QUICK_RESET_REASON` */
  'dev.quickReset.scheduler.reason': '스케줄러 종착 해제',
  'dev.quickReset.scheduler.done': '스케줄러 도는 중(해제됨)',
  /** `{reason}` 은 서버 `reset_off_reason` 원문 */
  'dev.quickReset.scheduler.notAllowed': '스케줄러 종착을 풀 수 없습니다({state}) — {reason}',
  'dev.quickReset.scheduler.stillDown':
    '스케줄러 종착을 풀었지만 {seconds}초 안에 다시 돌지 않았습니다 — {state}',
  'dev.quickReset.batches.none': '종착 안 한 작업 없음',
  'dev.quickReset.batches.running': '작업 {batch} {how} 중…',
  'dev.quickReset.batches.reason': '작업 {batch} 종착',
  'dev.quickReset.batches.done': '작업 {count}건 종착',
  'dev.quickReset.batches.notAllowed':
    '작업 {batch}({status}) — 서버가 중지·폐기를 허락하지 않습니다',
  'dev.quickReset.batches.left': '종착하지 않은 작업이 남았습니다 — {batches}',
  'dev.quickReset.quarantine.none': '이송 격리 없음',
  /** `{total}` 은 함께 나가는 «풀 항목» 전부 — 서버가 골라 풀기를 받지 않는다(`STOPS_CHANGED`) */
  'dev.quickReset.quarantine.running': '이송 격리 {count}건 해제 중…(함께 푸는 항목 {total}건)',
  'dev.quickReset.quarantine.reason': '종착 작업 · 멈춘 수동 이송이 남긴 이송 격리 해제',
  /** `{items}` 는 서버 `StopsReleaseResult.items[].said` 원문(+ 코드) */
  'dev.quickReset.quarantine.notDone': '풀지 못한 항목이 있습니다 — {items}',
  'dev.quickReset.quarantine.left': '풀기 뒤에도 이송 격리가 남았습니다 — {items}',
  'dev.quickReset.quarantine.done': '이송 격리 {count}건 해제',
  'dev.quickReset.unloads.none': '대기 중 반출 없음',
  'dev.quickReset.unloads.running': '반출 확인 {count}건 중…',
  'dev.quickReset.unloads.done': '반출 확인 {count}건',
  'dev.quickReset.prepare.none': '점유된 트레이 자리 없음',
  'dev.quickReset.prepare.running': '트레이 자리 정리 중… ({points})',
  'dev.quickReset.prepare.reason': '점유 자리 {points} 정리',
  'dev.quickReset.prepare.done': '트레이 자리 정리 ({points})',
  /*
   * 시작할 작업이 있을 때(작업 줄 [시작]의 시뮬 준비) — 서버 정리(`POST /modules/prepare`)는 비종착 작업을 «전부» 중단시켜
   * 시작할 작업까지 끝낸다(2026-10-22 실측). 보내지 않고 점유 판정은 사전 점검에 맡긴다
   */
  'dev.quickReset.prepare.kept':
    '시작할 작업이 있어 트레이 자리 정리를 건너뜀 — 점유 자리 {points} 는 사전 점검이 판정',
  /** 🔴 정리를 보냈어도 다시 읽은 점유가 남으면 멈춘다 — 옛 미러로 작업을 만들면 사전 점검이 막는다 */
  'dev.quickReset.prepare.left':
    '트레이 자리 정리 뒤 {seconds}초가 지나도 점유가 남았습니다 — {points}',
  'dev.quickReset.executions.none': '모듈에 남은 실행 없음',
  'dev.quickReset.executions.running': '실행 취소 중… ({modules})',
  'dev.quickReset.executions.reason': '남은 모듈 실행 취소',
  'dev.quickReset.executions.done': '실행 취소 ({modules})',
  'dev.quickReset.release.none': 'HANDOFF 에 남은 모듈 없음',
  'dev.quickReset.release.settling': '{modules} 실행 취소가 가라앉기를 기다리는 중…',
  'dev.quickReset.release.running': '{module} 쥔 트레이를 {point} 에 내려놓는 중…',
  'dev.quickReset.release.checking': '{module} 내려놓을 수 있는지 서버 판정을 기다리는 중…',
  /** 감사 사유 꼬리 */
  'dev.quickReset.release.reason': '{module} 쥔 트레이 내려놓기({point})',
  /** `{said}` 는 잡의 마지막 말(서버 원문) */
  'dev.quickReset.release.waiting': '{module} 내려놓는 중… ({said})',
  'dev.quickReset.release.done': '쥔 트레이 내려놓음 ({placed})',
  'dev.quickReset.release.executing':
    '{module} 에 실행이 남아 있습니다(HANDOFF) — 자동으로 놓지 않습니다',
  /** 🔴 `{reason}` 은 서버 후보 판정 원문(+ 코드) — 쥔 것 없음 · 파지 모름 · 자리 없음이 여기서 선다 */
  'dev.quickReset.release.refused': '{module} 쥔 트레이를 내려놓을 수 없습니다 — {reason}',
  'dev.quickReset.release.keptBatch':
    '{module} 쥔 트레이를 시작하려는 작업이 막고 있어 시작 창으로 넘깁니다 — {reason}',
  'dev.quickReset.release.noTarget': '{module} 쥔 트레이를 놓을 자리가 없습니다(서버 후보 0건)',
  'dev.quickReset.release.timeout':
    '{module} 내려놓기가 {seconds}초 안에 끝나지 않았습니다 — {said}',
  /** `{status}` 는 잡 상태 원값(`halted` · `stopped`), `{said}` 는 서버 원문 */
  'dev.quickReset.release.halted': '{module} 내려놓기가 멈췄습니다({status}) — {said}',
  /** 실행이 붙은 모듈을 기다리는 중(내려놓기 뒤 · 초기화 앞 — 2026-10-13 시연 사고) */
  'dev.quickReset.settle.running': '진행 중인 동작이 끝나기를 기다리는 중 — {modules}',
  'dev.quickReset.settle.timeout': '{seconds}초 안에 동작이 끝나지 않았습니다 — {modules}',
  'dev.quickReset.arm.none': '팔 복귀할 로봇 없음',
  'dev.quickReset.arm.noPoint': '없음',
  /** 🔴 지점을 지어내지 않는다 — 구조화 후보가 정확히 하나일 때만 보낸다 */
  'dev.quickReset.arm.ambiguous': '팔 지점을 정할 수 없어 멈췄습니다 — 후보: {points}',
  'dev.quickReset.arm.notFound': '팔 지점을 찾지 못해 멈췄습니다 — 시도: {tried}',
  /** 🔴 전부 부가축 거절 — 팔이 시도한 지점 안에 없다는 것까지만. 빠져나올 순서를 지어내지 않는다 */
  'dev.quickReset.arm.auxOnly':
    '부가축이 시도한 지점의 정거장에 없어 멈췄습니다 — 팔이 그 지점들 안에 있지 않습니다(이미 이동 자세·파킹이거나 다른 지점). 시도: {tried}',
  'dev.quickReset.arm.running': '{module} 팔 이동({label} · {point}) 중…',
  'dev.quickReset.arm.waiting': '{module} IDLE 대기 중… ({state})',
  'dev.quickReset.arm.refused': '{module} 팔 이동({label})이 거절됐습니다 ({code})',
  'dev.quickReset.arm.done': '팔 이동 자세 복귀 ({modules} · {point} → 이동 자세)',
  'dev.quickReset.initialize.none': '모듈 전부 IDLE — 초기화 안 함',
  'dev.quickReset.initialize.running': '{module} 초기화 중… ({state})',
  'dev.quickReset.initialize.closeDoor': '{module} 도어 닫기(close_door) 뒤 다시 초기화…',
  'dev.quickReset.initialize.done': '초기화 보냄 ({modules})',
  'dev.quickReset.initialize.local': '{module} 단독 모드 — 통합 콘솔에서 초기화할 수 없습니다',
  /** 🔴 트레이를 쥔 로봇을 초기화하면 떨어뜨린다 — 보내지 않는다 */
  'dev.quickReset.initialize.handoff':
    '{module} 이(가) 트레이를 쥐고 있습니다(HANDOFF) — 자동으로 초기화하지 않습니다',
  /** 🔴 L2 가 막았다(`MODULE_BUSY` · `robot_holding_tray`) — 기다려도 안 풀려 재시도하지 않는다 */
  'dev.quickReset.initialize.holding':
    '{module} 이(가) 파지 중이라 초기화가 막혔습니다(robot_holding_tray) — 자동으로 풀지 않습니다 · {reason}',
  /** 파지 «기억» 초기화 — 🔴 모듈 안 로봇 · 실행 없음 · `holding` 일 때만(6축이 쥔 트레이는 내려놓기가 출구) */
  'dev.quickReset.forget.running': '{module} 파지 기억 초기화({robots}) 뒤 다시 초기화…',
  /** 감사 사유 꼬리 */
  'dev.quickReset.forget.reason': '{module} 파지 기억 초기화({robot}) — 실행 없는 파지 기억',
  'dev.quickReset.forget.executing':
    '{module} 이(가) 아직 실행 중이라 파지 기억을 지우지 않습니다(robot_holding_tray)',
  'dev.quickReset.forget.none':
    '{module} 초기화가 파지 중으로 막혔지만 쥔(holding) 그리퍼가 보이지 않습니다 — {grips}',
  'dev.quickReset.forget.refused': '{module} 파지 기억 초기화({robot})가 거절됐습니다 — {reason}',
  'dev.quickReset.forget.stillHolding':
    '{module} 파지 기억을 지웠지만 {seconds}초 안에 holding 이 빠지지 않았습니다 — {robots}',
  'dev.quickReset.forget.initFailed':
    '{module} 파지 기억을 지운 뒤 초기화도 거절됐습니다 — {reason}',
  'dev.quickReset.recovery.running': '{module} 오류 — 복구 개시 중…',
  'dev.quickReset.safety.none': '안전 상태 정상 — 해제 안 함',
  'dev.quickReset.safety.running': '안전 해제 중… ({state})',
  'dev.quickReset.safety.done': '안전 해제 — normal',
  /** 🔴 거절은 다시 보내지 않는다 — 물리 가드(버튼 · 도어 등)는 사람이 풀어야 한다 */
  'dev.quickReset.safety.refused': '안전 해제가 거절됐습니다 — {reason}',
  'dev.quickReset.safety.blocked': '못 푼 모듈: {modules}',
  'dev.quickReset.safety.unresolved': '미해소 조건: {conditions}',
  'dev.quickReset.safety.stillHeld':
    '안전 해제 뒤 {seconds}초 안에 정상이 되지 않았습니다({state}) — {conditions}',
  /** 감사 사유 꼬리 */
  'dev.quickReset.recovery.reason': '{module} 오류 복구 개시',
  'dev.quickReset.recovery.latchReason': '{module} 복구 래치 해제',
  'dev.quickReset.recovery.failed': '{module} 복구 한 사이클 뒤 다시 오류입니다',
  'dev.quickReset.idle.already': '모듈 전부 IDLE',
  'dev.quickReset.idle.running': '모듈 IDLE 대기 {seconds}초 — {modules}',
  'dev.quickReset.idle.done': '모듈 전부 IDLE ({seconds}초)',
  'dev.quickReset.idle.timeout': '{seconds}초 안에 IDLE 이 되지 않았습니다 — {modules}',
  'reason.quickBatchNoRecipe': '레시피 미지정 — VITE_DEV_QUICK_BATCH_RECIPE 를 채우세요',

  // ── 브로커 계정 · 모듈 브로커 접속 · 모듈 ID 바꾸기 (IFC-RO-69~74 · 78 · 79 · 94 · admin · 2026-10-12) ───
  /*
   * 화면 `BrokerAccountsSection` · `BrokerAccountDialog`(관리 › 설정) · `ModuleBrokerCards`(모듈 서랍).
   * 🔴 거절 갈래(`broker.reason.<context.reason>`)는 서버 낱말 그대로다 — `brokerFailure` 가 `context.reason` 으로 고르고,
   *    사전에 없는 갈래는 서버 문면으로 떨어진다. 낱말을 바꾸지 마라.
   * 🔴 비밀번호 값은 문구에 넣지 않는다 — 창이 응답 값을 그대로 한 번 보인다.
   */
  'broker.title': '브로커 계정',
  'broker.intro':
    '모듈 · 코어가 MQTT 브로커에 붙을 때 쓰는 계정입니다. 끄기 · 지우기 · 비밀번호 · 역할을 바꾸면 그 계정으로 붙은 모듈이 끊길 수 있습니다.',
  'broker.empty': '브로커 계정 없음',
  'broker.adminOnly': 'admin 전용 — 브로커 계정 · 접속은 admin 만 볼 수 있습니다',
  'broker.column.name': '계정',
  'broker.column.kind': '종류',
  'broker.column.modules': '붙은 모듈',
  'broker.column.state': '상태',
  'broker.column.lastConnected': '마지막 접속',
  'broker.column.sync': '사본',
  'broker.column.actions': '동작',
  'broker.kind.module': '모듈',
  'broker.kind.l2': '코어(L2)',
  'broker.kind.admin': '관리',
  'broker.tag.test': '시험 계정',
  'broker.tag.unused': '미사용',
  'broker.state.enabled': '켬',
  'broker.state.disabled': '꺼짐',
  'broker.modules.none': '없음',
  /** 계약 — `null` 은 «모름»이지 «접속 안 함»이 아니다 */
  'broker.last.unknown': '기록 없음',
  'broker.sync.ok': '일치',
  'broker.sync.differs': '어긋남',
  'broker.sync.unknown': '모름',
  'broker.action.manage': '관리',
  'broker.action.roles': '역할 바꾸기',
  'broker.action.enable': '켜기',
  'broker.action.disable': '끄기',
  'broker.action.password': '비밀번호 바꾸기',
  'broker.action.delete': '계정 지우기',
  'broker.dialog.title': '브로커 계정 · {name}',
  'broker.dialog.actions': '조작',
  'broker.history.title': '조작 이력',
  'broker.history.empty': '조작 이력 없음',
  'broker.history.at': '시각',
  'broker.history.operation': '조작',
  'broker.history.outcome': '결과',
  'broker.history.actor': '사람',
  'broker.history.ok': '성공',
  /** 감사 행의 `operation`(계약 operationId) — 사전에 없으면 원문이 선다 */
  'broker.op.createBrokerAccount': '계정 만들기',
  'broker.op.setBrokerAccountRoles': '역할 바꾸기',
  'broker.op.disableBrokerAccount': '끄기',
  'broker.op.enableBrokerAccount': '켜기',
  'broker.op.changeBrokerPassword': '비밀번호 바꾸기',
  'broker.op.deleteBrokerAccount': '계정 지우기',
  'broker.op.switchCoreBrokerAccount': '코어 계정 옮기기',
  'broker.op.changeModuleBrokerConnection': '모듈 접속 바꾸기',
  'broker.roles.title': '역할 바꾸기 · {name}',
  'broker.roles.help':
    '고른 모듈의 토픽에만 쓸 수 있습니다. 고른 것 «전부»로 바뀝니다 — 뺀 모듈은 그 계정으로 발행하지 못합니다.',
  'broker.roles.noCandidates': '고를 모듈이 없습니다',
  'broker.roles.allRemoved': '모든 역할을 뗍니다 — 이 계정으로 붙은 모듈은 발행하지 못합니다',
  'broker.roles.noChange': '바뀐 역할이 없습니다',
  'broker.disable.title': '계정 끄기 · {name}',
  'broker.disable.body':
    '끄면 이 계정으로 붙은 모듈({modules})이 브로커에서 끊깁니다. [켜기]로 되돌릴 수 있습니다.',
  'broker.disable.bodyNoModule':
    '끄면 이 계정으로는 브로커에 붙을 수 없습니다. [켜기]로 되돌릴 수 있습니다.',
  'broker.password.title': '비밀번호 바꾸기 · {name}',
  'broker.password.body':
    '서버가 새 비밀번호를 만들어 붙은 모듈에 먼저 보냅니다. 모듈이 받지 않으면 브로커를 바꾸지 않습니다.',
  'broker.password.bodyCore':
    '코어가 지금 쓰는 계정입니다. 서버가 새 비밀번호로 바꾼 뒤 확인 접속을 하고, 실패하면 옛 값으로 되돌립니다. 끝나면 코어 .env 의 비밀번호도 같은 값으로 맞춰야 합니다.',
  'broker.password.once': '새 비밀번호는 다음 화면에 한 번만 보입니다. 다시 받을 길이 없습니다.',
  'broker.password.showOnce':
    '새 비밀번호 — 이 창을 닫으면 다시 볼 수 없습니다. 모듈 화면에 넣어야 하면 지금 옮겨 두세요.',
  'broker.password.copy': '비밀번호 복사',
  'broker.password.replayed': '이미 처리된 요청입니다 — 비밀번호는 첫 응답에만 실립니다',
  'broker.delete.title': '계정 지우기 · {name}',
  /** 서버 `_judge_delete` 그대로 — 붙은 모듈이 다른 계정으로 옮겨 가면 지우고, 끊기기만 하면 다시 켠다(되돌림) */
  'broker.delete.body':
    '먼저 끄고 접속을 관찰합니다. 붙은 모듈이 모두 다른 계정으로 옮겨 가면 지우고, 끊기기만 하면 다시 켭니다(되돌림). 끄는 순간 붙은 모듈은 끊깁니다.',
  'broker.delete.confirmLabel': '계정 이름을 다시 적으세요',
  'broker.delete.reason.confirmName': '계정 이름이 같아야 지웁니다',
  'broker.change.state': '진행',
  'broker.change.deadline': '관찰 시한',
  'broker.change.reason': '사유',
  /** 202 첫 줄 — 서버는 «시작했다»만 말한다. 끝은 구독(`broker_change`)이 목록을 다시 받아 보인다 */
  'broker.change.started.password': '비밀번호 바꾸기를 시작했습니다',
  'broker.change.started.delete':
    '계정 지우기를 시작했습니다 — 지금은 꺼졌고, 관찰 뒤 서버가 지우거나 다시 켭니다',
  'broker.change.started.core_switch': '코어 계정 옮기기를 시작했습니다',
  'broker.change.started.provision': '계정 넣기를 시작했습니다',
  'broker.change.started.connection':
    '접속 바꾸기를 시작했습니다 — 모듈이 새 주소로 붙는지 관찰합니다',
  'broker.change.started.identity':
    '모듈 ID 바꾸기를 시작했습니다 — 새 ID 가 도착하면 레지스트리가 바뀝니다',
  'broker.change.started.release': '역할 떼기를 시작했습니다',
  'broker.changeState.in_progress': '진행 중',
  'broker.changeState.done': '끝',
  'broker.changeState.rolled_back': '되돌림',
  'broker.changeState.undecided': '판정 못 함 — 사람이 확인하세요',
  /** 화면 앞단 게이트(서버 가드와 같은 차례 · `brokerAccountGate`) */
  'broker.reason.protected_test': '시험 계정 — 브로커 권한 시험이 쓰는 계정이라 바꾸지 않습니다',
  'broker.reason.protected_admin': '관리 계정 — 코어의 브로커 관리 계정은 바꾸지 않습니다',
  'broker.reason.protected_l2': '코어(L2) 계정 — 끄기 · 지우기 · 역할을 받지 않습니다',
  'broker.reason.protected_l2Other':
    '코어가 지금 쓰는 계정이 아닙니다 — 코어 자기 계정의 비밀번호만 바꿀 수 있습니다',
  'broker.reason.coreUnknown': '코어가 쓰는 계정을 확인하지 못했습니다',
  'broker.reason.alreadyDisabled': '이미 꺼져 있습니다',
  'broker.reason.alreadyEnabled': '이미 켜져 있습니다',
  /** 서버 거절 갈래 — `context.reason` 그대로 */
  'broker.reason.batch_active': '작업 {batch} 이(가) 도는 중이라 브로커를 바꿀 수 없습니다',
  'broker.reason.change_in_progress':
    '같은 계정 · 모듈의 교체가 진행 중입니다 — 끝난 뒤 다시 하세요',
  'broker.reason.protected_account': '코어 · 관리 · 시험 계정은 바꿀 수 없습니다',
  'broker.reason.module_not_acknowledged':
    '모듈이 새 설정을 받지 않았습니다 — 브로커는 바뀌지 않았습니다',
  'broker.reason.account_name_taken': '같은 이름의 계정이 이미 있습니다',
  'broker.reason.account_in_use': '그 계정으로 지금 붙어 있는 클라이언트가 있습니다',
  /**
   * 503 `브로커 제어 <kind>` — 🔴 `unconfigured` 는 막다른 오류가 아니라 «이 서버에서 안 되는 기능»이다(dev2 2026-10-12:
   * 브로커가 dynsec 이 아니라 password_file · acl_file 로 떠 있고 관리 자격도 없다). 절 안의 사유 한 줄로 선다.
   */
  'broker.reason.control_unconfigured':
    '이 서버의 브로커는 계정 제어를 지원하지 않습니다 — 브로커 관리 자격이 설정되어 있지 않습니다',
  'broker.reason.control_unreachable': '브로커에 닿지 못했습니다 — 바꾸지 않았습니다',
  'broker.reason.control_timeout': '브로커가 시간 안에 답하지 않았습니다 — 바뀌었는지 모릅니다',
  'broker.reason.not_found': '없는 계정 · 모듈입니다 — 목록을 다시 확인하세요',
  'broker.reason.same_id': '새 ID 가 지금 ID 와 같습니다',
  'broker.reason.confirm_mismatch': '다시 입력한 지금 ID 가 다릅니다',
  'broker.reason.id_format': '모듈 ID 형식이 아닙니다',
  'broker.reason.id_taken': '그 ID 는 이미 등록되어 있습니다',
  'broker.reason.same_address': '지금 주소와 같습니다',
  'broker.reason.confirm_address_change_required': '주소를 바꾸려면 확인에 체크해야 합니다',
  'broker.connection.address': '브로커 주소',

  // ── 관리 › 로그 「보충 기록」 · 「폐기통 교체」 (IFC-RO-32 · IFC-RO-20 · operator · admin · 2026-10-12) ───
  /* 화면 `LogsScreen` · 열 `recordColumns.tsx`. 종류 · 귀속 근거는 계약 enum 그대로(사전에 없으면 원문) */
  'tab.logs.replenish': '보충 기록',
  'tab.logs.waste': '폐기통 교체',
  'filter.search.records': '모듈 · 통 · 메모 검색',
  'state.empty.replenishments': '보충 기록 없음',
  'state.empty.wasteReplacements': '교체 기록 없음',
  'records.column.module': '모듈',
  'records.column.after': '보충 후',
  'records.column.container': '폐기통',
  'records.column.note': '메모',
  'records.replenish.kind.tip': '팁',
  'records.replenish.kind.tc': 'TC',
  'records.replenish.kind.reagent': '시약',
  /** 계약 — `count_after: null`(키 없음) = 모듈 만재값으로 리셋 */
  'records.replenish.resetFull': '만재로 리셋',
  'records.replenish.countAfter': '{count}개',
  /** 시약 — 자리(랩웨어 키) · 웰 · 웰마다 보충 후 양 */
  'records.replenish.reagentAfter': '{labware} · {wells} · 웰마다 {volume} µL',
  'records.attribution.authenticated_session': '로그인 세션',
  'records.attribution.batch_owner': '작업 소유자',
  'records.attribution.maintenance_session': '정비 세션',
  /* 사전 밖에 있던 화면 글자(통합 콘솔 영어 전환 · 2026-11-02) */
  'unit.kinds': '종',
  'error.requestTimeout': '요청 시간이 초과되었습니다',
  'error.eventTotalUnknown': '이벤트 전체 건수를 확인할 수 없습니다.',
  'error.historyIncomplete': '이력 조회가 상한에 도달해 전체 기록을 확인하지 못했습니다.',
  'error.standardRecipeMissing': '표준 레시피를 받지 못했습니다',
  'sampleNew.hints.type.test': '테스트',
  'sampleNew.hints.type.validation': '검증',
  'sampleNew.hints.type.other': '기타',
} as const
